import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useHistory, useParams, useLocation, useRouteMatch } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { css, StyleSheet } from 'aphrodite/no-important';
import { clone } from 'ramda';
import {
  Button,
  Checkbox,
  TabsContainer,
  TabItem,
  Loader,
  Drawer,
  TimePicker,
  PopupMenu,
  TextInput,
  DialogBox
} from 'quantux-ui';
import { BASE_URL, findFlag, padZero, FLAG_VALUE, getHighlightedText } from '../../utils';
import PatientInfo from '../patient-dashboard/patient-info';
import AddPatient from './add-patient';
import InsuranceInfo from './insurance-info';
import CaseTab from '../case-schedule/case-tab';
import IntraOp from '../case-schedule/intraop';
import { blue, gray, salmon, yellow } from '../../theme';
import PatientProfileDrawer from '../patient-dashboard/patient-profile-drawer';
import InsuranceDrawer from './insurance-drawer';
import CropperDrawer from '../patient-dashboard/cropper-drawer';
import WarningDialog from '../warning-dialog';
import AnesthesiaForms from './anesthesia-forms';
import Superbill from './superbill';
import AddPayerDrawer from '../payers-data-grid/add-payer-drawer';
import PatientCard from '../patient-card';
import CriticalWarningIcon from '../svg-icons/critical-warning-icon';
import SuccessIcon from '../svg-icons/success-icon';
import CaseField from '../revenue-cycle-management/case-field';
import CaseUserCard from '../revenue-cycle-management/case-user-card';

const DEBUG_CASE_DASHBOARD = false;
const DRAWER_WIDTH = 355;
const DIALOG_WIDTH = window.innerWidth <= 576 ? (window.innerWidth - 16) : 350;

const tabsTextStyles = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: '12px'
};

const rcmIcon = (
  <svg
    width="21"
    height="24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M19.653 3.563c-.394 0-.712.353-.712.79v1.89a10.846 10.846 0 0 0-3.105-3.619C14.177 1.398 12.241.75 10.24.75c-2.57 0-5.023 1.072-6.902 3.02C1.468 5.704.335 8.326.143 11.15c-.03.435.265.815.656.848a.433.433 0 0 0 .054.003c.37 0 .68-.317.71-.732.164-2.426 1.139-4.678 2.744-6.342 1.618-1.674 3.724-2.595 5.933-2.595 1.72 0 3.383.556 4.81 1.61 1.253.926 2.263 2.185 2.942 3.664h-1.978c-.393 0-.712.354-.712.79 0 .438.319.792.712.792h3.639c.393 0 .712-.354.712-.791V4.354c0-.438-.319-.791-.712-.791Zm.026 8.44c-.392-.034-.734.294-.764.729-.164 2.426-1.139 4.678-2.744 6.341-1.616 1.675-3.722 2.596-5.93 2.596-1.721 0-3.384-.556-4.81-1.611-1.254-.925-2.264-2.184-2.943-3.663h1.978c.393 0 .712-.354.712-.791 0-.438-.319-.791-.712-.791H.827c-.393 0-.712.353-.712.79v4.044c0 .437.319.791.712.791.394 0 .712-.354.712-.791v-1.89a10.863 10.863 0 0 0 3.103 3.62c1.66 1.225 3.595 1.874 5.598 1.874 2.57 0 5.023-1.073 6.902-3.02 1.87-1.936 3.003-4.557 3.193-7.38.03-.436-.263-.816-.656-.849ZM9.102 9.968c0 .594.41.975 1.688 1.344 1.276.368 2.643.975 2.643 2.75 0 1.281-.872 1.987-1.969 2.219v1.343H9.777V16.27c-1.08-.256-2.002-1.025-2.07-2.394h1.238c.061.738.517 1.313 1.676 1.313 1.243 0 1.518-.688 1.518-1.119 0-.581-.28-1.131-1.687-1.506-1.57-.419-2.644-1.138-2.644-2.582 0-1.206.878-1.993 1.969-2.256v-1.35h1.688v1.369c1.175.319 1.766 1.306 1.805 2.381h-1.243c-.034-.781-.405-1.312-1.406-1.312-.95 0-1.519.475-1.519 1.156Z"
      fill="#96C8FF"
    />
  </svg>
);

// #region ICONS
const IS_NONE_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none"><path fill="#96C8FF" fillRule="evenodd" d="M8 0C3.584 0 0 3.584 0 8s3.584 8 8 8 8-3.584 8-8-3.584-8-8-8ZM1.714 8.029c0-3.452 2.855-6.248 6.379-6.248 1.475 0 2.83.492 3.907 1.32l-8.938 8.754A6.091 6.091 0 0 1 1.714 8.03ZM4 12.904a6.385 6.385 0 0 0 3.907 1.32c3.524 0 6.379-2.796 6.379-6.247a6.091 6.091 0 0 0-1.348-3.827L4 12.904Z" clipRule="evenodd" /></svg>
);

const CRITIAL_ICON = (
  <svg
    width="16"
    height="13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16 12.364 8 0 0 12.364h16Zm-7.273-2.182H7.273V8.728h1.454v1.454ZM7.273 8h1.454V5.091H7.273v2.91Z"
      fill="#FF0"
    />
  </svg>
);

const BELL_ICON = (
  <svg
    width="16"
    height="16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14 7.439v4.376l2 1.75v.875H9.988C9.878 15.314 9.026 16 8 16c-1.036 0-1.88-.686-1.988-1.56H0v-.875l2-1.75V7.439c0-2.696 1.63-4.936 4.5-5.531v-.595C6.5.586 7.17 0 8 0s1.5.586 1.5 1.313v.595c2.86.595 4.5 2.844 4.5 5.53Z"
      fill="#FF6060"
    />
  </svg>
);

const SUCCESS_ICON = (
  <svg
    width="12"
    height="12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 6a6 6 0 1 1 12 0A6 6 0 1 1 0 6Zm2.646-.238 1.896 1.796L9.36 3l.641.608-5.458 5.17L2 6.369l.646-.607Z"
      fill="#00B080"
    />
  </svg>
);
// #endregion

const CaseDashboard = (props) => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [activeDrawerTab, setActiveDrawerTab] = useState('faceSheet');
  const [consentActiveTab, setConsentActiveTab] = useState('anesthesia');
  const [anesthesiaActiveTab, setAnesthesiaActiveTab] = useState('preOp');
  const [ordersActiveSubTab, setOrdersActiveSubTab] = useState('preOp');
  const [payersActiveTab, setPayersActiveTab] = useState('primary');
  const [recordsActiveSubTab, setRecordsActiveSubTab] = useState('labs');
  const [caseData, setCaseData] = useState({});
  const [masterFlag, setMasterFlag] = useState(FLAG_VALUE.critical);
  const [caseFlag, setCaseFlag] = useState(FLAG_VALUE.critical);
  const [caseTabIcon, setCaseTabIcon] = useState(FLAG_VALUE.critical);
  const [patientFlag, setPatientFlag] = useState(FLAG_VALUE.warning);
  const [payerFlag, setPayerFlag] = useState(FLAG_VALUE.warning);
  const [primaryFlag, setPrimaryFlag] = useState(FLAG_VALUE.warning);
  const [secondaryFlag, setSecondaryFlag] = useState(FLAG_VALUE.warning);
  const [tertiaryFlag, setTertiaryFlag] = useState(FLAG_VALUE.warning);
  const [selfPayFlag, setSelfPayFlag] = useState(FLAG_VALUE.warning);
  const [patientData, setPatientData] = useState({});
  const [isNoPrimaryInsurance, setIsNoPrimaryInsurance] = useState(false);
  const [isNoSecondaryInsurance, setIsNoSecondaryInsurance] = useState(false);
  const [isNoTertiaryInsurance, setIsNoTertiaryInsurance] = useState(false);
  const [primaryInsuranceData, setPrimaryInsuranceData] = useState(null);
  const [secondaryInsuranceData, setSecondaryInsuranceData] = useState(null);
  const [tertiaryInsuranceData, setTertiaryInsuranceData] = useState(null);
  // RCM Section state variables
  const [activeRcmTab, setActiveRcmTab] = useState('patient-case-info');
  const [activePatientCaseInfoTab, setActivePatientCaseInfoTab] = useState('case-info');
  const [activeFacilityRCMTab, setActiveFacilityRCMTab] = useState('superbill');
  const [activeNursingRCMTab, setActiveNursingRCMTab] = useState('superbill');
  const [activeSurgeonRCMTab, setActiveSurgeonRCMTab] = useState('superbill');
  const [activeAnesthesiaRCMTab, setActiveAnesthesiaRCMTab] = useState('superbill');
  // --
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  // --
  const [showPatientProfileDrawer, setShowPatientProfileDrawer] = useState(false);
  const [showPatientAvatarCropperDrawer, setShowPatientAvatarCropperDrawer] = useState(false);
  const [showPatientIdFrontCropperDrawer, setShowPatientIdFrontCropperDrawer] = useState(false);
  const [showPatientIdBackCropperDrawer, setShowPatientIdBackCropperDrawer] = useState(false);
  const [showPatientDrawerCloseWarning, setShowPatientDrawerCloseWarning] = useState(false);
  // Primary Insurance
  const [showPrimaryInsuranceDrawer, setShowPrimaryInsuranceDrawer] = useState(false);
  const [piPayerData, setPiPayerData] = useState({});
  const [showPrimaryInsuranceDrawerCloseWarning, setShowPrimaryInsuranceDrawerCloseWarning] = useState(false);
  const [showPrimaryInsurancePayerDeleteWarning, setShowPrimaryInsurancePayerDeleteWarning] = useState(false);
  // Secondary Insurance
  const [showSecondaryInsuranceDrawer, setShowSecondaryInsuranceDrawer] = useState(false);
  const [siPayerData, setSiPayerData] = useState({});
  const [showSecondaryInsuranceDrawerCloseWarning, setShowSecondaryInsuranceDrawerCloseWarning] = useState(false);
  const [showSecondaryInsurancePayerDeleteWarning, setShowSecondaryInsurancePayerDeleteWarning] = useState(false);
  // Tertiary Insurance
  const [showTertiaryInsuranceDrawer, setShowTertiaryInsuranceDrawer] = useState(false);
  const [tiPayerData, setTiPayerData] = useState({});
  const [showTertiaryInsuranceDrawerCloseWarning, setShowTertiaryInsuranceDrawerCloseWarning] = useState(false);
  const [showTertiaryInsurancePayerDeleteWarning, setShowTertiaryInsurancePayerDeleteWarning] = useState(false);
  // --
  const [showPrimaryInsuranceIdFrontDrawer, setShowPrimaryInsuranceIdFrontDrawer] = useState(false);
  const [showPrimaryInsuranceIdBackDrawer, setShowPrimaryInsuranceIdBackDrawer] = useState(false);
  const [showSecondaryInsuranceIdFrontDrawer, setShowSecondaryInsuranceIdFrontDrawer] = useState(false);
  const [showSecondaryInsuranceIdBackDrawer, setShowSecondaryInsuranceIdBackDrawer] = useState(false);
  const [showTertiaryInsuranceIdFrontDrawer, setShowTertiaryInsuranceIdFrontDrawer] = useState(false);
  const [showTertiaryInsuranceIdBackDrawer, setShowTertiaryInsuranceIdBackDrawer] = useState(false);
  //
  const [payerSearch, setPayerSearch] = useState('');
  const [showAddPayerDrawer, setShowAddPayerDrawer] = useState(false);
  const [payerBannerImageUrl, setPayerBannerImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/defaultBanner.svg');
  const [payerAvatarImageUrl, setPayerAvatarImageUrl] = useState('');
  const [showAvatarCropperDrawer, setShowAvatarCropperDrawer] = useState(false);
  const [showBannerCropperDrawer, setShowBannerCropperDrawer] = useState(false);
  // Case Posting Drawer
  const [showCasePostingDrawer, setShowCasePostingDrawer] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  // Resizing
  const [isResizing, setIsResizing] = useState(false);
  const [docsWidth, setDocsWidth] = useState(1050); // was 1024px
  // ---
  const [patientDos, setPatientDos] = useState('');
  // --- Patinet Card
  const [patientAdmType, setPatientAdmType] = useState('');
  const [patientFacility, setPatientFacility] = useState('');
  const [patientSurgeons, setPatientSurgeons] = useState([]);
  const [patientAnesthesia, setPatientAnesthesia] = useState([]);
  const [patientPreOpDx, setPatientPreOpDx] = useState([]);
  const [patientSurgicalProcedures, setPatientSurgicalProcedures] = useState([]);
  // --- Disccard | Post Case | Post Case
  const [showDisacardDialog1, setShowDiscardDialog1] = useState(false);
  const [showDisacardDialog2, setShowDiscardDialog2] = useState(false);
  const [shouldSaveCase, setShouldSaveCase] = useState(false);
  // --- Anesthesia Data
  const [anesthesiaProceduresList, setAnesthesiaProceduresList] = useState([]);
  const [isNoneAnesthesiaProcedure, setIsNoneAnesthesiaProcedure] = useState(false);
  // ASA Score
  const [asaScore, setAsaScore] = useState('');
  // -- Anesthesia Types
  const [anesthesiaTypes, setAnesthesiaTypes] = useState([]);
  // Anesthesia Modifiers
  const [anesthesiaModifiers, setAnesthesiaModifiers] = useState([]);
  // Anes Start Time
  const [anesStartHour, setAnesStartHour] = useState(24);
  const [anesStartMinute, setAnesStartMinute] = useState(60);
  const [isAnesStartTimePristine, setIsAnesStartTimePristine] = useState(true);
  // Anes Stop Time
  const [anesStopHour, setAnesStopHour] = useState(24);
  const [anesStopMinute, setAnesStopMinute] = useState(60);
  const [isAnesStopTimePristine, setIsAnesStopTimePristine] = useState(true);
  const [showAnesStopFlag, setShowAnesStopFlag] = useState(true);
  // Payer delete warning
  const [showPrimaryPayerDeleteWarning, setShowPrimaryPayerDeleteWarning] = useState(false);
  const [showSecondaryPayerDeleteWarning, setShowSecondaryPayerDeleteWarning] = useState(false);
  const [showTertiaryPayerDeleteWarning, setShowTertiaryPayerDeleteWarning] = useState(false);

  // Anesthesia Procedures
  const [anesthesiaProcedureValue, setAnesthesiaProcedureValue] = useState('');
  const [showAnesthesiaProceduresSearchMenu, setShowAnesthesiaProceduresSearchMenu] = useState(false);
  const [anesthesiaProceduresSearchResults, setAnesthesiaProceduresSearchResults] = useState([]);

  // Router History & Params
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;
  window.pn = pathname;
  const { path, url } = useRouteMatch();
  // console.log(`path = ${path}`);
  // console.log(`url = ${url}`);
  // console.log(`pathname = ${pathname}`);
  const { caseId } = useParams();

  const getCaseData = () => {
    setIsLoadingData(true);
    const formData = new FormData();
    formData.append('caseId', caseId);

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/cases/getCaseDataNew`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        // console.log(res.patientData);
        if (res.success) {
          setIsLoadingData(false);
          // console.log(res.caseData);
          const {
            dateOfSurgery,
            isScheduleToFollow,
            scheduleTimeStartHour,
            scheduleTimeStartMinute,
            scheduleTimeEndHour,
            scheduleTimeEndMinute,
            patientType,
            selectedFacilityLocation,
            surgicalProcedures,
            otherSurgicalProcedures,
            preOpDx,
            otherPreOpDx,
            postOpDx,
            otherPostOpDx,
            isSameAsPreOpDx
          } = res.caseData;

          // -- Date of Surgery
          const tmpDos1 = dateOfSurgery.split('T');
          const tmpDos2 = tmpDos1[0].split('-');
          const dosDate = +tmpDos2[2];
          const dosMonth = +tmpDos2[1] - 1;
          const dosYear = +tmpDos2[0];
          if (res.caseData && !res.caseData.isDosTbd) {
            setPatientDos(`${padZero(+dosYear)}-${padZero(+dosMonth + 1)}-${padZero(+dosDate)}T00:00:00.000Z`);
          } else {
            setPatientDos('');
          }

          // Patinet Type
          if (res.caseData.patientType && res.caseData.patientType.length > 0) {
            setPatientAdmType(res.caseData.patientType);
          } else {
            setPatientAdmType('Not Set');
          }

          // -- Scheduled Time
          const scheduleStartHour = scheduleTimeStartHour;
          const scheduleStartMin = scheduleTimeStartMinute;
          const scheduleEndHour = scheduleTimeEndHour;
          const scheduleEndMin = scheduleTimeEndMinute;

          /* _______________ Case Times _______________ */
          // Anes Start Time
          if (res.caseData.anesthesiaStartHour !== undefined && res.caseData.anesthesiaStartMinute !== undefined) {
            setAnesStartHour(res.caseData.anesthesiaStartHour);
            setAnesStartMinute(res.caseData.anesthesiaStartMinute);
            setIsAnesStartTimePristine(false);
          } else {
            setAnesStartHour(24);
            setAnesStartMinute(60);
          }

          // Anes End
          if (res.caseData.anesthesiaEndHour !== undefined && res.caseData.anesthesiaEndMinute !== undefined) {
            setAnesStopHour(res.caseData.anesthesiaEndHour);
            setAnesStopMinute(res.caseData.anesthesiaEndMinute);
            setIsAnesStopTimePristine(false);
          } else {
            setAnesStopHour(24);
            setAnesStopMinute(60);
          }

          // ASA Score, Anesthesia Types, and Anesthesia Modifiers
          setAsaScore(res.caseData.asaScore || []);
          setAnesthesiaTypes(res.caseData.anesthesiaTypes || []);
          setAnesthesiaModifiers(res.caseData.anesthesiaModifiers || []);

          setCaseData({
            ...res.caseData,
            // Date of Surgery
            surgeonsList: res.caseData.surgeons,
            crnasList: res.caseData.crnas,
            anesthesiologistsList: res.caseData.anesthesiologists,
            dosDate,
            dosMonth,
            dosYear,
            isDosTbd: res.caseData.isDosTbd,
            isDosPristine: res.caseData.isDosTbd,
            showDosAlert: false,
            // Scheduled Time
            scheduleStartHour,
            scheduleStartMin,
            scheduleEndHour,
            scheduleEndMin,
            isScheduleStartTimePristine: false,
            isScheduleEndTimePristine: false,
            isScheduleTimeTF: isScheduleToFollow,
            stAlertFlag: false,
            stInCompleteFlag: false,
            // Admission Type
            admissionType: patientType,
            // Facility Location
            selectedLocation: selectedFacilityLocation,
            // Surgical Procedures
            proceduresList: surgicalProcedures.concat(otherSurgicalProcedures),
            // PreOp Dx
            preOpDxList: preOpDx.concat(otherPreOpDx),
            // postOpDx
            postOpDxList: postOpDx.concat(otherPostOpDx),
            isSameAsPreOpDx
          });

          setPatientData(res.patientData);
          setIsNoPrimaryInsurance(res.patientData.isNoPrimaryInsurance);
          setIsNoSecondaryInsurance(res.patientData.isNoSecondaryInsurance);
          setIsNoTertiaryInsurance(res.patientData.isNoTertiaryInsurance);

          if (res.patientData.primaryInsurance && Object.keys(res.patientData.primaryInsurance).length > 0) {
            setPrimaryInsuranceData(res.patientData.primaryInsurance);
          } else {
            setPrimaryInsuranceData(null);
          }

          if (res.patientData.secondaryInsurance && Object.keys(res.patientData.secondaryInsurance).length > 0) {
            setSecondaryInsuranceData(res.patientData.secondaryInsurance);
          } else {
            setSecondaryInsuranceData(null);
          }

          if (res.patientData.tertiaryInsurance && Object.keys(res.patientData.tertiaryInsurance).length > 0) {
            setTertiaryInsuranceData(res.patientData.tertiaryInsurance);
          } else {
            setTertiaryInsuranceData(null);
          }
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnCaseDataChange = () => {
    if (!shouldSaveCase) {
      setShouldSaveCase(true);
    }
  };

  const loadPiPayerData = () => {
    const url = new URL(`${BASE_URL}/patients/getPatientPrimaryInsuranceData`);
    const params = { patientId: patientData._id };
    url.search = new URLSearchParams(params).toString();

    fetch(url)
      .then(response => response.text())
      .then(result => {
        const reqData = JSON.parse(result);
        if (reqData.payerData && Object.keys(reqData.payerData).length > 0) {
          console.log(reqData.payerData);
          setPiPayerData(reqData.payerData);
        }
      })
      .catch(error => console.log('error', error));
  };

  const loadSiPayerData = () => {
    const url = new URL(`${BASE_URL}/patients/getPatientSecondaryInsuranceData`);
    const params = { patientId: patientData._id };
    url.search = new URLSearchParams(params).toString();

    fetch(url)
      .then(response => response.text())
      .then(result => {
        const reqData = JSON.parse(result);
        if (reqData.payerData && Object.keys(reqData.payerData).length > 0) {
          setSiPayerData(reqData.payerData);
        }
      })
      .catch(error => console.log('error', error));
  };

  const loadTiPayerData = () => {
    const url = new URL(`${BASE_URL}/patients/getPatientTertiaryInsuranceData`);
    const params = { patientId: patientData._id };
    url.search = new URLSearchParams(params).toString();

    fetch(url)
      .then(response => response.text())
      .then(result => {
        const reqData = JSON.parse(result);
        if (reqData.payerData && Object.keys(reqData.payerData).length > 0) {
          setTiPayerData(reqData.payerData);
        }
      })
      .catch(error => console.log('error', error));
  };

  // Load case data
  useEffect(() => {
    getCaseData();
  }, []);

  const handleWindowResize = () => {
    setWindowHeight(window.innerHeight);
    // setIsMobileLayout(window.innerWidth <= 1500);
    if (window.innerWidth <= 700) {
      setDocsWidth(window.innerWidth);
    }
    setIsMobileLayout(window.innerWidth <= 1200);
  };

  const docMouseUp = () => {
    document.removeEventListener('mousemove', callBackMouseMove);
  };

  // Effects
  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    document.addEventListener('mouseup', docMouseUp);
    handleWindowResize();
    return () => {
      window.removeEventListener('resize', handleWindowResize);
      document.removeEventListener('mouseup', docMouseUp);
    };
  }, []);

  // Primary Insurance Payer Data
  useEffect(() => {
    if (patientData.primaryInsurance && Object.keys(patientData.primaryInsurance).length > 0) {
      loadPiPayerData();
    }
  }, [primaryInsuranceData]);

  useEffect(() => {
    if (patientData.secondaryInsurance && Object.keys(patientData.secondaryInsurance).length > 0) {
      loadSiPayerData();
    }
  }, [secondaryInsuranceData]);

  useEffect(() => {
    if (patientData.tertiaryInsurance && Object.keys(patientData.tertiaryInsurance).length > 0) {
      loadTiPayerData();
    }
  }, [tertiaryInsuranceData]);

  // Search Anesthesia Procedure Value
  // --- Surgical Procedures Search ---
  const searchSurgicalProcedures = (value) => {
    const url = new URL(`${BASE_URL}/anesProc/searchAnesthesiaProcedures`);
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        setShowAnesthesiaProceduresSearchMenu(true);
        setAnesthesiaProceduresSearchResults(data.anesthesiaProcedures);
      })
      .catch(error => console.log('error', error));
  };
  const debouncedProceduresSearch = useCallback(debounce(value => searchSurgicalProcedures(value), 800), []);

  useEffect(() => {
    if (anesthesiaProcedureValue && anesthesiaProcedureValue.length === 0) {
      setAnesthesiaProceduresSearchResults([]);
    }
    if (anesthesiaProcedureValue && anesthesiaProcedureValue.trim().length > 0) {
      debouncedProceduresSearch(anesthesiaProcedureValue);
    }
  }, [anesthesiaProcedureValue]);

  // Case Times Flags ( For Each Step)
  useEffect(() => {
    const anesStartTime = `${anesStartMinute < 10 ? anesStartHour * 10 : anesStartHour}${anesStartMinute}`;
    const anesStopTime = `${anesStopMinute < 10 ? anesStopHour * 10 : anesStopHour}${anesStopMinute}`;
    console.log(`🩻 anesStartTime = ${anesStartTime} -- ${typeof anesStartHour}, ${typeof anesStartMinute}`);
    console.log(`🩻 anesStopTime = ${anesStopTime} -- ${typeof anesStopHour}, ${typeof anesStopMinute}`);
    const tmp = +anesStopTime > +anesStartTime;
    console.log(`Value of anesTopFlag = ${tmp}`);
    setShowAnesStopFlag(+anesStopTime <= +anesStartTime);
  }, [
    anesStartHour,
    anesStartMinute,
    anesStopHour,
    anesStopMinute
  ]);

  // Patient Flag and Payers Flag
  useEffect(() => {
    // Patient Flag
    if (Object.keys(patientData).length > 0) {
      // Patient Flag
      setPatientFlag(FLAG_VALUE.success);

      // Payer Flag
      let piFlag = FLAG_VALUE.warning;
      let siFlag = FLAG_VALUE.warning;
      let tiFlag = FLAG_VALUE.warning;
      let spFlag = FLAG_VALUE.warning;

      // Primary Insurance Flag
      if (patientData.isNoPrimaryInsurance) {
        piFlag = FLAG_VALUE.success;
        setPrimaryFlag(FLAG_VALUE.success);
      } else {
        if (patientData.primaryInsurance && Object.keys(patientData.primaryInsurance).length > 0) {
          piFlag = FLAG_VALUE.success;
          setPrimaryFlag(FLAG_VALUE.success);
        } else {
          piFlag = FLAG_VALUE.warning;
          setPrimaryFlag(FLAG_VALUE.warning);
        }
      }

      // Secondary Insurance Flag
      if (patientData.isNoSecondaryInsurance) {
        siFlag = FLAG_VALUE.success;
        setSecondaryFlag(FLAG_VALUE.success);
      } else {
        if (patientData.secondaryInsurance && Object.keys(patientData.secondaryInsurance).length > 0) {
          siFlag = FLAG_VALUE.success;
          setSecondaryFlag(FLAG_VALUE.success);
        } else {
          siFlag = FLAG_VALUE.warning;
          setSecondaryFlag(FLAG_VALUE.warning);
        }
      }

      // Tertiary Insurance Flag
      if (patientData.isNoTertiaryInsurance) {
        tiFlag = FLAG_VALUE.success;
        setTertiaryFlag(FLAG_VALUE.success);
      } else {
        if (patientData.tertiaryInsurance && Object.keys(patientData.tertiaryInsurance).length > 0) {
          tiFlag = FLAG_VALUE.success;
          setTertiaryFlag(FLAG_VALUE.success);
        } else {
          tiFlag = FLAG_VALUE.warning;
          setTertiaryFlag(FLAG_VALUE.warning);
        }
      }

      // Self Pay Flag
      if (patientData.isNoSelfPay) {
        spFlag = FLAG_VALUE.success;
        setSelfPayFlag(FLAG_VALUE.success);
      } else {
        spFlag = FLAG_VALUE.warning;
        setSelfPayFlag(FLAG_VALUE.warning);
      }

      setPayerFlag(findFlag(piFlag, siFlag, tiFlag, spFlag));
    } else {
      setPatientFlag(FLAG_VALUE.warning);
      setPayerFlag(FLAG_VALUE.warning);
    }
  }, [patientData]);

  // Payer Flag
  useEffect(() => {
    setPayerFlag(findFlag(primaryFlag, secondaryFlag, tertiaryFlag));
  }, [
    primaryFlag,
    secondaryFlag,
    tertiaryFlag
  ]);

  // Case Flag (CaseInfo, Patient Indo and Payers)
  useEffect(() => {
    console.log('--- Setting Case Flag!');
    setCaseFlag(findFlag(caseTabIcon, patientFlag, payerFlag));
  }, [
    caseTabIcon,
    patientFlag,
    payerFlag
  ]);

  // Case Master Flag
  useEffect(() => {
    /* ----- Case Times ----- */
    // const timeFlag = showTimesFlag ? FLAG_VALUE.critical : FLAG_VALUE.success;
    const timeFlag = (isAnesStopTimePristine || showAnesStopFlag) ? FLAG_VALUE.critical : FLAG_VALUE.success;

    if (Object.keys(caseData).length > 0) {
      /* ----- Face Sheet Tab Flag ----- */
      let faceSheetFlag = FLAG_VALUE.critical;
      if (caseData.isNoneFaceSheet) {
        faceSheetFlag = FLAG_VALUE.none;
      } else if (caseData.faceSheetDocs.length > 0 || caseData.isNoneFaceSheet) {
        faceSheetFlag = FLAG_VALUE.success;
      } else {
        faceSheetFlag = FLAG_VALUE.critical;
      }

      /* ----- Records Tab Flag ----- */
      let recordsFlag = FLAG_VALUE.critical;
      if (caseData.isNoneLabDocs && caseData.isNoneEkgDocs && caseData.isNoneImagingDocs && caseData.isNoneConsults) {
        recordsFlag = FLAG_VALUE.none;
      } else {
        const labsFlag = caseData.labsDocs.length > 0 || caseData.isNoneLabDocs;
        const ekgFlag = caseData.ekgDocs.length > 0 || caseData.isNoneEkgDocs;
        const imagingFlag = caseData.imagingDocs.length > 0 || caseData.isNoneImagingDocs;
        const consultsFlag = caseData.consultsDocs.length > 0 || caseData.isNoneConsults;

        if (labsFlag && ekgFlag && imagingFlag && consultsFlag) {
          recordsFlag = FLAG_VALUE.success;
        } else {
          recordsFlag = FLAG_VALUE.critical;
        }
      }

      /* ----- Consent Tab Flag ----- */
      let consentFlag = FLAG_VALUE.critical;
      if (caseData.isNoneConsentSurgFormDocs && caseData.isNoneConsentFormDocs) {
        consentFlag = FLAG_VALUE.none;
      } else {
        const surgFlag = caseData.consentSurgFormDocs.length > 0 || caseData.isNoneConsentSurgFormDocs;
        const anesFlag = caseData.consentFormDocs.length > 0 || caseData.isNoneConsentFormDocs;

        if (surgFlag && anesFlag) {
          consentFlag = FLAG_VALUE.success;
        } else {
          consentFlag = FLAG_VALUE.critical;
        }
      }

      /* ----- Anesthesia Tab Flag ----- */
      /*
      preOpFormDocs     isNonePreOpFormDocs
      intraOpFormDocs   isNoneIntraOpFormDocs
      postOpFormDocs    isNonePostOpFormDocs
      */
      let anesthesiaFlag = FLAG_VALUE.critical;
      // PreOp Tab Flag
      let preOpFlagValue = FLAG_VALUE.critical;
      if (caseData.isNonePreOpFormDocs) {
        preOpFlagValue = FLAG_VALUE.none;
      } else {
        if (caseData.preOpFormDocs.length > 0) {
          preOpFlagValue = FLAG_VALUE.success;
        } else {
          preOpFlagValue = FLAG_VALUE.critical;
        }
      }

      // IntraOp Tab Flag
      let intraOpFlagValue = FLAG_VALUE.critical;
      let anesthesiaDataFlagValue = FLAG_VALUE.critical;
      if (caseData.asaScore.length > 0 && caseData.anesthesiaTypes.length > 0 && caseData.anesthesiaModifiers.length > 0) {
        anesthesiaDataFlagValue = FLAG_VALUE.success;
      }
      if (anesthesiaDataFlagValue === FLAG_VALUE.critical) {
        intraOpFlagValue = anesthesiaDataFlagValue;
      } else {
        if (caseData.isNoneIntraOpFormDocs) {
          intraOpFlagValue = FLAG_VALUE.none;
        } else {
          if (caseData.intraOpFormDocs.length > 0) {
            intraOpFlagValue = FLAG_VALUE.success;
          } else {
            intraOpFlagValue = FLAG_VALUE.critical;
          }
        }
      }

      // PostOp Tab Flag
      let postOpFlagValue = FLAG_VALUE.critical;
      if (caseData.isNonePostOpFormDocs) {
        postOpFlagValue = FLAG_VALUE.none;
      } else {
        if (caseData.postOpFormDocs.length > 0) {
          postOpFlagValue = FLAG_VALUE.success;
        } else {
          postOpFlagValue = FLAG_VALUE.critical;
        }
      }

      // Procedures Tab Flag
      let proceduresFlagValue = FLAG_VALUE.critical;
      if (caseData.isNoneAnesthesiaProcedure) {
        proceduresFlagValue = FLAG_VALUE.none;
      } else {
        if (caseData.anesthesiaProcedures.length === 0) {
          proceduresFlagValue = FLAG_VALUE.critical;
        } else if (caseData.anesthesiaProcedures.length > 0 && (caseData.anesthesiaProcedures.filter(el => el.npi === 0)).length > 0) {
          proceduresFlagValue = FLAG_VALUE.warning;
        } else if (caseData.anesthesiaProcedures.length > 0 && (caseData.anesthesiaProcedures.filter(el => el.npi === 0)).length === 0) {
          proceduresFlagValue = FLAG_VALUE.success;
        } else {
          proceduresFlagValue = FLAG_VALUE.critical;
        }
      }

      const anesthesiaTabFlag = findFlag(preOpFlagValue, intraOpFlagValue, postOpFlagValue, proceduresFlagValue);
      switch (anesthesiaTabFlag) {
        case FLAG_VALUE.critical:
          anesthesiaFlag = FLAG_VALUE.critical;
          break;
        case FLAG_VALUE.warning:
          anesthesiaFlag = FLAG_VALUE.warning;
          break;
        case FLAG_VALUE.success:
          anesthesiaFlag = FLAG_VALUE.success;
          break;
        case FLAG_VALUE.none:
          anesthesiaFlag = FLAG_VALUE.none;
          break;
        default:
          anesthesiaFlag = FLAG_VALUE.critical;
      }

      /* ----- Nursing Tab Flag ----- */
      let nursingFlag = FLAG_VALUE.critical;
      if (caseData.isNoneNursingDocs) {
        nursingFlag = FLAG_VALUE.none;
      } else {
        if (caseData.nursingDocs.length > 0 || caseData.isNoneNursingDocs) {
          nursingFlag = FLAG_VALUE.success;
        } else {
          nursingFlag = FLAG_VALUE.critical;
        }
      }

      /* ----- Surgeon Tab Flag ----- */
      // surgeonDocs    isNoneSurgeonDocs
      let surgeonFlag = FLAG_VALUE.critical;
      if (caseData.isNoneSurgeonDocs) {
        surgeonFlag = FLAG_VALUE.none;
      } else {
        if (caseData.surgeonDocs.length > 0 || caseData.isNoneSurgeonDocs) {
          surgeonFlag = FLAG_VALUE.success;
        } else {
          surgeonFlag = FLAG_VALUE.critical;
        }
      }

      /* ----- Discharge Tab Flag ----- */
      let dcFlag = FLAG_VALUE.critical;
      if (caseData.isNoneDischargeDocs) {
        dcFlag = FLAG_VALUE.none;
      } else {
        if (caseData.dischargeDocs.length > 0 || caseData.isNoneDischargeDocs) {
          dcFlag = FLAG_VALUE.success;
        }
        dcFlag = FLAG_VALUE.critical;
      }

      /* ----- Orders Tab Flag ----- */
      /*
      ordersPreOpDocs     isNoneOrdersPreOpDocs
      ordersIntraOpDocs   isNoneOrdersIntraOpDocs
      ordresPostOpDocs    isNoneOrdersPostOpDocs
      */
      let ordersFlag = FLAG_VALUE.critical;
      if (caseData.isNoneOrdersPreOpDocs && caseData.isNoneOrdersIntraOpDocs && caseData.isNoneOrdersPostOpDocs) {
        ordersFlag = FLAG_VALUE.none;
      }
      const preOPFlag = caseData.ordersPreOpDocs.length > 0 || caseData.isNoneOrdersPreOpDocs;
      const intraOpFlag = caseData.ordersIntraOpDocs.length > 0 || caseData.isNoneOrdersIntraOpDocs;
      const postOpFlag = caseData.ordresPostOpDocs.length > 0 || caseData.isNoneOrdersPostOpDocs;

      if (preOPFlag && intraOpFlag && postOpFlag) {
        ordersFlag = FLAG_VALUE.success;
      } else {
        ordersFlag = FLAG_VALUE.critical;
      }

      if (DEBUG_CASE_DASHBOARD) {
        const tmp = findFlag(
          caseFlag,
          timeFlag,
          faceSheetFlag,
          recordsFlag,
          consentFlag,
          nursingFlag,
          surgeonFlag,
          anesthesiaFlag,
          ordersFlag,
          dcFlag
        );

        console.log('🔥🔥🔥🔥 -------- 🔥🔥🔥🔥');
        console.log(`Case Master Flag = ${tmp}`);
        console.log(`caseFlag = ${caseFlag}`);
        console.log(`timeFlag = ${timeFlag}`);
        console.log(`faceSheetFlag = ${faceSheetFlag}`);
        console.log(`recordsFlag = ${recordsFlag}`);
        console.log(`consentFlag = ${consentFlag}`);
        console.log(`nursingFlag = ${nursingFlag}`);
        console.log(`surgeonFlag = ${surgeonFlag}`);
        console.log(`anesthesiaFlag = ${anesthesiaFlag}`);
        console.log(`ordersFlag = ${ordersFlag}`);
        console.log(`dcFlag = ${dcFlag}`);
        console.log('🔥🔥🔥🔥 -------- 🔥🔥🔥🔥');
      }

      setMasterFlag(findFlag(
        caseFlag,
        timeFlag,
        faceSheetFlag,
        recordsFlag,
        consentFlag,
        nursingFlag,
        surgeonFlag,
        anesthesiaFlag,
        ordersFlag,
        dcFlag
      ));
    } else {
      setMasterFlag(FLAG_VALUE.critical);
    }
  }, [
    caseData,
    caseFlag,
    isAnesStopTimePristine,
    showAnesStopFlag
  ]);

  // useEffect(() => {
  //   if (isResizing) {
  //     console.log('[Debug] Effects -> Start Resizing');
  //     // document.addEventListener('mousemove', handleMouseMove);
  //     document.addEventListener('mousemove', callBackMouseMove);
  //   } else {
  //     console.log('[Debug] Effects -> Stop Resizing');
  //     // document.removeEventListener('mousemove', handleMouseMove);
  //     document.removeEventListener('mousemove', callBackMouseMove);
  //   }
  // }, [isResizing]);

  // ------------ STYLES ------------
  const styles = StyleSheet.create({
    dashboardContainer: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100vw',
      // height: '100vh',
      // height: `${window.innerHeight - 5}px`,
      height: `${windowHeight - 5}px`,
      // height: 'calc(100vh - 5px)',
      // background: '#780178',
      background: blue[900],
      zIndex: '5000',
      overflow: 'hidden'
    },
    sectionsContainer: {
      height: 'calc(100% - 40px)',
      display: 'flex',
      flexFlow: 'row',
      overflow: 'hidden'
    },
    header: {
      height: '40px',
      background: '#004080',
      borderBottom: '1px solid #000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 4px'
    },
    headerText: {
      fontSize: '14px',
      fontWeight: '700',
      color: '#96C8FF',
      height: '40px',
      display: 'flex',
      alignItems: 'center'
    },
    rightIconContainer: {
      background: 'black',
      borderRadius: '4px',
      height: '28px',
      padding: '2px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '53px'
    },
    patientCardSection: {
      display: 'flex',
      marginTop: '0px',
      padding: '1px',
      justifyContent: isMobileLayout ? 'flex-end' : ''
    },
    patientCard: {
      position: 'relative',
      width: docsWidth >= 700 ? '318px' : '100%',
      minWidth: '318px',
      height: '120px',
      background: 'white',
      marginLeft: docsWidth >= 700 ? '8px' : ''
      // background: blue[900]
    },
    tabSection: {
      background: '#003060'
    },
    tabSectionTop: {
      height: '33px',
      display: 'flex',
      borderBottom: '1px solid black'
    },
    tabSectionDrawerIcon: {
      width: '18px',
      height: '24px',
      background: '#004080',
      color: '#00A0FF',
      fontSize: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    tabHeader: {
      fontSize: '14px',
      color: '#00A0FF',
      marginLeft: '4px'
    },
    tabSectionBottom: {
      height: '34px'
    },
    slidingDrawerMask: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      background: 'black',
      zIndex: '5001',
      display: showDrawer ? 'block' : 'none',
      opacity: '0.2'
    },
    slidingDrawer: {
      position: 'absolute',
      transition: 'left 0.3s ease',
      top: '139px',
      background: '#003060',
      width: '200px',
      height: 'calc(100vh - 143px)',
      zIndex: '5002',
      paddingRight: '8px'
    },
    showDrawer: {
      left: '0'
    },
    hideDrawer: {
      left: '-200px'
    },
    drawerSeparatorContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '50px',
      width: '100%'
    },
    drawerSeparator: {
      height: '1px',
      background: '#004080',
      width: '184px'
    },
    drawerTabsGroup: {
      color: '#96C8FF',
      paddingLeft: '4px',
      fontSize: '14px'
    },
    drawerTab: {
      cursor: 'pointer',
      color: '#96C8FF',
      height: '40px',
      paddingLeft: '8px',
      display: 'flex',
      alignItems: 'center',
      ':hover': {
        color: '#00A0FF',
        background: '#004080',
        borderLeft: '1px solid #00A0FF'
      }
    },
    activeDrawerTab: {
      color: '#00A0FF',
      background: '#004080',
      borderLeft: '1px solid #00A0FF'
    },
    drawerTabIconContainer: {
      cursor: 'pointer',
      background: '#96C8FF',
      height: '24px',
      width: '24px',
      borderRadius: '4px'
    },
    drawerTabText: {
      cursor: 'pointer',
      fontSize: '14px',
      marginLeft: '8px'
    },
    activeComponentContainer: {
      padding: '0',
      // height: 'calc(100vh - 187px)',
      height: 'calc(100vh - 219px)',
      // overflowY: 'auto'
      overflowY: 'hidden'
    },
    patientCardField: {
      color: '#5080B0',
      // color: blue[200],
      fontWeight: '700',
      fontSize: '12px'
    },
    patientCardFieldValue: {
      color: 'black',
      fontSize: '12px',
      maxWidth: '200px'
    },
    halfMask: {
      position: 'fixed',
      background: 'rgb(0, 24, 36)',
      top: '0px',
      height: '100vh',
      width: '100%',
      opacity: '0.8',
      zIndex: '1950'
    },
    btnIcon: {
      fill: blue[800],
      ':not(.disabled):hover': {
        fill: blue[100]
      }
    },
    separator: {
      minWidth: '6px',
      height: 'calc(100vh - 40px)',
      background: '#00A0FF',
      ':hover': {
        cursor: 'ew-resize'
      }
    },
    separator2: {
      position: 'absolute',
      top: '0',
      right: '0',
      minWidth: '6px',
      height: 'calc(100vh - 40px)',
      background: '#00A0FF',
      ':hover': {
        cursor: 'ew-resize'
      }
    },
    docFooter: {
      position: 'absolute',
      bottom: '0',
      height: '32px',
      width: 'calc(100% - 6px)',
      background: blue[700],
      flexGrow: '0',
      display: 'flex',
      alignItems: 'center',
      fontSize: '12px',
      fontWeight: 'bold'
    },
    footerFlag: {
      height: '32px',
      width: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRight: `1px solid ${blue[900]}`,
      paddingBottom: '2px'
    },
    // Drawer
    drawerContainer: {
      background: gray[800],
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    drawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0',
      background: blue[900]
    },
    drawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    drawerBanner: {
      position: 'relative',
      padding: '8px',
      flexShrink: '0'
    },
    drawerBannerImg: {
      width: '100%',
      height: 'auto'
    },
    drawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '0',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerContentArea: {
      // height: 'calc(100vh - 186px)',
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1'
    },
    drawerUserCardAvatar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '80px',
      height: '80px',
      background: blue[500],
      color: 'white',
      borderRadius: '4px',
      position: 'relative'
    },
    drawerUserCardAvatarImg: {
      width: 'auto',
      height: '80px',
      maxHeight: '80px'
    },
    drawerAvatarCamera: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      left: '-3px',
      top: '58px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerUserCardText: {
      fontSize: '12px'
    },
    drawerFooter: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: gray[700],
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0'
    },
    searchPopupMenuContainer: {
      width: `${DRAWER_WIDTH - 21}px`,
      background: blue[900],
      paddingBottom: '1px',
      borderRadius: '5px',
      overflowY: 'auto',
      maxHeight: '480px'
    },
    menuItem: {
      cursor: 'pointer',
      background: gray[900],
      color: gray[300],
      padding: '4px',
      lineHeight: '16px',
      fontSize: '14px',
      marginTop: '1px',
      ':hover': {
        background: '#184878',
        color: 'white'
      },
      ':last-child': {
        borderBottom: '0'
      }
    },
    ellipsisBtn: {
      position: 'relative',
      width: '24px',
      height: '24px',
      background: blue[200],
      color: gray[600],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '2px'
    }
  });

  // Refs
  const mainTabsContainer = useRef();
  const caseTabRef = useRef();
  const pacuMenuRef = useRef();
  const procedureSearchMenuRef = useRef();
  const headerMenuRef = useRef();

  // #region Event Handlers
  const handleOnDeletePrimaryInsurance = () => {
    const updatedPatientData = clone(patientData);
    updatedPatientData.primaryInsuranceCardFrontScan = 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg';
    updatedPatientData.primaryInsuranceCardBackScan = 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg';

    delete updatedPatientData.primaryInsurance;
    setPatientData(updatedPatientData);
    setPrimaryInsuranceData(null);
  };

  const handleOnDeleteSecondaryInsurance = () => {
    const updatedPatientData = clone(patientData);
    updatedPatientData.secondaryInsuranceCardFrontScan = 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg';
    updatedPatientData.secondaryInsuranceCardBackScan = 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg';

    delete updatedPatientData.secondaryInsurance;
    setPatientData(updatedPatientData);
    setSecondaryInsuranceData(null);
  };

  const handleOnDeleteTertiaryInsurance = () => {
    const updatedPatientData = clone(patientData);
    updatedPatientData.tertiaryInsuranceCardFrontScan = 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg';
    updatedPatientData.tertiaryInsuranceCardBackScan = 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg';

    delete updatedPatientData.tertiaryInsurance;
    setPatientData(updatedPatientData);
    setTertiaryInsuranceData(null);
  };

  const handleOnSavePrimaryInsurance = (piData) => {
    // console.log('Patient primary insurance data saved!');
    const updatedPatientData = clone(patientData);
    updatedPatientData.primaryInsurance = {
      authorizationNumber: piData.authorizationNumber,
      groupNumber: piData.groupNumber,
      memberDob: piData.memberDob,
      memberFirstName: piData.memberFirstName,
      memberLastName: piData.memberLastName,
      memberMiddleName: piData.memberMiddleName,
      patientRelationToMember: piData.patientRelationToMember,
      payer: piData.payer,
      policyNumber: piData.policyNumber
    };
    setPatientData(updatedPatientData);
    setPrimaryInsuranceData(piData);
  };

  const handleOnSaveSecondaryInsurance = (siData) => {
    console.log('Patient secondary insurance data saved!');
    const updatedPatientData = clone(patientData);
    updatedPatientData.secondaryInsurance = {
      authorizationNumber: siData.authorizationNumber,
      groupNumber: siData.groupNumber,
      memberDob: siData.memberDob,
      memberFirstName: siData.memberFirstName,
      memberLastName: siData.memberLastName,
      memberMiddleName: siData.memberMiddleName,
      patientRelationToMember: siData.patientRelationToMember,
      payer: siData.payer,
      policyNumber: siData.policyNumber
    };
    setPatientData(updatedPatientData);
    setSecondaryInsuranceData(siData);
  };

  const handleOnSaveTertiaryInsurance = (tiData) => {
    console.log('Patient tertiary insurance data saved!');
    const updatedPatientData = clone(patientData);
    updatedPatientData.tertiaryInsurance = {
      authorizationNumber: tiData.authorizationNumber,
      groupNumber: tiData.groupNumber,
      memberDob: tiData.memberDob,
      memberFirstName: tiData.memberFirstName,
      memberLastName: tiData.memberLastName,
      memberMiddleName: tiData.memberMiddleName,
      patientRelationToMember: tiData.patientRelationToMember,
      payer: tiData.payer,
      policyNumber: tiData.policyNumber
    };
    setPatientData(updatedPatientData);
    setTertiaryInsuranceData(tiData);
  };

  const handleOnSavePatientData = (data) => {
    const {
      patientFirstName,
      patientMiddleName,
      patientLastName,
      patientDobYear,
      patientDobDate,
      patientDobMonth,
      patientSSN1,
      patientSSN2,
      patientSSN3,
      patientDisplayName,
      patientUserName,
      patientHomeStreetAddress,
      patientHomeSuite,
      patientHomeCity,
      patientHomeState,
      patientHomeZip,
      mailingAddressSameAsHomeAddress,
      patientMailingStreetAddress,
      patientMailingSuite,
      patientMailingCity,
      patientMailingState,
      patientMailingZip,
      contactType,
      mobile,
      email,
      patientIdFrontScan,
      patientIdBackScan
    } = data;

    const formData = new FormData();
    formData.append('patientId', patientData._id);
    formData.append('firstName', patientFirstName);
    formData.append('middleName', patientMiddleName);
    formData.append('lastName', patientLastName);
    //
    formData.append('ssn1', patientSSN1);
    formData.append('ssn2', patientSSN2);
    formData.append('ssn3', patientSSN3);
    formData.append('dob', `${patientDobYear}-${padZero(+(patientDobMonth) + 1)}-${padZero(+patientDobDate)}` + 'T00:00:00.000Z');
    //
    formData.append('displayName', patientDisplayName);
    formData.append('userName', patientUserName);
    //
    formData.append('homeStreetAddress', patientHomeStreetAddress);
    formData.append('homeSuite', patientHomeSuite);
    formData.append('homeCity', patientHomeCity);
    formData.append('homeState', patientHomeState);
    formData.append('homeZip', patientHomeZip);
    //
    formData.append('mailingAddressSameAsHomeAddress', mailingAddressSameAsHomeAddress);
    if (!mailingAddressSameAsHomeAddress) {
      formData.append('mailingStreetAddress', patientMailingStreetAddress);
      formData.append('mailingSuite', patientMailingSuite);
      formData.append('mailingCity', patientMailingCity);
      formData.append('mailingState', patientMailingState);
      formData.append('mailingZip', patientMailingZip);
    }
    //
    formData.append('mobile', mobile);
    formData.append('contactType', contactType);
    formData.append('email', email);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/patients/updatePatientProfile`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        if (res.success) {
          // const dd = new Date(patientDobYear, patientDobMonth, patientDobDate);
          if (!mailingAddressSameAsHomeAddress) {
            setPatientData((prvState) => ({
              ...prvState,
              firstName: patientFirstName,
              middleName: patientMiddleName,
              lastName: patientLastName,
              // dob: dd.toISOString(),
              dob: `${patientDobYear}-${padZero(patientDobMonth + 1)}-${padZero(patientDobDate)}` + 'T00:00:00.000Z',
              ssn1: patientSSN1,
              ssn2: patientSSN2,
              ssn3: patientSSN3,
              displayName: patientDisplayName,
              userName: patientUserName,
              homeAddress: {
                streetAddress1: patientHomeStreetAddress,
                suite: patientHomeSuite,
                city: patientHomeCity,
                state: patientHomeState,
                zip: patientHomeZip
              },
              mailingAddressSameAsHomeAddress,
              mailingAddress: {
                streetAddress1: patientMailingStreetAddress,
                suite: patientMailingSuite,
                city: patientMailingCity,
                state: patientMailingState,
                zip: patientMailingZip
              },
              mobile,
              contactType,
              email,
              patientIdFrontScan,
              patientIdBackScan
            }));
          } else {
            setPatientData((prvState) => ({
              ...prvState,
              firstName: patientFirstName,
              middleName: patientMiddleName,
              lastName: patientLastName,
              dob: `${patientDobYear}-${padZero(patientDobMonth + 1)}-${padZero(patientDobDate)}` + 'T00:00:00.000Z',
              ssn1: patientSSN1,
              ssn2: patientSSN2,
              ssn3: patientSSN3,
              displayName: patientDisplayName,
              userName: patientUserName,
              homeAddress: {
                streetAddress1: patientHomeStreetAddress,
                suite: patientHomeSuite,
                city: patientHomeCity,
                state: patientHomeState,
                zip: patientHomeZip
              },
              mailingAddress: {
                streetAddress1: patientHomeStreetAddress,
                suite: patientHomeSuite,
                city: patientHomeCity,
                state: patientHomeState,
                zip: patientHomeZip
              },
              mailingAddressSameAsHomeAddress,
              mobile,
              contactType,
              email,
              patientIdFrontScan,
              patientIdBackScan
            }));
          }
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnChangeIsNoPrimaryInsurance = () => {
    const updatedIsNoPiValue = !isNoPrimaryInsurance;
    setIsNoPrimaryInsurance(updatedIsNoPiValue);
    setPrimaryFlag(updatedIsNoPiValue ? FLAG_VALUE.success : FLAG_VALUE.warning);
    const formData = new FormData();
    formData.append('patientId', patientData._id);
    formData.append('isNoPrimaryInsurance', updatedIsNoPiValue);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/patients/setNoPrimaryInsurance`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        if (res.success) {
          setIsNoPrimaryInsurance(res.isNoPrimaryInsurance);
          // props.updateFlag();
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnChangeIsNoSecondaryInsurance = () => {
    const updatedIsNoSiValue = !isNoSecondaryInsurance;
    setIsNoSecondaryInsurance(updatedIsNoSiValue);
    setSecondaryFlag(updatedIsNoSiValue ? FLAG_VALUE.success : FLAG_VALUE.warning);
    const formData = new FormData();
    formData.append('patientId', patientData._id);
    formData.append('isNoSecondaryInsurance', updatedIsNoSiValue);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/patients/setNoSecondaryInsurance`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        if (res.success) {
          setIsNoSecondaryInsurance(res.isNoSecondaryInsurance);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnChangeIsNoTertiaryInsurance = () => {
    const updatedIsNoTiValue = !isNoTertiaryInsurance;
    setIsNoTertiaryInsurance(updatedIsNoTiValue);
    setTertiaryFlag(updatedIsNoTiValue ? FLAG_VALUE.success : FLAG_VALUE.warning);
    const formData = new FormData();
    formData.append('patientId', patientData._id);
    formData.append('isNoTertiaryInsurance', updatedIsNoTiValue);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/patients/setNoTertiaryInsurance`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        if (res.success) {
          setIsNoTertiaryInsurance(res.isNoTertiaryInsurance);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnAddPatient = (data) => {
    console.log(data);
    handleOnCaseDataChange();
    setPatientData(data);
    // setPatientFlag(Object.keys(data).length > 0 ? FLAG_VALUE.success : FLAG_VALUE.warning);
  };

  const handleOnDeletePatient = () => {
    handleOnCaseDataChange();
    setPatientData({});
  };

  // Save/Update Case Data
  const handleOnSaveCase = (data, closeDrawer = false) => {
    console.log('---- handleOnSaveCase ----');
    const TODAY = new Date();
    const formData = new FormData();
    // Auth Headers
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    /* --- Case Data ------- */
    const {
      dosYear = TODAY.getDate(),
      dosMonth = TODAY.getMonth(),
      dosDate = TODAY.getFullYear(),
      isDosTbd,
      isScheduleStartTimePristine = true,
      isScheduleEndTimePristine = true,
      scheduleStartHour = TODAY.getHours(),
      scheduleStartMin = TODAY.getMinutes(),
      scheduleEndHour = TODAY.getHours(),
      scheduleEndMin = TODAY.getMinutes(),
      isScheduleTimeTF = false,
      admissionType = '',
      facility = {},
      selectedLocation = 'TBD (To Be Determined)',
      surgeonsList = [],
      surgeonPractices = [],
      proceduresList = [],
      preOpDxList = [],
      isSkipPreOpDx = false,
      postOpDxList = [],
      isSameAsPreOpDx = false,
      anesthesiologistsList = [],
      isNoneAnesthesiologist = false,
      anesthesiaPractices = [],
      crnasList = [],
      isNoneCrna = false,
      crnaPractices = [],
      lien,
      workmanComp,
      unknownEnd,
      schedulingNotes
    } = data;

    // Case Id
    formData.append('caseId', caseId);
    // Patient Id
    if (Object.keys(patientData).length > 0 && patientData._id) {
      formData.append('patientId', patientData._id);
    }

    /* ____________ CASE TAB SECTION ____________ */
    // DOS
    formData.append('dateOfSurgery', `${dosYear}-${padZero(dosMonth + 1)}-${padZero(dosDate)}T00:00:00.000Z`);
    formData.append('isDosTbd', isDosTbd || false);
    // Scheduled Times
    formData.append('scheduleStartHour', !isScheduleStartTimePristine ? scheduleStartHour : null);
    formData.append('scheduleStartMin', !isScheduleStartTimePristine ? scheduleStartMin : null);
    formData.append('scheduleEndHour', !isScheduleEndTimePristine ? scheduleEndHour : null);
    formData.append('scheduleEndMin', !isScheduleEndTimePristine ? scheduleEndMin : null);
    formData.append('isScheduleTimeTF', JSON.stringify(isScheduleTimeTF));
    // Admission Type
    formData.append('admissionType', admissionType);
    // Facility
    formData.append('facility', JSON.stringify(facility));
    formData.append('selectedFacilityLocation', selectedLocation);
    // Surgeon
    formData.append('surgeons', JSON.stringify(surgeonsList.filter(el => el.npi !== 0)));
    const tmpSurgeonPractices = [];
    surgeonPractices.forEach((el) => {
      const tmpProviders = [];
      if (el.addedProviders.length > 0) {
        el.addedProviders.forEach((provider) => {
          tmpProviders.push({
            providerId: provider.providerId || provider._id,
            providerNpi: provider.providerNpi || provider.npi,
            providerFirstName: provider.providerFirstName || provider.firstName,
            providerLastName: provider.providerLastName || provider.lastName,
            providerMiddleName: provider.providerMiddleName || provider.middleName,
            specialty: provider.specialty,
            avatarImageUrl: provider.avatarImageUrl
          });
        });
      }
      tmpSurgeonPractices.push({
        practiceId: el._id,
        practiceName: el.practiceName,
        practiceNpi: el.npi,
        addedProviders: tmpProviders,
        taxonomy: el.taxonomy,
        avatarImageUrl: el.avatarImageUrl
      });
    });
    formData.append('surgeonPractices', JSON.stringify(tmpSurgeonPractices));
    // Surgical Procedures
    formData.append('procedures', JSON.stringify(proceduresList.filter(el => el.npi !== 0)));
    formData.append('otherProcedures', JSON.stringify(proceduresList.filter(el => el.npi === 0)));
    // --- PreOpDx
    formData.append('preOpDx', JSON.stringify(preOpDxList.filter(el => el.npi !== 0)));
    formData.append('otherPreOpDx', JSON.stringify(preOpDxList.filter(el => el.npi === 0)));
    formData.append('isSkipPreOpDx', JSON.stringify(isSkipPreOpDx));
    // --- PostOpDx
    formData.append('postOpDx', JSON.stringify(postOpDxList.filter(el => el.npi !== 0)));
    formData.append('otherPostOpDx', JSON.stringify(postOpDxList.filter(el => el.npi === 0)));
    formData.append('isSameAsPreOpDx', JSON.stringify(isSameAsPreOpDx));
    // Anesthesiologist(s)
    formData.append('anesthesiologists', JSON.stringify(anesthesiologistsList.filter(el => el.npi !== 0)));
    formData.append('otherAnesthesiologists', JSON.stringify(anesthesiologistsList.filter(el => el.npi === 0)));
    formData.append('isNoneAnesthesiologist', JSON.stringify(isNoneAnesthesiologist));
    const tmpAnesthesiaPractices = [];
    anesthesiaPractices.forEach((el) => {
      const tmpProviders = [];
      if (el.addedProviders.length > 0) {
        el.addedProviders.forEach((provider) => {
          tmpProviders.push({
            providerId: provider.providerId || provider._id,
            providerNpi: provider.providerNpi || provider.npi,
            providerFirstName: provider.providerFirstName || provider.firstName,
            providerLastName: provider.providerLastName || provider.lastName,
            providerMiddleName: provider.providerMiddleName || provider.middleName,
            specialty: provider.specialty,
            avatarImageUrl: provider.avatarImageUrl
          });
        });
      }
      tmpAnesthesiaPractices.push({
        practiceId: el._id,
        practiceName: el.practiceName,
        practiceNpi: el.npi,
        addedProviders: tmpProviders,
        taxonomy: el.taxonomy,
        avatarImageUrl: el.avatarImageUrl
      });
    });
    formData.append('anesthesiaPractices', JSON.stringify(tmpAnesthesiaPractices));
    // CRNA
    formData.append('crnas', JSON.stringify(crnasList.filter(el => el.npi !== 0)));
    formData.append('otherCrnas', JSON.stringify(crnasList.filter(el => el.npi === 0)));
    formData.append('isNoneCrna', JSON.stringify(isNoneCrna));
    const tmpCrnaPractices = [];
    crnaPractices.forEach((el) => {
      const tmpProviders = [];
      if (el.addedProviders.length > 0) {
        el.addedProviders.forEach((provider) => {
          tmpProviders.push({
            providerId: provider.providerId || provider._id,
            providerNpi: provider.providerNpi || provider.npi,
            providerFirstName: provider.providerFirstName || provider.firstName,
            providerLastName: provider.providerLastName || provider.lastName,
            providerMiddleName: provider.providerMiddleName || provider.middleName,
            specialty: provider.specialty,
            avatarImageUrl: provider.avatarImageUrl
          });
        });
      }
      tmpCrnaPractices.push({
        practiceId: el._id,
        practiceName: el.practiceName,
        practiceNpi: el.npi,
        addedProviders: tmpProviders,
        taxonomy: el.taxonomy,
        avatarImageUrl: el.avatarImageUrl
      });
    });
    formData.append('crnaPractices', JSON.stringify(tmpCrnaPractices));
    // Anesthesia Data
    formData.append('asa', asaScore);
    formData.append('anesthesiaTypes', JSON.stringify(anesthesiaTypes));
    formData.append('anesthesiaModifiers', JSON.stringify(anesthesiaModifiers));
    // Anesthsia Procedures
    formData.append('anesthesiaProcedures', JSON.stringify(caseData.anesthesiaProcedures.filter(el => el.npi !== 0)));
    formData.append('otherAnesthesiaProcedures', JSON.stringify(caseData.anesthesiaProcedures.filter(el => el.npi === 0)));
    formData.append('isNoneAnesthesiaProcedures', JSON.stringify(caseData.isNoneAnesthesiaProcedure));
    // Lien, Workman Comp, Unknown End and Scheduling Notes
    formData.append('lien', lien);
    formData.append('workmanComp', workmanComp);
    formData.append('unknownEnd', unknownEnd);
    formData.append('schedulingNotes', schedulingNotes);

    /* ____________ CASE TIMES SECTION ____________ */
    // Anesthesia Start Time
    if (!isAnesStartTimePristine) {
      formData.append('anesStartHour', anesStartHour);
      formData.append('anesStartMinute', anesStartMinute);
    }

    // Anesthesia Stop Time
    if (!isAnesStopTimePristine) {
      formData.append('anesStopHour', anesStopHour);
      formData.append('anesStopMinute', anesStopMinute);
    }

    /* ____________ CASE DOCS SECTION ____________ */
    // FaceSheet Docs
    formData.append('faceSheetDocs', JSON.stringify(caseData.faceSheetDocs));
    formData.append('isNoneFaceSheet', JSON.stringify(caseData.isNoneFaceSheet));

    // --- Records ---
    // Labs Docs
    formData.append('labsDocs', JSON.stringify(caseData.labsDocs));
    formData.append('isNoneLabDocs', JSON.stringify(caseData.isNoneLabDocs));
    // EKG Docs
    formData.append('ekgDocs', JSON.stringify(caseData.ekgDocs));
    formData.append('isNoneEkgDocs', JSON.stringify(caseData.isNoneEkgDocs));
    // Imaging Docs
    formData.append('imagingDocs', JSON.stringify(caseData.imagingDocs));
    formData.append('isNoneImagingDocs', JSON.stringify(caseData.isNoneImagingDocs));
    // Consults Docs
    formData.append('consultsDocs', JSON.stringify(caseData.consultsDocs));
    formData.append('isNoneConsults', JSON.stringify(caseData.isNoneConsults));

    // --- Consent Docs ---
    // Anesthesia Consent Docs
    formData.append('consentFormDocs', JSON.stringify(caseData.consentFormDocs));
    formData.append('isNoneConsentFormDocs', JSON.stringify(caseData.isNoneConsentFormDocs));
    // Surgery Consent Docs
    formData.append('consentSurgFormDocs', JSON.stringify(caseData.consentSurgFormDocs));
    formData.append('isNoneConsentSurgFormDocs', JSON.stringify(caseData.isNoneConsentSurgFormDocs));

    // Nursing Docs
    formData.append('nursingDocs', JSON.stringify(caseData.nursingDocs));
    formData.append('isNoneNursingDocs', JSON.stringify(caseData.isNoneNursingDocs));

    // Surgeon Docs
    formData.append('surgeonDocs', JSON.stringify(caseData.surgeonDocs));
    formData.append('isNoneSurgeonDocs', JSON.stringify(caseData.isNoneSurgeonDocs));

    // Anesthesia Docs
    formData.append('preOpFormDocs', JSON.stringify(caseData.preOpFormDocs));
    formData.append('isNonePreOpFormDocs', JSON.stringify(caseData.isNonePreOpFormDocs));
    formData.append('intraOpFormDocs', JSON.stringify(caseData.intraOpFormDocs));
    formData.append('isNoneIntraOpFormDocs', JSON.stringify(caseData.isNoneIntraOpFormDocs));
    formData.append('postOpFormDocs', JSON.stringify(caseData.postOpFormDocs));
    formData.append('isNonePostOpFormDocs', JSON.stringify(caseData.isNonePostOpFormDocs));

    // Orders Docs
    formData.append('ordersPreOpDocs', JSON.stringify(caseData.ordersPreOpDocs));
    formData.append('isNoneOrdersPreOpDocs', JSON.stringify(caseData.isNoneOrdersPreOpDocs));
    formData.append('ordersIntraOpDocs', JSON.stringify(caseData.ordersIntraOpDocs));
    formData.append('isNoneOrdersIntraOpDocs', JSON.stringify(caseData.isNoneOrdersIntraOpDocs));
    formData.append('ordresPostOpDocs', JSON.stringify(caseData.ordresPostOpDocs));
    formData.append('isNoneOrdersPostOpDocs', JSON.stringify(caseData.isNoneOrdersPostOpDocs));

    // Discharge Docs
    formData.append('dischargeDocs', JSON.stringify(caseData.dischargeDocs));
    formData.append('isNoneDischargeDocs', JSON.stringify(caseData.isNoneDischargeDocs));

    const requestOptions = { method: 'POST', headers: myHeaders, body: formData, redirect: 'follow' };
    fetch(`${BASE_URL}/cases/saveCaseData`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const data = JSON.parse(result);
        console.log(data);
        if (data.success) {
          history.push('/admin/cases');
        }
      })
      .catch(err => console.log(err));
  };
  // #endregion

  // #region FaceSheet Docs Event Handlers
  const handleOnFaceSheetDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.faceSheetDocs = docs;
    setCaseData(updatedCaseData);
  };

  const handleOnChangeIsNoneFaceSheet = (value) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.isNoneFaceSheet = value;
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteFaceSheetPage = (docId) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.faceSheetDocs = updatedCaseData.faceSheetDocs.filter(el => el.id !== docId);
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteFaceSheetDocument = () => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.faceSheetDocs = [];
    setCaseData(updatedCaseData);
  };
  // #endregion

  // #region Consent Surgery Docs Event Handlers
  const handleOnConsentSurgFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.consentSurgFormDocs = docs;
    setCaseData(updatedCaseData);
  };

  const handleOnChangeIsNoneConsentSurgForm = (value) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.isNoneConsentSurgFormDocs = value;
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteConsentSurgFormDocPage = (docId) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.consentSurgFormDocs = updatedCaseData.consentSurgFormDocs.filter(el => el.id !== docId);
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteConsentSurgFormDocDocument = () => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.consentSurgFormDocs = [];
    setCaseData(updatedCaseData);
  };

  // #endregion

  // #region Consent Anesthesia Docs Event Handlers
  const handleOnConsentFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.consentFormDocs = docs;
    setCaseData(updatedCaseData);
  };

  const handleOnChangeIsNoneConsentForm = (value) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.isNoneConsentFormDocs = value;
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteConsentFormDocPage = (docId) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.consentFormDocs = updatedCaseData.consentFormDocs.filter(el => el.id !== docId);
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteConsentFormDocDocument = () => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.consentFormDocs = [];
    setCaseData(updatedCaseData);
  };
  // #endregion

  // #region Surgeon Docs Event Handlers
  const handleOnSurgeonDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.surgeonDocs = docs;
    setCaseData(updatedCaseData);
  };

  const handleOnChangeIsNoneSurgeonDocs = (value) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.isNoneSurgeonDocs = value;
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteSurgeonDocsPage = (docId) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.surgeonDocs = updatedCaseData.surgeonDocs.filter(el => el.id !== docId);
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteSurgeonDocsDocument = () => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.surgeonDocs = [];
    setCaseData(updatedCaseData);
  };
  // #endregion

  // #region Nursing Docs Event Handlers
  const handleOnNursingDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.nursingDocs = docs;
    setCaseData(updatedCaseData);
  };

  const handleOnChangeIsNoneNursingDocs = (value) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.isNoneNursingDocs = value;
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteNursingDocsPage = (docId) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.nursingDocs = updatedCaseData.nursingDocs.filter(el => el.id !== docId);
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteNursingDocsDocument = () => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.nursingDocs = [];
    setCaseData(updatedCaseData);
  };
  // #endregion

  // #region Records Docs Event Handlers
  const handleOnLabsDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.labsDocs = docs;
    setCaseData(updatedCaseData);
  };

  const handleOnChangeIsNoneLabsDocs = (value) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.isNoneLabDocs = value;
    setCaseData(updatedCaseData);
  }

  const handleOnDeleteLabsDocsPage = (docId) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.labsDocs = updatedCaseData.labsDocs.filter(el => el.id !== docId);
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteLabsDocsDocument = () => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.labsDocs = [];
    setCaseData(updatedCaseData);
  };

  const handleOnEkgDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.ekgDocs = docs;
    setCaseData(updatedCaseData);
  };

  const handleOnChangeIsNoneEkgDocs = (value) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.isNoneEkgDocs = value;
    setCaseData(updatedCaseData);
  }

  const handleOnDeleteEkgDocsPage = (docId) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.ekgDocs = updatedCaseData.ekgDocs.filter(el => el.id !== docId);
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteEkgDocsDocument = () => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.ekgDocs = [];
    setCaseData(updatedCaseData);
  };

  const handleOnImagingDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.imagingDocs = docs;
    setCaseData(updatedCaseData);
  };

  const handleOnChangeIsNoneImagingDocs = (value) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.isNoneImagingDocs = value;
    setCaseData(updatedCaseData);
  }

  const handleOnDeleteImagingDocsPage = (docId) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.imagingDocs = updatedCaseData.imagingDocs.filter(el => el.id !== docId);
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteImagingDocsDocument = () => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.imagingDocs = [];
    setCaseData(updatedCaseData);
  };

  const handleOnConsultsDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.consultsDocs = docs;
    setCaseData(updatedCaseData);
  };

  const handleOnChangeIsNoneConsultsDocs = (value) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.isNoneConsults = value;
    setCaseData(updatedCaseData);
  }

  const handleOnDeleteConsultsDocsPage = (docId) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.consultsDocs = updatedCaseData.consultsDocs.filter(el => el.id !== docId);
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteConsultsDocsDocument = () => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.consultsDocs = [];
    setCaseData(updatedCaseData);
  };
  // #endregion

  // #region Orders Docs Event Handlers

  const handleOnOrdersPreOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.ordersPreOpDocs = docs;
    setCaseData(updatedCaseData);
  };

  const handleOnChangeIsNoneOrdersPreOpForm = (value) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.isNoneOrdersPreOpDocs = value;
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteOrdersPreOpFormmDocPage = (docId) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.ordersPreOpDocs = updatedCaseData.ordersPreOpDocs.filter(el => el.id !== docId);
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteOrdersPreOpFormmDocDocument = () => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.ordersPreOpDocs = [];
    setCaseData(updatedCaseData);
  };

  const handleOnOrdersIntraOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.ordersIntraOpDocs = docs;
    setCaseData(updatedCaseData);
  };

  const handleOnChangeIsNoneOrdersIntraOpForm = (value) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.isNoneOrdersIntraOpDocs = value;
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteOrdersIntraOpFormmDocPage = (docId) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.ordersIntraOpDocs = updatedCaseData.ordersIntraOpDocs.filter(el => el.id !== docId);
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteOrdersIntraOpFormmDocDocument = () => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.ordersIntraOpDocs = [];
    setCaseData(updatedCaseData);
  };

  const handleOnOrdersPostOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.ordresPostOpDocs = docs;
    setCaseData(updatedCaseData);
  };

  const handleOnChangeIsNoneOrdersPostOpForm = (value) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.isNoneOrdersPostOpDocs = value;
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteOrdersPostOpFormDocPage = (docId) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.ordresPostOpDocs = updatedCaseData.ordresPostOpDocs.filter(el => el.id !== docId);
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteOrdersPostOpFormDocDocument = () => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.ordresPostOpDocs = [];
    setCaseData(updatedCaseData);
  };
  // #endregion

  // #region Anesthesia Docs Event Handlers
  const handleOnPreOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.preOpFormDocs = docs;
    setCaseData(updatedCaseData);
  };

  const handleOnChangeIsNonePreOpForm = (value) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.isNonePreOpFormDocs = value;
    setCaseData(updatedCaseData);
  };

  const handleOnDeletePreOpFormmDocPage = (docId) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.preOpFormDocs = updatedCaseData.preOpFormDocs.filter(el => el.id !== docId);
    setCaseData(updatedCaseData);
  };

  const handleOnDeletePreOpFormmDocDocument = () => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.preOpFormDocs = [];
    setCaseData(updatedCaseData);
  };

  const handleOnIntraOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.intraOpFormDocs = docs;
    setCaseData(updatedCaseData);
  };

  const handleOnChangeIsNoneIntraOpForm = (value) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.isNoneIntraOpFormDocs = value;
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteIntraOpFormmDocPage = (docId) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.intraOpFormDocs = updatedCaseData.intraOpFormDocs.filter(el => el.id !== docId);
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteIntraOpFormmDocDocument = () => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.intraOpFormDocs = [];
    setCaseData(updatedCaseData);
  };

  const handleOnPostOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.postOpFormDocs = docs;
    setCaseData(updatedCaseData);
  };

  const handleOnChangeIsNonePostOpForm = (value) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.isNonePostOpFormDocs = value;
    setCaseData(updatedCaseData);
  };

  const handleOnDeletePostOpFormmDocPage = (docId) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.postOpFormDocs = updatedCaseData.postOpFormDocs.filter(el => el.id !== docId);
    setCaseData(updatedCaseData);
  };

  const handleOnDeletePostOpFormmDocDocument = () => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.postOpFormDocs = [];
    setCaseData(updatedCaseData);
  };
  // #endregion

  // #region Discharage Docs Event Handelers
  const handleOnDischargeDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.dischargeDocs = docs;
    setCaseData(updatedCaseData);
  };

  const handleOnChangeIsNoneDischarge = (value) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.isNoneDischargeDocs = value;
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteDischargeDocPage = (docId) => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.dischargeDocs = updatedCaseData.dischargeDocs.filter(el => el.id !== docId);
    setCaseData(updatedCaseData);
  };

  const handleOnDeleteDischargeDocDocument = () => {
    handleOnCaseDataChange();
    const updatedCaseData = clone(caseData);
    updatedCaseData.dischargeDocs = [];
    setCaseData(updatedCaseData);
  };
  // #endregion

  // #region ** Delete Insurnace ** Event Handlers
  const deleteInsurance = (type = 'primary') => {
    const formData = new FormData();
    formData.append('patientId', patientData._id);

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    let url = '';
    switch (type) {
      case 'primary':
        url = `${BASE_URL}/patients/deletePrimaryInsurance`;
        break;
      case 'secondary':
        url = `${BASE_URL}/patients/deleteSecondaryInsurance`;
        break;
      case 'tertiary':
        url = `${BASE_URL}/patients/deleteTertiaryInsurance`;
        break;
      default:
        url = '';
    }
    fetch(url, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        if (res.success) {
          getCaseData();
        }
      })
      .catch(error => console.log('error', error));
  };
  // #endregion

  // #region Collapse Code
  let dos = '';
  const tmpDos = new Date(caseData.dateOfSurgery);
  dos = `${tmpDos.getMonth() + 1}/${tmpDos.getDate()}/${tmpDos.getFullYear()}`;

  let patientType = '';
  if (caseData.patientType) {
    patientType = caseData.patientType === 'OutPatient' ? 'OutPt' : 'InPt';
  }

  let facilityName = '';
  if (caseData.facility && caseData.facility.facilityName) {
    facilityName = caseData.facility.facilityName;
  }
  if (caseData.otherFacility && caseData.otherFacility.facilityName) {
    facilityName = caseData.otherFacility.facilityName;
  }

  let surgeons = '';
  if (caseData.surgeons) {
    caseData.surgeons.forEach(el => {
      surgeons += el.fullName + ' | ';
    });
  }

  if (caseData.otherSurgeons && caseData.otherSurgeons.length > 0) {
    caseData.otherSurgeons.forEach((el) => {
      surgeons += el.text + ' | ';
    });
  }

  let admDx = '';
  if (caseData && Object.keys(caseData).length && caseData.preOpDx.length > 0) {
    caseData.preOpDx.forEach(el => {
      admDx += el.description + ' | ';
    });
  }

  if (caseData.otherPreOpDx && caseData.otherPreOpDx.length && caseData.otherPreOpDx.length > 0) {
    caseData.otherPreOpDx.forEach(el => {
      admDx += el.description + ' | ';
    });
  }

  let surgicalProcedures = '';
  if (caseData.surgicalProcedures && caseData.surgicalProcedures.length && caseData.surgicalProcedures.length > 0) {
    caseData.surgicalProcedures.forEach(el => {
      surgicalProcedures += el.description + ' | ';
    });
  }

  if (caseData.otherSurgicalProcedures && caseData.otherSurgicalProcedures.length && caseData.otherSurgicalProcedures.length > 0) {
    caseData.otherSurgicalProcedures.forEach(el => {
      surgicalProcedures += el.description + ' | ';
    });
  }

  // #region Tab Header Text & Active Tabs
  let tabHeaderText = '';
  let tabs = null;
  let rcmTabs = null;

  // console.log(`pathname = ${pathname}`);
  if (pathname.endsWith('/personelle')) {
    tabHeaderText = 'Personelle';
  } else if (pathname.endsWith('/surgeon')) {
    tabHeaderText = 'Surgeon';
  } else if (pathname.endsWith('orders')) {
    tabHeaderText = 'Orders';
    tabs = (
      <TabsContainer
        dropDownId="dddd2"
        showPlusIcon={false}
        isDrawer={false}
        shiftMethod='minWidth'
        minWidth={610}
        containerStyles={{ width: '100%' }}
      >
        {/* PreOp */}
        <TabItem
          key='surgeon_preop_1'
          tabKey="2"
          parentKey="0666"
          text="PreOp"
          isActive={ordersActiveSubTab === 'preOp'}
          showTabIcon
          showSvgIcon
          svgIcon={(() => {
            if (Object.keys(caseData).length > 0) {
              if (caseData.isNoneOrdersPreOpDocs) {
                return IS_NONE_ICON;
              }
              if (caseData.ordersPreOpDocs.length > 0) {
                return SUCCESS_ICON;
              } else {
                return CRITIAL_ICON;
              }
            } else {
              return CRITIAL_ICON;
            }
          })()}
          style={{
            width: '78px',
            minWidth: '78px',
            maxWidth: '78px',
            background: '#003060',
            height: '32px',
            color: anesthesiaActiveTab === 'preop' ? '#00a0ff' : '',
            borderBottom: anesthesiaActiveTab === 'preop' ? '1px solid #00a0ff' : '1px solid #003060'
            // borderBottom: 'none'
          }}
          textStyles={tabsTextStyles}
          onActivate={() => {
            setOrdersActiveSubTab('preOp');
          }}
        >
          PreOp
        </TabItem>
        {/* Intra OP */}
        <TabItem
          key='anestehsia_IntraOP_1'
          tabKey="1"
          parentKey="0555"
          text="Consent"
          isActive={ordersActiveSubTab === 'intraOp'}
          showTabIcon
          showSvgIcon
          svgIcon={(() => {
            if (Object.keys(caseData).length > 0) {
              if (caseData.isNoneOrdersIntraOpDocs) {
                return IS_NONE_ICON;
              }
              if (caseData.ordersIntraOpDocs.length > 0) {
                return SUCCESS_ICON;
              } else {
                return CRITIAL_ICON;
              }
            } else {
              return CRITIAL_ICON;
            }
          })()}
          style={{
            width: '85px',
            minWidth: '85px',
            maxWidth: '85px',
            background: '#003060',
            height: '32px',
            color: anesthesiaActiveTab === 'consent' ? '#00a0ff' : '',
            borderBottom: anesthesiaActiveTab === 'intraOp' ? '1px solid #00a0ff' : '1px solid #003060'
            // borderBottom: 'none'
          }}
          textStyles={tabsTextStyles}
          onActivate={() => {
            setOrdersActiveSubTab('intraOp');
          }}
        >
          IntraOp
        </TabItem>
        {/* PostOp */}
        <TabItem
          key='surgeon_postop_1'
          tabKey="3"
          parentKey="0777"
          text="PostOp"
          isActive={ordersActiveSubTab === 'postOp'}
          showTabIcon
          showSvgIcon
          svgIcon={(() => {
            if (Object.keys(caseData).length > 0) {
              if (caseData.isNoneOrdersPostOpDocs) {
                return IS_NONE_ICON;
              }
              if (caseData.ordresPostOpDocs.length > 0) {
                return SUCCESS_ICON;
              } else {
                return CRITIAL_ICON;
              }
            } else {
              return CRITIAL_ICON;
            }
          })()}
          style={{
            width: '85px',
            minWidth: '85px',
            maxWidth: '85px',
            background: '#003060',
            height: '32px',
            color: anesthesiaActiveTab === 'postop' ? '#00a0ff' : '',
            borderBottom: anesthesiaActiveTab === 'postop' ? '1px solid #00a0ff' : '1px solid #003060'
            // borderBottom: 'none'
          }}
          textStyles={tabsTextStyles}
          onActivate={() => {
            setOrdersActiveSubTab('postOp');
          }}
        >
          PostOp
        </TabItem>
      </TabsContainer>
    );
  } else if (pathname.endsWith('records')) {
    tabHeaderText = 'Records';
    tabs = (
      <TabsContainer
        dropDownId="dddd2"
        showPlusIcon={false}
        isDrawer={false}
        shiftMethod='minWidth'
        minWidth={610}
        containerStyles={{ width: '100%' }}
      >
        {/* Labs */}
        <TabItem
          key='records_lab_1'
          tabKey="8989"
          parentKey="11111111111"
          text="Labs"
          isActive={recordsActiveSubTab === 'labs'}
          showTabIcon
          showSvgIcon
          svgIcon={(() => {
            if (Object.keys(caseData).length > 0) {
              if (caseData.isNoneLabDocs) {
                return IS_NONE_ICON;
              }
              if (caseData.labsDocs.length > 0) {
                return SUCCESS_ICON;
              } else {
                return CRITIAL_ICON;
              }
            } else {
              return CRITIAL_ICON;
            }
          })()}
          style={{
            width: '78px',
            minWidth: '78px',
            maxWidth: '78px',
            background: '#003060',
            height: '32px',
            color: recordsActiveSubTab === 'labs' ? '#00a0ff' : '',
            borderBottom: recordsActiveSubTab === 'labs' ? '1px solid #00a0ff' : '1px solid #003060'
          }}
          textStyles={tabsTextStyles}
          onActivate={() => {
            setRecordsActiveSubTab('labs');
          }}
        >
          Labs
        </TabItem>
        {/* EKG */}
        <TabItem
          key='records_ekg_1'
          tabKey="8990"
          parentKey="11111111112"
          text="EKG"
          isActive={recordsActiveSubTab === 'ekg'}
          showTabIcon
          showSvgIcon
          svgIcon={(() => {
            if (Object.keys(caseData).length > 0) {
              if (caseData.isNoneEkgDocs) {
                return IS_NONE_ICON;
              }
              if (caseData.ekgDocs.length > 0) {
                return SUCCESS_ICON;
              } else {
                return CRITIAL_ICON;
              }
            } else {
              return CRITIAL_ICON;
            }
          })()}
          style={{
            width: '78px',
            minWidth: '78px',
            maxWidth: '78px',
            background: '#003060',
            height: '32px',
            color: recordsActiveSubTab === 'ekg' ? '#00a0ff' : '',
            borderBottom: recordsActiveSubTab === 'ekg' ? '1px solid #00a0ff' : '1px solid #003060'
          }}
          textStyles={tabsTextStyles}
          onActivate={() => {
            setRecordsActiveSubTab('ekg');
          }}
        >
          EKG
        </TabItem>
        {/* Imaging */}
        <TabItem
          key='records_imaging_1'
          tabKey="8991"
          parentKey="11111111113"
          text="Imaging"
          isActive={recordsActiveSubTab === 'imaging'}
          showTabIcon
          showSvgIcon
          svgIcon={(() => {
            if (Object.keys(caseData).length > 0) {
              if (caseData.isNoneImagingDocs) {
                return IS_NONE_ICON;
              }
              if (caseData.imagingDocs.length > 0) {
                return SUCCESS_ICON;
              } else {
                return CRITIAL_ICON;
              }
            } else {
              return CRITIAL_ICON;
            }
          })()}
          style={{
            width: '90px',
            minWidth: '90px',
            maxWidth: '90px',
            background: '#003060',
            height: '32px',
            color: recordsActiveSubTab === 'imaging' ? '#00a0ff' : '',
            borderBottom: recordsActiveSubTab === 'imaging' ? '1px solid #00a0ff' : '1px solid #003060'
          }}
          textStyles={tabsTextStyles}
          onActivate={() => {
            setRecordsActiveSubTab('imaging');
          }}
        >
          Imaging
        </TabItem>
        {/* Consults */}
        <TabItem
          key='records_consults_1'
          tabKey="8992"
          parentKey="11111111114"
          text="Consults"
          isActive={recordsActiveSubTab === 'consults'}
          showTabIcon
          showSvgIcon
          svgIcon={(() => {
            if (Object.keys(caseData).length > 0) {
              if (caseData.isNoneConsults) {
                return IS_NONE_ICON;
              }
              if (caseData.consultsDocs.length > 0) {
                return SUCCESS_ICON;
              } else {
                return CRITIAL_ICON;
              }
            } else {
              return CRITIAL_ICON;
            }
          })()}
          style={{
            width: '90px',
            minWidth: '90px',
            maxWidth: '90px',
            background: '#003060',
            height: '32px',
            color: recordsActiveSubTab === 'consults' ? '#00a0ff' : '',
            borderBottom: recordsActiveSubTab === 'consults' ? '1px solid #00a0ff' : '1px solid #003060'
          }}
          textStyles={tabsTextStyles}
          onActivate={() => {
            setRecordsActiveSubTab('consults');
          }}
        >
          Consults
        </TabItem>
      </TabsContainer>
    );
  } else if (pathname.endsWith('/nursing')) {
    tabHeaderText = 'Nursing';
  } else if (pathname.endsWith('/anesthesia')) {
    tabHeaderText = 'Anesthesia';
    tabs = (
      <TabsContainer
        dropDownId="d1"
        showPlusIcon={false}
        isDrawer={false}
        shiftMethod='minWidth'
        minWidth={610}
        containerStyles={{ width: '100%' }}
      >
        {/* PreOp */}
        <TabItem
          key='surgeon_preop_1'
          tabKey="2"
          parentKey="0666"
          text="PreOp"
          isActive={anesthesiaActiveTab === 'preOp'}
          showTabIcon
          showSvgIcon
          svgIcon={(() => {
            if (Object.keys(caseData).length > 0) {
              if (caseData.isNonePreOpFormDocs) {
                return IS_NONE_ICON;
              }
              if (caseData.preOpFormDocs.length > 0) {
                return SUCCESS_ICON;
              } else {
                return CRITIAL_ICON;
              }
            } else {
              return CRITIAL_ICON;
            }
          })()}
          style={{
            width: '78px',
            minWidth: '78px',
            maxWidth: '78px',
            background: '#003060',
            height: '32px',
            color: anesthesiaActiveTab === 'preop' ? '#00a0ff' : '',
            borderBottom: anesthesiaActiveTab === 'preop' ? '1px solid #00a0ff' : '1px solid #003060'
            // borderBottom: 'none'
          }}
          textStyles={tabsTextStyles}
          onActivate={() => {
            setAnesthesiaActiveTab('preOp');
          }}
        >
          PreOp
        </TabItem>
        {/* Intra OP */}
        <TabItem
          key='anestehsia_IntraOP_1'
          tabKey="1"
          parentKey="0555"
          text="Consent"
          isActive={anesthesiaActiveTab === 'intraOp'}
          showTabIcon
          showSvgIcon
          svgIcon={(() => {
            if (Object.keys(caseData).length > 0) {
              let anesthesiaDataFlagValue = FLAG_VALUE.critical;
              if (caseData.asaScore.length > 0 && caseData.anesthesiaTypes.length > 0 && caseData.anesthesiaModifiers.length > 0) {
                anesthesiaDataFlagValue = FLAG_VALUE.success;
              }
              if (anesthesiaDataFlagValue === FLAG_VALUE.critical) {
                return CRITIAL_ICON;
              } else {
                if (caseData.isNoneIntraOpFormDocs) {
                  // return IS_NONE_ICON;
                  return SUCCESS_ICON;
                }
                if (caseData.intraOpFormDocs.length > 0) {
                  return SUCCESS_ICON;
                } else {
                  return CRITIAL_ICON;
                }
              }
            } else {
              return CRITIAL_ICON;
            }
          })()}
          style={{
            width: '85px',
            minWidth: '85px',
            maxWidth: '85px',
            background: '#003060',
            height: '32px',
            color: anesthesiaActiveTab === 'consent' ? '#00a0ff' : '',
            borderBottom: anesthesiaActiveTab === 'intraOp' ? '1px solid #00a0ff' : '1px solid #003060'
            // borderBottom: 'none'
          }}
          textStyles={tabsTextStyles}
          onActivate={() => {
            setAnesthesiaActiveTab('intraOp');
          }}
        >
          IntraOp
        </TabItem>
        {/* PostOp */}
        <TabItem
          key='surgeon_postop_1'
          tabKey="3"
          parentKey="0777"
          text="PostOp"
          isActive={anesthesiaActiveTab === 'postOp'}
          showTabIcon
          showSvgIcon
          svgIcon={(() => {
            if (Object.keys(caseData).length > 0) {
              if (caseData.isNonePostOpFormDocs) {
                return IS_NONE_ICON;
              }
              if (caseData.postOpFormDocs.length > 0) {
                return SUCCESS_ICON;
              } else {
                return CRITIAL_ICON;
              }
            } else {
              return CRITIAL_ICON;
            }
          })()}
          style={{
            width: '85px',
            minWidth: '85px',
            maxWidth: '85px',
            background: '#003060',
            height: '32px',
            color: anesthesiaActiveTab === 'postop' ? '#00a0ff' : '',
            borderBottom: anesthesiaActiveTab === 'postop' ? '1px solid #00a0ff' : '1px solid #003060'
            // borderBottom: 'none'
          }}
          textStyles={tabsTextStyles}
          onActivate={() => {
            setAnesthesiaActiveTab('postOp');
          }}
        >
          PostOp
        </TabItem>
        {/* Procedures */}
        <TabItem
          key='surgeon_procedures_1'
          tabKey="4"
          parentKey="0888"
          text="Procedures"
          isActive={anesthesiaActiveTab === 'procedures'}
          showSvgIcon
          showTabIcon
          svgIcon={(() => {
            if (Object.keys(caseData).length > 0) {
              if (caseData.isNoneAnesthesiaProcedure) {
                return IS_NONE_ICON;
              }
              if (caseData.anesthesiaProcedures.length === 0) {
                return CRITIAL_ICON;
              }
              if (caseData.anesthesiaProcedures.length > 0 && (caseData.anesthesiaProcedures.filter(el => el.npi === 0)).length > 0) {
                return BELL_ICON;
              } else {
                return SUCCESS_ICON;
              }
            } else {
              return CRITIAL_ICON;
            }
          })()}
          style={{
            width: '105px',
            minWidth: '105px',
            maxWidth: '105px',
            background: '#003060',
            height: '32px',
            color: anesthesiaActiveTab === 'procedures' ? '#00a0ff' : '',
            borderBottom: anesthesiaActiveTab === 'procedures' ? '1px solid #00a0ff' : '1px solid #003060'
            // borderBottom: 'none',
          }}
          textStyles={tabsTextStyles}
          onActivate={() => {
            setAnesthesiaActiveTab('procedures');
          }}
        >
          Procedures
        </TabItem>
      </TabsContainer>
    );
  } else if (pathname.endsWith('/discharge')) {
    tabHeaderText = 'Discharge';
  } else if (pathname.endsWith('/followUp')) {
    tabHeaderText = 'Follow Up';
  } else if (pathname.endsWith('/consents')) {
    tabHeaderText = 'Consents';
    tabs = (
      <TabsContainer
        dropDownId="d1"
        showPlusIcon={false}
        isDrawer={false}
        shiftMethod='minWidth'
        minWidth={610}
        containerStyles={{ width: '100%', background: '#003060' }}
      >
        {/* Surgery */}
        <TabItem
          key='consents_surgery_1'
          tabKey="1"
          parentKey="0555"
          text="Surgery"
          isActive={consentActiveTab === 'surgery'}
          showTabIcon
          showSvgIcon
          svgIcon={(() => {
            if (Object.keys(caseData).length > 0) {
              if (caseData.isNoneConsentSurgFormDocs) {
                return IS_NONE_ICON;
              }
              if (caseData.consentSurgFormDocs.length > 0) {
                return SUCCESS_ICON;
              } else {
                return CRITIAL_ICON;
              }
            } else {
              return CRITIAL_ICON;
            }
          })()}
          style={{
            width: '85px',
            minWidth: '85px',
            maxWidth: '85px',
            height: '32px',
            background: '#003060',
            color: consentActiveTab === 'surgery' ? '#00a0ff' : '',
            borderBottom: 'none'
            // borderBottom: consentActiveTab === 'surgery' ? '1px solid #00a0ff' : '1px solid #003060'
          }}
          textStyles={tabsTextStyles}
          onActivate={() => {
            setConsentActiveTab('surgery');
          }}
        >
          {/* <Link to={`${url}/info`}>Primary Insurance</Link> */}
          Surgery
        </TabItem>
        {/* Anesthesia */}
        <TabItem
          key='consents_anesthesia_1'
          tabKey="2"
          parentKey="0666"
          text="Tab 2"
          isActive={consentActiveTab === 'anesthesia'}
          showSvgIcon
          showTabIcon
          svgIcon={(() => {
            if (Object.keys(caseData).length > 0) {
              if (caseData.isNoneConsentFormDocs) {
                return IS_NONE_ICON;
              }
              if (caseData.consentFormDocs.length > 0) {
                return SUCCESS_ICON;
              } else {
                return CRITIAL_ICON;
              }
            } else {
              return CRITIAL_ICON;
            }
          })()}
          style={{
            width: '100px',
            minWidth: '100px',
            maxWidth: '100px',
            height: '32px',
            background: '#003060',
            color: consentActiveTab === 'anesthesia' ? '#00a0ff' : '',
            borderBottom: 'none'
            // borderBottom: consentActiveTab === 'anesthesia' ? '1px solid #00a0ff' : '1px solid #003060'
          }}
          textStyles={tabsTextStyles}
          onActivate={() => {
            setConsentActiveTab('anesthesia');
          }}
        >
          {/* <Link to={`${url}/info`}>Primary Insurance</Link> */}
          Anesthesia
        </TabItem>
      </TabsContainer>
    );
  } else {
    tabHeaderText = 'FaceSheet';
  }

  switch (activeRcmTab) {
    case 'patient-case-info':
      rcmTabs = (
        <TabsContainer
          dropDownId="d3"
          showPlusIcon={false}
          isDrawer={false}
          shiftMethod='minWidth'
          minWidth={610}
          containerStyles={{ width: '100%' }}
        >
          {/* Case Info */}
          <TabItem
            key='cp_case_info_1'
            tabKey="2"
            parentKey="2001"
            text="CMS1500"
            isActive={activePatientCaseInfoTab === 'case-info'}
            showTabIcon
            showSvgIcon
            svgIcon={(() => {
              if (caseTabIcon === FLAG_VALUE.critical) {
                return CRITIAL_ICON;
              } else if (caseTabIcon === FLAG_VALUE.warning) {
                return BELL_ICON;
              } else {
                return SUCCESS_ICON;
              }
            })()}
            style={{
              width: '100px',
              minWidth: '100px',
              maxWidth: '100px',
              height: '28px',
              background: '#003060',
              borderBottom: 'none'
              // borderBottom: activePatientCaseInfoTab === 'cms1500' ? '1px solid #00A0FF' : 'none'
            }}
            textStyles={tabsTextStyles}
            onActivate={() => {
              setActivePatientCaseInfoTab('case-info');
            }}
          >
            Case Info
          </TabItem>
          {/* Patient Info */}
          <TabItem
            key='cp_patinet-info_1'
            tabKey="2"
            parentKey="2000"
            text="Patient Info"
            isActive={activePatientCaseInfoTab === 'patient-info'}
            showTabIcon
            showSvgIcon
            svgIcon={patientFlag === FLAG_VALUE.success ? SUCCESS_ICON : BELL_ICON}
            style={{
              width: '110px',
              minWidth: '110px',
              maxWidth: '110px',
              height: '28px',
              background: '#003060',
              borderBottom: 'none'
              // borderBottom: activePatientCaseInfoTab === 'patient-info' ? '1px solid #00A0FF' : 'none'
            }}
            textStyles={tabsTextStyles}
            onActivate={() => {
              setActivePatientCaseInfoTab('patient-info');
            }}
          >
            Patient Info
          </TabItem>
          {/* Payers */}
          <TabItem
            key='cp_payers_1'
            tabKey="3"
            parentKey="2003"
            text="Payers"
            isActive={activePatientCaseInfoTab === 'payers'}
            showTabIcon
            showSvgIcon
            svgIcon={payerFlag === FLAG_VALUE.success ? SUCCESS_ICON : BELL_ICON}
            style={{
              width: '100px',
              minWidth: '100px',
              maxWidth: '100px',
              height: '28px',
              background: '#003060',
              borderBottom: 'none'
              // borderBottom: activePatientCaseInfoTab === 'patient-info' ? '1px solid #00A0FF' : 'none'
            }}
            textStyles={tabsTextStyles}
            onActivate={() => {
              setActivePatientCaseInfoTab('payers');
            }}
          >
            Payers
          </TabItem>
        </TabsContainer>
      );
      break;
    case 'facility-rcm':
      rcmTabs = (
        <TabsContainer
          dropDownId="d3"
          showPlusIcon={false}
          isDrawer={false}
          shiftMethod='minWidth'
          minWidth={610}
          containerStyles={{ width: '100%' }}
        >
          {/* Supepr Bill */}
          <TabItem
            key='superbill_surgeon_1'
            tabKey="1"
            parentKey="3000"
            text="Superbill"
            isActive={activeFacilityRCMTab === 'superbill'}
            showTabIcon={false}
            showSvgIcon={false}
            style={{
              width: '60px',
              minWidth: '60px',
              maxWidth: '60px',
              height: '28px',
              background: '#003060',
              borderBottom: 'none'
              // borderBottom: activeFacilityRCMTab === 'superbill' ? '1px solid #00A0FF' : 'none'
            }}
            textStyles={tabsTextStyles}
            onActivate={() => {
              setActiveFacilityRCMTab('superbill');
            }}
          >
            Superbill
          </TabItem>
          {/* CMS 1500 */}
          <TabItem
            key='cms_surgeon_2'
            tabKey="2"
            parentKey="3001"
            text="CMS1500"
            isActive={activeFacilityRCMTab === 'cms1500'}
            showTabIcon={false}
            showSvgIcon={false}
            style={{
              width: '76px',
              minWidth: '76px',
              maxWidth: '76px',
              height: '28px',
              background: '#003060',
              borderBottom: 'none'
              // borderBottom: activeFacilityRCMTab === 'cms1500' ? '1px solid #00A0FF' : 'none'
            }}
            textStyles={tabsTextStyles}
            onActivate={() => {
              setActiveFacilityRCMTab('cms1500');
            }}
          >
            CMS1500
          </TabItem>
          {/* Payments */}
          <TabItem
            key='payments_surgeon_3'
            tabKey="3"
            parentKey="3002"
            text="Payments"
            isActive={activeFacilityRCMTab === 'payments'}
            showTabIcon={false}
            showSvgIcon={false}
            style={{
              width: '76px',
              minWidth: '76px',
              maxWidth: '76px',
              height: '28px',
              background: '#003060',
              borderBottom: 'none'
              // borderBottom: activeFacilityRCMTab === 'payments' ? '1px solid #00A0FF' : 'none'
            }}
            textStyles={tabsTextStyles}
            onActivate={() => {
              setActiveFacilityRCMTab('payments');
            }}
          >
            Payments
          </TabItem>
        </TabsContainer>
      );
      break;
    case 'nursing-rcm':
      rcmTabs = (
        <TabsContainer
          dropDownId="d3"
          showPlusIcon={false}
          isDrawer={false}
          shiftMethod='minWidth'
          minWidth={610}
          containerStyles={{ width: '100%' }}
        >
          {/* Supepr Bill */}
          <TabItem
            key='superbill_nursing_1'
            tabKey="1"
            parentKey="4000"
            text="Superbill"
            isActive={activeNursingRCMTab === 'superbill'}
            showTabIcon={false}
            showSvgIcon={false}
            style={{
              width: '60px',
              minWidth: '60px',
              maxWidth: '60px',
              height: '28px',
              background: '#003060',
              borderBottom: 'none'
              // borderBottom: activeNursingRCMTab === 'superbill' ? '1px solid #00A0FF' : 'none'
            }}
            textStyles={tabsTextStyles}
            onActivate={() => {
              setActiveNursingRCMTab('superbill');
            }}
          >
            Superbill
          </TabItem>
          {/* CMS 1500 */}
          <TabItem
            key='cms_nursing_2'
            tabKey="2"
            parentKey="4001"
            text="CMS1500"
            isActive={activeNursingRCMTab === 'cms1500'}
            showTabIcon={false}
            showSvgIcon={false}
            style={{
              width: '76px',
              minWidth: '76px',
              maxWidth: '76px',
              height: '28px',
              background: '#003060',
              borderBottom: activeNursingRCMTab === 'cms1500' ? '1px solid #00A0FF' : 'none'
            }}
            textStyles={tabsTextStyles}
            onActivate={() => {
              setActiveNursingRCMTab('cms1500');
            }}
          >
            CMS1500
          </TabItem>
          {/* Payments */}
          <TabItem
            key='payments_nursing_3'
            tabKey="3"
            parentKey="4002"
            text="Payments"
            isActive={activeNursingRCMTab === 'payments'}
            showTabIcon={false}
            showSvgIcon={false}
            style={{
              width: '76px',
              minWidth: '76px',
              maxWidth: '76px',
              height: '28px',
              background: '#003060',
              borderBottom: 'none'
              // borderBottom: activeNursingRCMTab === 'payments' ? '1px solid #00A0FF' : 'none'
            }}
            textStyles={tabsTextStyles}
            onActivate={() => {
              setActiveNursingRCMTab('payments');
            }}
          >
            Payments
          </TabItem>
        </TabsContainer>
      );
      break;
    case 'surgeon-rcm':
      rcmTabs = (
        <TabsContainer
          dropDownId="d3"
          showPlusIcon={false}
          isDrawer={false}
          shiftMethod='minWidth'
          minWidth={610}
          containerStyles={{ width: '100%' }}
        >
          {/* Supepr Bill */}
          <TabItem
            key='superbill_anesthesiologist_1'
            tabKey="1"
            parentKey="5000"
            text="Superbill"
            isActive={activeSurgeonRCMTab === 'superbill'}
            showTabIcon={false}
            showSvgIcon={false}
            style={{
              width: '60px',
              minWidth: '60px',
              maxWidth: '60px',
              height: '28px',
              background: '#003060',
              borderBottom: 'none'
              // borderBottom: activeSurgeonRCMTab === 'superbill' ? '1px solid #00A0FF' : 'none'
            }}
            textStyles={tabsTextStyles}
            onActivate={() => {
              setActiveSurgeonRCMTab('superbill');
            }}
          >
            Superbill
          </TabItem>
          {/* CMS 1500 */}
          <TabItem
            key='cms_anesthesiologist_2'
            tabKey="2"
            parentKey="5001"
            text="CMS1500"
            isActive={activeSurgeonRCMTab === 'cms1500'}
            showTabIcon={false}
            showSvgIcon={false}
            style={{
              width: '76px',
              minWidth: '76px',
              maxWidth: '76px',
              height: '28px',
              background: '#003060',
              borderBottom: 'none'
              // borderBottom: activeSurgeonRCMTab === 'cms1500' ? '1px solid #00A0FF' : 'none'
            }}
            textStyles={tabsTextStyles}
            onActivate={() => {
              setActiveSurgeonRCMTab('cms1500');
            }}
          >
            CMS1500
          </TabItem>
          {/* Payments */}
          <TabItem
            key='payments_anesthesiologist_3'
            tabKey="3"
            parentKey="5002"
            text="Payments"
            isActive={activeSurgeonRCMTab === 'payments'}
            showTabIcon={false}
            showSvgIcon={false}
            style={{
              width: '76px',
              minWidth: '76px',
              maxWidth: '76px',
              height: '28px',
              background: '#003060',
              borderBottom: 'none'
              // borderBottom: activeSurgeonRCMTab === 'payments' ? '1px solid #00A0FF' : 'none'
            }}
            textStyles={tabsTextStyles}
            onActivate={() => {
              setActiveSurgeonRCMTab('payments');
            }}
          >
            Payments
          </TabItem>
        </TabsContainer>
      );
      break;
    case 'anesthesia-rcm':
      rcmTabs = (
        <TabsContainer
          dropDownId="d3"
          showPlusIcon={false}
          isDrawer={false}
          shiftMethod='minWidth'
          minWidth={610}
          containerStyles={{ width: '100%' }}
        >
          {/* Supepr Bill */}
          <TabItem
            key='superbill_crna_1'
            tabKey="1"
            parentKey="6000"
            text="Superbill"
            isActive={activeAnesthesiaRCMTab === 'superbill'}
            showTabIcon={false}
            showSvgIcon={false}
            style={{
              width: '60px',
              minWidth: '60px',
              maxWidth: '60px',
              height: '28px',
              background: '#003060',
              borderBottom: 'none'
              // borderBottom: activeAnesthesiaRCMTab === 'superbill' ? '1px solid #00A0FF' : 'none'
            }}
            textStyles={tabsTextStyles}
            onActivate={() => {
              setActiveAnesthesiaRCMTab('superbill');
            }}
          >
            Superbill
          </TabItem>
          {/* CMS 1500 */}
          <TabItem
            key='cms_crna_2'
            tabKey="2"
            parentKey="6001"
            text="CMS1500"
            isActive={activeAnesthesiaRCMTab === 'cms1500'}
            showTabIcon={false}
            showSvgIcon={false}
            style={{
              width: '76px',
              minWidth: '76px',
              maxWidth: '76px',
              height: '28px',
              background: '#003060',
              borderBottom: 'none'
              // borderBottom: activeAnesthesiaRCMTab === 'cms1500' ? '1px solid #00A0FF' : 'none'
            }}
            textStyles={tabsTextStyles}
            onActivate={() => {
              setActiveAnesthesiaRCMTab('cms1500');
            }}
          >
            CMS1500
          </TabItem>
          {/* Payments */}
          <TabItem
            key='payments_crna_3'
            tabKey="3"
            parentKey="6002"
            text="Payments"
            isActive={activeAnesthesiaRCMTab === 'payments'}
            showTabIcon={false}
            showSvgIcon={false}
            style={{
              width: '76px',
              minWidth: '76px',
              maxWidth: '76px',
              height: '28px',
              background: '#003060',
              borderBottom: 'none'
              // borderBottom: activeAnesthesiaRCMTab === 'payments' ? '1px solid #00A0FF' : 'none'
            }}
            textStyles={tabsTextStyles}
            onActivate={() => {
              setActiveAnesthesiaRCMTab('payments');
            }}
          >
            Payments
          </TabItem>
        </TabsContainer>
      );
      break;
    default:
      rcmTabs = null;
  }
  // #endregion

  let headerText = '';
  if (caseData && caseData.facility && Object.keys(caseData.facility).length > 0) {
    headerText = caseData.facility.facilityName;
  }

  if (caseData && caseData.otherFacility && Object.keys(caseData.otherFacility).length > 0) {
    headerText = caseData.otherFacility.facilityName;
  }
  // #endregion

  /* ________________________ Case Tab Events ________________________ */
  // #region --- Case Tab Event Handlers
  const handleOnUpdatePatientCard = (key, value) => {
    if (key === 'dateOfSurgery') {
      const tmp = value.split('/');
      setPatientDos(`${+tmp[2]}-${padZero(+tmp[0] + 1)}-${padZero(+tmp[1])}T00:00:00.000Z`);
    }
    if (key === 'admType') {
      setPatientAdmType(value);
      // setDcHour(24);
      // setDcMinute(60);
      // setShowDischargeFlag(true);
      // setIsDcPristine(true);
    }

    if (key === 'facility') {
      setPatientFacility(value);
    }

    if (key === 'surgeons') {
      setPatientSurgeons(value);
    }

    if (key === 'anesthesia') {
      setPatientAnesthesia(value);
    }

    if (key === 'preOpDx') {
      setPatientPreOpDx(value);
    }

    if (key === 'surgicalProcedures') {
      setPatientSurgicalProcedures(value);
    }
  };

  const closeScheduleCaseDrawer = () => {
    console.log('Close Case Tab Event');
  };

  const handleOnSaveSchedule = (data, closeDrawer = false) => {
    console.log('Saving case data...');
  };

  const handleOnUpdateCaseData = (data, saveCase, closeDrawer = false) => {
    console.log('Handle on update case data');
  };

  const handleOnChangeCaseFlag = (value) => {
    // setCaseFlag(value);
    setCaseTabIcon(value);
    // switch (value) {
    //   case FLAG_VALUE.critical:
    //     setCaseTabIcon(CRITIAL_ICON);
    //     break;
    //   case FLAG_VALUE.warning:
    //     setCaseTabIcon(BELL_ICON);
    //     break;
    //   case FLAG_VALUE.success:
    //     setCaseTabIcon(SUCCESS_ICON);
    //     break;
    //   default:
    //     setCaseTabIcon(CRITIAL_ICON);
    // }
  };

  // #endregion

  // ------------------------ RCM Section ------------------------
  const rcmSection = (
    <div
      className='noselect rcm-section'
      style={{
        // flexGrow: '1',
        // overflow: 'auto',
        width: '100%',
        background: '#003060'
      }}
    >
      {/* Patient Profile Drawer - closing warning */}
      {showPatientDrawerCloseWarning &&
        <WarningDialog
          show={showPatientDrawerCloseWarning}
          title='Close Patient Profile Drawer'
          message='Do you want to close without saving?'
          onCancel={() => setShowPatientDrawerCloseWarning(false)}
          onContinue={() => {
            setShowPatientDrawerCloseWarning(false);
            setShowPatientProfileDrawer(false);
          }}
        />
      }

      {/* Primary Insurance delete warning */}
      {showPrimaryPayerDeleteWarning &&
        <WarningDialog
          show={showPrimaryPayerDeleteWarning}
          title='Delete Primary Insurance'
          message='Do you want to delete patient primary insurance data?'
          onCancel={() => setShowPrimaryPayerDeleteWarning(false)}
          onContinue={() => {
            setShowPrimaryPayerDeleteWarning(false);
            deleteInsurance('primary');
          }}
        />
      }

      {/* Secondary Insurance delete warning */}
      {showSecondaryPayerDeleteWarning &&
        <WarningDialog
          show={showSecondaryPayerDeleteWarning}
          title='Delete Secondary Insurance'
          message='Do you want to delete patient secondary insurance data?'
          onCancel={() => setShowSecondaryPayerDeleteWarning(false)}
          onContinue={() => {
            setShowSecondaryPayerDeleteWarning(false);
            deleteInsurance('secondary');
          }}
        />
      }

      {/* Tertiary Insurance delete warning */}
      {showTertiaryPayerDeleteWarning &&
        <WarningDialog
          show={showTertiaryPayerDeleteWarning}
          title='Delete Tertiary Insurance'
          message='Do you want to delete patient tertiary insurance data?'
          onCancel={() => setShowTertiaryPayerDeleteWarning(false)}
          onContinue={() => {
            setShowTertiaryPayerDeleteWarning(false);
            deleteInsurance('tertiary');
          }}
        />
      }

      {/* Patient Avatar Cropper Drawer */}
      {showPatientAvatarCropperDrawer &&
        <>
          <div
            onClick={() => setShowPatientProfileDrawer(false)}
            style={{
              position: 'fixed',
              background: 'rgb(0, 24, 36)',
              top: '0px',
              height: '100vh',
              width: '100%',
              opacity: '0.8',
              zIndex: '1950'
            }}
          />
          <CropperDrawer
            isVisible={showPatientAvatarCropperDrawer}
            placement='custom'
            style={{ position: 'absolute', top: '0', right: '0', zIndex: '2050' }}
            cropperAspectRatio={1}
            headerText='Upload Patient Avatar'
            uploadUserMessage='Uploading Patient Avatar Image'
            cropButtonText='Crop Avatar Image'
            rawFileUploadUrl='users/uploadImg'
            croppedFileUploadUrl='patients/updatePatientAvatar'
            imageUploadFormData={{
              patientId: patientData._id
            }}
            onClose={() => {
              setShowPatientAvatarCropperDrawer(false);
            }}
            onCroppedImageUpload={(url) => {
              console.log(`Cropped file uploaded. URL = ${url}`);
              // props.onReloadPatientData();
            }}
          />
        </>
      }

      {/* Patient profile drawer */}
      {showPatientProfileDrawer &&
        <>
          <div
            onClick={() => setShowPatientDrawerCloseWarning(true)}
            style={{
              position: 'fixed',
              background: 'rgb(0, 24, 36)',
              top: '0px',
              height: '100vh',
              width: '100%',
              opacity: '0.8',
              zIndex: '1950'
            }}
          />
          <PatientProfileDrawer
            isVisible={showPatientProfileDrawer}
            placement='custom'
            style={{ position: 'absolute', top: '0', right: '0', zIndex: '2000' }}
            patientData={patientData}
            onCloseWithoutWarning={() => {
              setShowPatientProfileDrawer(false);
            }}
            onClose={() => {
              setShowPatientDrawerCloseWarning(true);
            }}
            onClickAvatarCamera={() => {
              console.log('Opening .... patient cropper drawer');
              setShowPatientAvatarCropperDrawer(true);
            }}
            onClickFrontIdCamera={() => {
              setShowPatientIdFrontCropperDrawer(true);
            }}
            onClickBackIdCamera={() => {
              setShowPatientIdBackCropperDrawer(true);
            }}
            onSavePatientProfile={handleOnSavePatientData}
          />
        </>
      }

      {/* Patient ID (Front) Cropper Drawer */}
      {showPatientIdFrontCropperDrawer &&
        <>
          <div
            className={css(styles.halfMask)}
            onClick={() => setShowPatientIdFrontCropperDrawer(false)}
          />
          <CropperDrawer
            isVisible={showPatientIdFrontCropperDrawer}
            placement='custom'
            style={{ position: 'absolute', top: '0', right: '0', zIndex: '2050' }}
            cropperAspectRatio={NaN}
            headerText='Upload Patient ID (Front)'
            uploadUserMessage='Uploading Patient ID (Front)'
            cropButtonText='Crop Image'
            rawFileUploadUrl='users/uploadImg'
            croppedFileUploadUrl='patients/updatePatientIdFront'
            imageUploadFormData={{
              patientId: patientData._id
            }}
            onClose={() => {
              setShowPatientIdFrontCropperDrawer(false);
            }}
            onCroppedImageUpload={(url) => {
              console.log(`Cropped file uploaded. URL = ${url}`);
              setPatientData((prvState) => ({
                ...prvState,
                patientIdFrontScan: url
              }));
              // props.onReloadPatientData();
            }}
          />
        </>
      }

      {/* Patient ID (Back) Cropper Drawer */}
      {showPatientIdBackCropperDrawer &&
        <>
          <div
            onClick={() => setShowPatientIdBackCropperDrawer(false)}
            className={css(styles.halfMask)}
          />
          <CropperDrawer
            isVisible={showPatientIdBackCropperDrawer}
            placement='custom'
            style={{ position: 'absolute', top: '0', right: '0', zIndex: '2050' }}
            cropperAspectRatio={NaN}
            headerText='Upload Patient ID (Back)'
            uploadUserMessage='Uploading Patient ID (Back)'
            cropButtonText='Crop Image'
            rawFileUploadUrl='users/uploadImg'
            croppedFileUploadUrl='patients/updatePatientIdBack'
            imageUploadFormData={{
              patientId: patientData._id
            }}
            onClose={() => {
              setShowPatientIdBackCropperDrawer(false);
            }}
            onCroppedImageUpload={(url) => {
              console.log(`Cropped file uploaded. URL = ${url}`);
              setPatientData((prvState) => ({
                ...prvState,
                patientIdBackScan: url
              }));
              // props.onReloadPatientData();
            }}
          />
        </>
      }

      {/* ---------------------------- Add Payer Drawer ---------------------------- */}
      {showAddPayerDrawer &&
        <>
          <div
            className={css(styles.halfMask)}
            onClick={() => setShowAddPayerDrawer(false)}
          />
          <AddPayerDrawer
            isOpen={showAddPayerDrawer}
            payerName={payerSearch}
            zIndex="2200"
            showMask={false}
            onClose={() => setShowAddPayerDrawer(false)}
            payerBannerImageUrl={payerBannerImageUrl}
            payerAvatarImageUrl={payerAvatarImageUrl}
            onReloadData={() => {
              console.log('Reloading data not required ...');
              setShowAddPayerDrawer(false);
            }}
            onClickAvatarCamera={() => {
              setShowAvatarCropperDrawer(true);
            }}
            onClickBannerCamera={() => {
              setShowBannerCropperDrawer(true);
            }}
          />
        </>
      }

      {/* ---------------------------- Payer Avatar Drawer ---------------------------- */}
      {showAvatarCropperDrawer &&
        <>
          <div
            className={css(styles.halfMask)}
            onClick={() => setShowAddPayerDrawer(false)}
          />
          <CropperDrawer
            isVisible={showAvatarCropperDrawer}
            placement='custom'
            style={{ position: 'absolute', top: '0', right: '0', zIndex: '2350' }}
            cropperAspectRatio={1}
            headerText='Upload Payer Avatar'
            uploadUserMessage='Uploading Payer Avatar Image'
            cropButtonText='Crop Avatar Image'
            rawFileUploadUrl='users/uploadImg'
            croppedFileUploadUrl='users/uploadImg'
            imageUploadFormData={{}}
            onClose={() => {
              setShowAvatarCropperDrawer(false);
            }}
            onCroppedImageUpload={(url) => {
              console.log(`Cropped file uploaded. URL = ${url}`);
              setPayerAvatarImageUrl(url);
            }}
          />
        </>
      }

      {/* ---------------------------- Payer Banner Drawer ---------------------------- */}
      {
        showBannerCropperDrawer &&
        <>
          <div
            className={css(styles.halfMask)}
            onClick={() => setShowAddPayerDrawer(false)}
          />
          <CropperDrawer
            isVisible={showBannerCropperDrawer}
            placement='custom'
            style={{ position: 'absolute', top: '0', right: '0', zIndex: '2350' }}
            cropperAspectRatio={6}
            headerText='Upload Payer Banner'
            uploadUserMessage='Uploading Payer Banner Image'
            cropButtonText='Crop Banner Image'
            rawFileUploadUrl='users/uploadImg'
            croppedFileUploadUrl='users/uploadImg'
            imageUploadFormData={{}}
            onClose={() => {
              setShowBannerCropperDrawer(false);
            }}
            onCroppedImageUpload={(url) => {
              console.log(`Cropped file uploaded. URL = ${url}`);
              setPayerBannerImageUrl(url);
            }}
          />
        </>
      }

      {/* ---------------------------- PRIMARY INSURANCE ---------------------------- */}
      {/* Primary Insurance Drawer */}
      {showPrimaryInsuranceDrawer &&
        <>
          <div
            className={css(styles.halfMask)}
            onClick={() => setShowPatientProfileDrawer(false)}
          />
          <InsuranceDrawer
            isVisible={showPrimaryInsuranceDrawer}
            onPayerSearch={(value) => {
              setPayerSearch(value);
            }}
            handleOnAddNewPayer={() => {
              setPayerBannerImageUrl('https://quantumtrader.s3.amazonaws.com/defaultBanner.svg');
              setPayerAvatarImageUrl('');
              setShowAddPayerDrawer(true);
            }}
            insuranceType='primary'
            headerText='Primary Insurnace'
            placement='custom'
            saveUrl='savePatientPrimaryInsuranceData'
            style={{ position: 'absolute', top: '0', right: '0' }}
            patientData={patientData}
            payerData={piPayerData}
            onShowWarning={() => {
              setShowPrimaryInsuranceDrawerCloseWarning(true);
            }}
            onShowPayerDeleteWarning={() => {
              setShowPrimaryInsurancePayerDeleteWarning(true);
            }}
            onSaveData={handleOnSavePrimaryInsurance}
            onClose={() => {
              setShowPrimaryInsuranceDrawer(false);
            }}
            onInsuranceCardFrontCameraClick={() => {
              setShowPrimaryInsuranceIdFrontDrawer(true);
            }}
            onInsuranceCardBackCameraClick={() => {
              setShowPrimaryInsuranceIdBackDrawer(true);
            }}
          />
        </>
      }

      {/* Primary Insurance ID (Front) Cropper Drawer */}
      {showPrimaryInsuranceIdFrontDrawer &&
        <>
          <div
            className={css(styles.halfMask)}
            onClick={() => setShowPatientProfileDrawer(false)}
          />
          <CropperDrawer
            isVisible={showPrimaryInsuranceIdFrontDrawer}
            placement='custom'
            style={{ position: 'absolute', right: '0', top: '0' }}
            zIndex='2020'
            cropperAspectRatio={NaN}
            headerText='Upload Primary Insurance Card (Front)'
            uploadUserMessage='Uploading PI Card (Front) Image'
            cropButtonText='Crop Image'
            rawFileUploadUrl='users/uploadImg'
            croppedFileUploadUrl='patients/updatePiCardFront'
            imageUploadFormData={{
              patientId: patientData._id
            }}
            onClose={() => {
              setShowPrimaryInsuranceIdFrontDrawer(false);
            }}
            onCroppedImageUpload={(url) => {
              setShowPrimaryInsuranceIdFrontDrawer(false);
              setPatientData((prvState) => ({
                ...prvState,
                primaryInsuranceCardFrontScan: url
              }));
            }}
          />
        </>
      }

      {/* Primary Insurance ID (BACK) Cropper Drawer */}
      {showPrimaryInsuranceIdBackDrawer &&
        <>
          <div
            className={css(styles.halfMask)}
            onClick={() => setShowPatientProfileDrawer(false)}
          />
          <CropperDrawer
            isVisible={showPrimaryInsuranceIdBackDrawer}
            placement='custom'
            style={{ position: 'absolute', right: '0', top: '0' }}
            zIndex='2020'
            cropperAspectRatio={NaN}
            headerText='Upload Primary Insurance Card (Back)'
            uploadUserMessage='Uploading PI Card (Back) Image'
            cropButtonText='Crop Image'
            rawFileUploadUrl='users/uploadImg'
            croppedFileUploadUrl='patients/updatePiCardBack'
            imageUploadFormData={{
              patientId: patientData._id
            }}
            onClose={() => {
              setShowPrimaryInsuranceIdBackDrawer(false);
            }}
            onCroppedImageUpload={(url) => {
              setShowPrimaryInsuranceIdBackDrawer(false);
              setPatientData((prvState) => ({
                ...prvState,
                primaryInsuranceCardBackScan: url
              }));
            }}
          />
        </>
      }

      {/* Primary Insurance Drawer Close Warning */}
      {showPrimaryInsuranceDrawerCloseWarning &&
        <WarningDialog
          show={showPrimaryInsuranceDrawerCloseWarning}
          title='Close Primary Insurance Drawer'
          message='Do you want to close without saving?'
          onCancel={() => setShowPrimaryInsuranceDrawerCloseWarning(false)}
          onContinue={() => {
            setShowPrimaryInsuranceDrawerCloseWarning(false);
            setShowPrimaryInsuranceDrawer(false);
          }}
        />
      }

      {/* Primary Insurance Payer Delete Warning Dialog */}
      {showPrimaryInsurancePayerDeleteWarning &&
        <WarningDialog
          show={showPrimaryInsurancePayerDeleteWarning}
          title='Delete Payer'
          message='Do you want to delete payer?'
          onCancel={() => setShowPrimaryInsurancePayerDeleteWarning(false)}
          onContinue={() => {
            setShowPrimaryInsurancePayerDeleteWarning(false);
            setShowPrimaryInsuranceDrawer(false);
            // --------------------------------------------------------
            const formData = new FormData();
            formData.append('patientId', patientData._id);
            const requestOptions = {
              method: 'POST',
              body: formData,
              redirect: 'follow'
              // headers: reqHeaders
            };

            fetch(`${BASE_URL}/patients/deletePrimaryInsurance`, requestOptions)
              .then(response => response.text())
              .then(result => {
                const res = JSON.parse(result);
                if (res.success) {
                  setPiPayerData({});
                  handleOnDeletePrimaryInsurance();
                }
              })
              .catch(error => console.log('error', error));
          }}
        />
      }

      {/* ---------------------------- SECONDARY INSURANCE ---------------------------- */}
      {/* Secondary Insurance Drawer */}
      {showSecondaryInsuranceDrawer &&
        <>
          <div
            className={css(styles.halfMask)}
            onClick={() => setShowPatientProfileDrawer(false)}
          />
          <InsuranceDrawer
            isVisible={showSecondaryInsuranceDrawer}
            onPayerSearch={(value) => {
              setPayerSearch(value);
            }}
            insuranceType='secondary'
            handleOnAddNewPayer={() => {
              setPayerBannerImageUrl('https://quantumtrader.s3.amazonaws.com/defaultBanner.svg');
              setPayerAvatarImageUrl('');
              setShowAddPayerDrawer(true);
            }}
            headerText='Secondary Insurnace'
            placement='custom'
            saveUrl='savePatientSecondaryInsuranceData'
            style={{ position: 'absolute', right: '0', top: '0' }}
            patientData={patientData}
            payerData={siPayerData}
            onShowWarning={() => {
              setShowSecondaryInsuranceDrawerCloseWarning(true);
            }}
            onShowPayerDeleteWarning={() => {
              setShowSecondaryInsurancePayerDeleteWarning(true);
            }}
            onSaveData={handleOnSaveSecondaryInsurance}
            onClose={() => {
              setShowSecondaryInsuranceDrawer(false);
            }}
            onInsuranceCardFrontCameraClick={() => {
              setShowSecondaryInsuranceIdFrontDrawer(true);
            }}
            onInsuranceCardBackCameraClick={() => {
              setShowSecondaryInsuranceIdBackDrawer(true);
            }}
          />
        </>
      }

      {/* Secondary Insurance ID (Front) Cropper Drawer */}
      {showSecondaryInsuranceIdFrontDrawer &&
        <>
          <div
            className={css(styles.halfMask)}
            onClick={() => setShowPatientProfileDrawer(false)}
          />
          <CropperDrawer
            isVisible={showSecondaryInsuranceIdFrontDrawer}
            placement='custom'
            style={{ position: 'absolute', right: '0', top: '0' }}
            zIndex='2020'
            cropperAspectRatio={NaN}
            headerText='Upload Secondary Insurance Card (Front)'
            uploadUserMessage='Uploading SI Card (Front) Image'
            cropButtonText='Crop Image'
            rawFileUploadUrl='users/uploadImg'
            croppedFileUploadUrl='patients/updateSiCardFront'
            imageUploadFormData={{
              patientId: patientData._id
            }}
            onClose={() => {
              setShowSecondaryInsuranceIdFrontDrawer(false);
            }}
            onCroppedImageUpload={(url) => {
              setShowSecondaryInsuranceIdFrontDrawer(false);
              setPatientData((prvState) => ({
                ...prvState,
                secondaryInsuranceCardFrontScan: url
              }));
            }}
          />
        </>
      }

      {/* Secondary Insurance ID (Back) Cropper Drawer */}
      {showSecondaryInsuranceIdBackDrawer &&
        <>
          <div
            className={css(styles.halfMask)}
            onClick={() => setShowPatientProfileDrawer(false)}
          />
          <CropperDrawer
            isVisible={showSecondaryInsuranceIdBackDrawer}
            placement='custom'
            style={{ position: 'absolute', right: '0', top: '0' }}
            zIndex='2020'
            cropperAspectRatio={NaN}
            headerText='Upload Secondary Insurance Card (Back)'
            uploadUserMessage='Uploading SI Card (Back) Image'
            cropButtonText='Crop Image'
            rawFileUploadUrl='users/uploadImg'
            croppedFileUploadUrl='patients/updateSiCardBack'
            imageUploadFormData={{
              patientId: patientData._id
            }}
            onClose={() => {
              setShowSecondaryInsuranceIdBackDrawer(false);
            }}
            onCroppedImageUpload={(url) => {
              setShowSecondaryInsuranceIdBackDrawer(false);
              setPatientData((prvState) => ({
                ...prvState,
                secondaryInsuranceCardBackScan: url
              }));
            }}
          />
        </>
      }

      {/* Secondary Insurance Drawer Close Warning */}
      {showSecondaryInsuranceDrawerCloseWarning &&
        <WarningDialog
          show={showSecondaryInsuranceDrawerCloseWarning}
          title='Close Secondary Insurance Drawer'
          message='Do you want to close without saving?'
          onCancel={() => setShowSecondaryInsuranceDrawerCloseWarning(false)}
          onContinue={() => {
            setShowSecondaryInsuranceDrawerCloseWarning(false);
            setShowSecondaryInsuranceDrawer(false);
          }}
        />
      }

      {/* Secondary Insurance Payer Delete Warning Dialog */}
      {showSecondaryInsurancePayerDeleteWarning &&
        <WarningDialog
          show={showSecondaryInsurancePayerDeleteWarning}
          title='Delete Payer'
          message='Do you want to delete payer?'
          onCancel={() => setShowSecondaryInsurancePayerDeleteWarning(false)}
          onContinue={() => {
            setShowSecondaryInsurancePayerDeleteWarning(false);
            setShowSecondaryInsuranceDrawer(false);
            // --------------------------------------------------------
            const formData = new FormData();
            formData.append('patientId', patientData._id);
            const requestOptions = {
              method: 'POST',
              body: formData,
              redirect: 'follow'
              // headers: reqHeaders
            };

            fetch(`${BASE_URL}/patients/deleteSecondaryInsurance`, requestOptions)
              .then(response => response.text())
              .then(result => {
                const res = JSON.parse(result);
                if (res.success) {
                  setSiPayerData({});
                  handleOnDeleteSecondaryInsurance();
                }
              })
              .catch(error => console.log('error', error));
          }}
        />
      }

      {/* ---------------------------- TERTIARY INSURANCE ---------------------------- */}
      {/* Tertiary Insurance Drawer */}
      {showTertiaryInsuranceDrawer &&
        <>
          <div
            className={css(styles.halfMask)}
            onClick={() => setShowPatientProfileDrawer(false)}
          />
          <InsuranceDrawer
            isVisible={showTertiaryInsuranceDrawer}
            onPayerSearch={(value) => {
              setPayerSearch(value);
            }}
            insuranceType='tertiary'
            handleOnAddNewPayer={() => {
              setPayerBannerImageUrl('https://quantumtrader.s3.amazonaws.com/defaultBanner.svg');
              setPayerAvatarImageUrl('');
              setShowAddPayerDrawer(true);
            }}
            headerText='Tertiary Insurnace'
            placement='custom'
            saveUrl='savePatientTertiaryInsuranceData'
            style={{ position: 'absolute', right: '0', top: '0' }}
            patientData={patientData}
            payerData={tiPayerData}
            onShowWarning={() => {
              setShowTertiaryInsuranceDrawerCloseWarning(true);
            }}
            onShowPayerDeleteWarning={() => {
              setShowTertiaryInsurancePayerDeleteWarning(true);
            }}
            onSaveData={handleOnSaveTertiaryInsurance}
            onClose={() => {
              setShowTertiaryInsuranceDrawer(false);
            }}
            onInsuranceCardFrontCameraClick={() => {
              setShowTertiaryInsuranceIdFrontDrawer(true);
            }}
            onInsuranceCardBackCameraClick={() => {
              setShowTertiaryInsuranceIdBackDrawer(true);
            }}
          />
        </>
      }

      {/* Tertiary Insurance ID (Front) Cropper Drawer */}
      {showTertiaryInsuranceIdFrontDrawer &&
        <>
          <div
            className={css(styles.halfMask)}
            onClick={() => setShowPatientProfileDrawer(false)}
          />
          <CropperDrawer
            isVisible={showTertiaryInsuranceIdFrontDrawer}
            placement='custom'
            style={{ position: 'absolute', right: '0', top: '0' }}
            zIndex='2020'
            cropperAspectRatio={NaN}
            headerText='Upload Tertiary Insurance Card (Front)'
            uploadUserMessage='Uploading TI Card (Front) Image'
            cropButtonText='Crop Image'
            rawFileUploadUrl='users/uploadImg'
            croppedFileUploadUrl='patients/updateTiCardFront'
            imageUploadFormData={{
              patientId: patientData._id
            }}
            onClose={() => {
              setShowTertiaryInsuranceIdFrontDrawer(false);
            }}
            onCroppedImageUpload={(url) => {
              setShowTertiaryInsuranceIdFrontDrawer(false);
              setPatientData((prvState) => ({
                ...prvState,
                tertiaryInsuranceCardFrontScan: url
              }));
            }}
          />
        </>
      }

      {/* Tertiary Insurance ID (Back) Cropper Drawer */}
      {showTertiaryInsuranceIdBackDrawer &&
        <>
          <div
            className={css(styles.halfMask)}
            onClick={() => setShowPatientProfileDrawer(false)}
          />
          <CropperDrawer
            isVisible={showTertiaryInsuranceIdBackDrawer}
            placement='custom'
            style={{ position: 'absolute', right: '0', top: '0' }}
            zIndex='2020'
            cropperAspectRatio={NaN}
            headerText='Upload Tertiary Insurance Card (Back)'
            uploadUserMessage='Uploading TI Card (Back) Image'
            cropButtonText='Crop Image'
            rawFileUploadUrl='users/uploadImg'
            croppedFileUploadUrl='patients/updateTiCardBack'
            imageUploadFormData={{
              patientId: patientData._id
            }}
            onClose={() => {
              setShowTertiaryInsuranceIdBackDrawer(false);
            }}
            onCroppedImageUpload={(url) => {
              setShowTertiaryInsuranceIdBackDrawer(false);
              setPatientData((prvState) => ({
                ...prvState,
                tertiaryInsuranceCardBackScan: url
              }));
            }}
          />
        </>
      }

      {/* Tertiary Insurance Drawer Close Warning */}
      {showTertiaryInsuranceDrawerCloseWarning &&
        <WarningDialog
          show={showTertiaryInsuranceDrawerCloseWarning}
          title='Close Tertiary Insurance Drawer'
          message='Do you want to close without saving?'
          onCancel={() => setShowTertiaryInsuranceDrawerCloseWarning(false)}
          onContinue={() => {
            setShowTertiaryInsuranceDrawerCloseWarning(false);
            setShowTertiaryInsuranceDrawer(false);
          }}
        />
      }

      {/* Tertiary Insurance Payer Delete Warning Dialog */}
      {showTertiaryInsurancePayerDeleteWarning &&
        <WarningDialog
          show={showTertiaryInsurancePayerDeleteWarning}
          title='Delete Payer'
          message='Do you want to delete payer?'
          onCancel={() => setShowTertiaryInsurancePayerDeleteWarning(false)}
          onContinue={() => {
            setShowTertiaryInsurancePayerDeleteWarning(false);
            setShowTertiaryInsuranceDrawer(false);
            // --------------------------------------------------------
            const formData = new FormData();
            formData.append('patientId', patientData._id);
            const requestOptions = {
              method: 'POST',
              body: formData,
              redirect: 'follow'
              // headers: reqHeaders
            };

            fetch(`${BASE_URL}/patients/deleteTertiaryInsurance`, requestOptions)
              .then(response => response.text())
              .then(result => {
                const res = JSON.parse(result);
                if (res.success) {
                  setTiPayerData({});
                  handleOnDeleteTertiaryInsurance();
                }
              })
              .catch(error => console.log('error', error));
          }}
        />
      }

      <div style={{ background: '#003060' }}>
        {/* RCM Heading with Icon */}
        <div
          className='fx fx__v-center'
          style={{
            height: '32px',
            background: '#003060',
            justifyContent: 'space-between',
            // display: 'flex',
            display: 'none'
          }}
        >
          <div
            className='ml-2'
            style={{
              fontWeight: '700',
              color: '#96C8FF'
            }}
          >
            RCM: Revenue Cycle Management Dashboard
          </div>
          {/* <i
            className={`pr-1 fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
            onClick={() => setShowCasePostingDrawer(false)}
          /> */}
        </div>
        {/* RCM Tabs and SubTabs */}
        <div
          style={{
            height: '64px',
            background: '#003060',
            borderTop: '1px solid #000'
          }}
        >
          {/* Main Tabs */}
          <div>
            <TabsContainer
              dropDownId="d2_RCM"
              showPlusIcon={false}
              isDrawer={false}
              shiftMethod='minWidth'
              minWidth={610}
              containerStyles={{ width: '100%' }}
            >
              {/* Patient/Case Info - Case Posting */}
              <TabItem
                key='facility_RCM_main_tabs_1'
                tabKey="case_patient_info_1"
                parentKey="1111"
                text="Patient/Case Info"
                isActive={activeRcmTab === 'patient-case-info'}
                showTabIcon
                showSvgIcon
                svgIcon={(() => {
                  if (caseFlag === FLAG_VALUE.success) {
                    return SUCCESS_ICON;
                  } else if (caseFlag === FLAG_VALUE.warning) {
                    return BELL_ICON;
                  } else {
                    return CRITIAL_ICON;
                  }
                })()}
                style={{
                  width: '115px',
                  minWidth: '115px',
                  maxWidth: '115px',
                  height: '30px',
                  background: activeRcmTab === 'patient-case-info' ? '#184878' : '#083050',
                  borderBottom: 'none',
                  marginRight: '0'
                }}
                textStyles={tabsTextStyles}
                onActivate={() => {
                  setActiveRcmTab('patient-case-info');
                }}
              >
                Case Posting
              </TabItem>
              {/* Facility RCM */}
              {!isMobileLayout &&
                <TabItem
                  key='facility_RCM_main_tabs_2'
                  tabKey="facility_rcm_2222"
                  parentKey="1112"
                  text="Facility RCM"
                  isActive={activeRcmTab === 'facility-rcm'}
                  showTabIcon
                  showSvgIcon
                  svgIcon={rcmIcon}
                  style={{
                    width: '155px',
                    minWidth: '155px',
                    maxWidth: '155px',
                    height: '30px',
                    background: activeRcmTab === 'facility-rcm' ? '#184878' : '#083050',
                    borderBottom: 'none',
                    marginRight: '0'
                  }}
                  textStyles={tabsTextStyles}
                  onActivate={() => {
                    setActiveRcmTab('facility-rcm');
                  }}
                >
                  Facility RCM
                </TabItem>
              }
              {/* Nursing RCM */}
              {!isMobileLayout &&
                <TabItem
                  key='nursing_RCM_main_tabs_3'
                  tabKey="nursing_3"
                  parentKey="1113"
                  text="Nursing"
                  isActive={activeRcmTab === 'nursing-rcm'}
                  showTabIcon
                  showSvgIcon
                  svgIcon={rcmIcon}
                  style={{
                    width: '155px',
                    minWidth: '155px',
                    maxWidth: '155px',
                    height: '30px',
                    background: activeRcmTab === 'nursing-rcm' ? '#184878' : '#083050',
                    borderBottom: 'none',
                    marginRight: '0'
                  }}
                  textStyles={tabsTextStyles}
                  onActivate={() => {
                    setActiveRcmTab('nursing-rcm');
                  }}
                >
                  Nursing RCM
                </TabItem>
              }
              {/* Surgeon RCM */}
              {!isMobileLayout &&
                <TabItem
                  key='surgeon_RCM_main_tabs_4'
                  tabKey="surgeon_99990_4"
                  parentKey="1114"
                  text="Anesthesiologist"
                  isActive={activeRcmTab === 'surgeon-rcm'}
                  showTabIcon
                  showSvgIcon
                  svgIcon={rcmIcon}
                  style={{
                    width: '155px',
                    minWidth: '155px',
                    maxWidth: '155px',
                    height: '30px',
                    background: activeRcmTab === 'surgeon-rcm' ? '#184878' : '#083050',
                    borderBottom: 'none',
                    marginRight: '0'
                  }}
                  textStyles={tabsTextStyles}
                  onActivate={() => {
                    setActiveRcmTab('surgeon-rcm');
                  }}
                >
                  Surgeon RCM
                </TabItem>
              }
              {/* Anesthesia RCM */}
              <TabItem
                key='crna_RCM_main_tabs_5'
                tabKey="crna_5"
                parentKey="1115"
                text="CRNA/CAA"
                isActive={activeRcmTab === 'anesthesia-rcm'}
                showTabIcon
                showSvgIcon
                svgIcon={rcmIcon}
                style={{
                  width: '155px',
                  minWidth: '155px',
                  maxWidth: '155px',
                  height: '30px',
                  marginRight: '0',
                  background: activeRcmTab === 'anesthesia-rcm' ? '#184878' : '#083050',
                  borderBottom: 'none'
                }}
                textStyles={tabsTextStyles}
                onActivate={() => {
                  setActiveRcmTab('anesthesia-rcm');
                }}
              >
                Anesthesia RCM
              </TabItem>
            </TabsContainer>
          </div>
          {/* Sub Tabs */}
          <div className='pl-2'>
            {rcmTabs}
          </div>
        </div>
      </div>
      {/* ---------------------------- RCM Active Tab ---------------------------- */}
      <div
        style={{
          // height: 'calc(100vh - 138px)',
          height: 'calc(100vh - 106px)',
          // background: gray[700]
          background: '#082840',
          overflow: 'auto'
          // background: '#780178'
          // padding: '16px',
        }}
      >
        {/* Add Patient */}
        {Object.keys(patientData).length === 0 && activeRcmTab === 'patient-case-info' && activePatientCaseInfoTab === 'patient-info' &&
          <AddPatient
            className='pl-4'
            caseData={caseData}
            onAddPatient={handleOnAddPatient}
          />
        }

        {/* Patient Data */}
        {Object.keys(patientData).length > 0 && activeRcmTab === 'patient-case-info' && activePatientCaseInfoTab === 'patient-info' &&
          <div style={{ width: '400px' }}>
            <PatientInfo
              patientData={patientData}
              onEdit={() => setShowPatientProfileDrawer(true)}
              onDeletePatient={handleOnDeletePatient}
            />
          </div>
        }

        {/* Payers Tab */}
        {activeRcmTab === 'patient-case-info' && activePatientCaseInfoTab === 'payers' &&
          <div>
            <TabsContainer
              dropDownId="dddd3"
              showPlusIcon={false}
              isDrawer={false}
              shiftMethod='minWidth'
              minWidth={610}
              containerStyles={{ width: '100%' }}
            >
              {/* Primary Insurnance */}
              <TabItem
                key='cp_payers_primary_1'
                tabKey="cp_payers_primary_1"
                parentKey="cp_payers_primary_1"
                text="Primary Insurnace"
                isActive={payersActiveTab === 'primary'}
                showTabIcon
                showSvgIcon
                svgIcon={primaryFlag === FLAG_VALUE.success ? SUCCESS_ICON : BELL_ICON}
                style={{
                  width: '120px',
                  minWidth: '120px',
                  maxWidth: '120px',
                  background: '#003060',
                  margin: '0',
                  height: '32px',
                  color: payersActiveTab === 'primary' ? '#00a0ff' : '',
                  borderBottom: payersActiveTab === 'primary' ? '1px solid #00a0ff' : '1px solid #003060'
                }}
                textStyles={tabsTextStyles}
                onActivate={() => {
                  setPayersActiveTab('primary');
                }}
              >
                Primary
              </TabItem>
              {/* Secondary Insurnance */}
              <TabItem
                key='cp_payers_secondary_1'
                tabKey="cp_payers_secondary_1"
                parentKey="cp_payers_secondary_1"
                text="Secondary Insurnace"
                isActive={payersActiveTab === 'secondary'}
                showTabIcon
                showSvgIcon
                svgIcon={secondaryFlag === FLAG_VALUE.success ? SUCCESS_ICON : BELL_ICON}
                style={{
                  width: '120px',
                  minWidth: '120px',
                  maxWidth: '120px',
                  margin: '0',
                  background: '#003060',
                  height: '32px',
                  color: payersActiveTab === 'secondary' ? '#00a0ff' : '',
                  borderBottom: payersActiveTab === 'secondary' ? '1px solid #00a0ff' : '1px solid #003060'
                }}
                textStyles={tabsTextStyles}
                onActivate={() => {
                  setPayersActiveTab('secondary');
                }}
              >
                Secondary
              </TabItem>
              {/* Tertiary Insurnance */}
              <TabItem
                key='cp_payers_tertiary_1'
                tabKey="cp_payers_tertiary_1"
                parentKey="cp_payers_tertiary_1"
                text="Tertiary Insurnace"
                isActive={payersActiveTab === 'tertiary'}
                showTabIcon
                showSvgIcon
                svgIcon={tertiaryFlag === FLAG_VALUE.success ? SUCCESS_ICON : BELL_ICON}
                style={{
                  width: '120px',
                  minWidth: '120px',
                  maxWidth: '120px',
                  background: '#003060',
                  height: '32px',
                  margin: '0',
                  color: payersActiveTab === 'tertiary' ? '#00a0ff' : '',
                  borderBottom: payersActiveTab === 'tertiary' ? '1px solid #00a0ff' : '1px solid #003060'
                }}
                textStyles={tabsTextStyles}
                onActivate={() => {
                  setPayersActiveTab('tertiary');
                }}
              >
                Tertiary
              </TabItem>
              {/* Self Pay */}
              <TabItem
                key='cp_payers_selfPay_1'
                tabKey="cp_payers_selfPay_1"
                parentKey="cp_payers_selfPay_1"
                text="Tertiary Insurnace"
                isActive={payersActiveTab === 'selfPay'}
                showTabIcon
                showSvgIcon
                svgIcon={selfPayFlag === FLAG_VALUE.success ? SUCCESS_ICON : BELL_ICON}
                style={{
                  width: '120px',
                  minWidth: '120px',
                  maxWidth: '120px',
                  background: '#003060',
                  margin: '0',
                  height: '32px',
                  color: payersActiveTab === 'selfPay' ? '#00a0ff' : '',
                  borderBottom: payersActiveTab === 'selfPay' ? '1px solid #00a0ff' : '1px solid #003060'
                }}
                textStyles={tabsTextStyles}
                onActivate={() => {
                  setPayersActiveTab('selfPay');
                }}
              >
                Self Pay
              </TabItem>
            </TabsContainer>
            {/* Primary Insurance */}
            {!isLoadingData && activeRcmTab === 'patient-case-info' && activePatientCaseInfoTab === 'payers' && payersActiveTab === 'primary' && Object.keys(patientData).length === 0 &&
              <div
                className='pl-4 mt-4'
                style={{ width: '375px' }}
              >
                Patient profile must be created to add payers. Go to &quot;Patient&quot; tab to create patient profile.
              </div>
            }
            {patientData && Object.keys(patientData).length > 0 && activeRcmTab === 'patient-case-info' && activePatientCaseInfoTab === 'payers' && payersActiveTab === 'primary' && patientData.primaryInsurance && Object.keys(patientData.primaryInsurance).length > 0
              ? <InsuranceInfo
                headingText='Primary Insurnace'
                patientData={patientData}
                insuranceType='primary'
                onDeletePayer={() => {
                  setShowPrimaryPayerDeleteWarning(true);
                }}
                onEditInsuranceInfo={() => {
                  setShowPrimaryInsuranceDrawer(true);
                }}
              />
              : <div
                className='pl-4 mt-4'
                style={{ display: !isLoadingData && activeRcmTab === 'patient-case-info' && activePatientCaseInfoTab === 'payers' && payersActiveTab === 'primary' && Object.keys(patientData).length > 0 ? '' : 'none' }}
              >
                {/* Add Primary Insurance Button */}
                <Button
                  btnType='primary'
                  isDisabled={isNoPrimaryInsurance}
                  onClick={() => { setShowPrimaryInsuranceDrawer(true); }}
                  style={{
                    width: '375px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  className={`${isNoPrimaryInsurance ? 'disabled' : ''} ${css(styles.btnIcon)}`}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <svg
                      width="18"
                      height="20"
                      fill="inherit"
                      xmlns="http://www.w3.org/2000/svg"
                      className='mr-2'
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M.5 2.857 9.071 0l8.572 2.857v8.572A8.57 8.57 0 0 1 9.07 20 8.57 8.57 0 0 1 .5 11.429V2.857Zm8.574 15.536a6.965 6.965 0 0 0 6.964-6.964V4.063L9.074 1.607 2.109 4.063v7.366a6.965 6.965 0 0 0 6.965 6.964Zm1.101-5.318V14h-4v-.925H7.64V8.86c0-.167.005-.338.015-.515l-1.04.87a.457.457 0 0 1-.18.09.457.457 0 0 1-.3-.05.318.318 0 0 1-.09-.085l-.39-.535L7.86 6.76h1.015v6.315h1.3Zm2.957-8.095a2.222 2.222 0 0 0-.828-.148c-.307 0-.585.05-.836.148-.25.099-.465.239-.644.42a1.906 1.906 0 0 0-.412.664 2.526 2.526 0 0 0-.144.876c0 .325.048.619.144.88.099.261.236.484.412.668.179.184.393.325.644.424.25.099.53.148.836.148.304 0 .58-.05.828-.148.25-.099.464-.24.64-.424a1.87 1.87 0 0 0 .412-.668c.096-.261.144-.555.144-.88 0-.323-.048-.615-.144-.876a1.794 1.794 0 0 0-1.052-1.084Zm-.072 2.976c-.163.23-.415.344-.756.344-.347 0-.604-.115-.772-.344-.165-.232-.248-.568-.248-1.008 0-.44.083-.776.248-1.008.168-.235.425-.352.772-.352.341 0 .593.116.756.348.165.232.248.57.248 1.012 0 .443-.083.779-.248 1.008Z"
                        style={{ fill: 'inherit' }}
                      />
                    </svg>
                    <div>Add Primary Insurnace</div>
                  </div>
                </Button>
                {/* Is No Primary Insurance Checkbox */}
                <div className='mt-2'>
                  <Checkbox
                    text='Check if no Primary Insurance'
                    isChecked={isNoPrimaryInsurance}
                    onChange={() => {
                      handleOnChangeIsNoPrimaryInsurance();
                    }}
                  />
                </div>
              </div>
            }

            {/* Secondary Insurance */}
            {!isLoadingData && activeRcmTab === 'patient-case-info' && activePatientCaseInfoTab === 'payers' && payersActiveTab === 'secondary' && Object.keys(patientData).length === 0 &&
              <div
                className='pl-4 mt-4'
                style={{ width: '375px' }}
              >
                Patient profile must be created to add payers. Go to &quot;Patient&quot; tab to create patient profile.
              </div>
            }
            {patientData && Object.keys(patientData).length > 0 && activeRcmTab === 'patient-case-info' && activePatientCaseInfoTab === 'payers' && payersActiveTab === 'secondary' && patientData.secondaryInsurance && Object.keys(patientData.secondaryInsurance).length > 0
              ? <InsuranceInfo
                headingText='Secondary Insurnace'
                patientData={patientData}
                insuranceType='secondary'
                onDeletePayer={() => {
                  setShowSecondaryPayerDeleteWarning(true);
                }}
                onEditInsuranceInfo={() => {
                  setShowSecondaryInsuranceDrawer(true);
                }}
              />
              : <div
                className='pl-4 mt-4'
                style={{ display: !isLoadingData && activeRcmTab === 'patient-case-info' && activePatientCaseInfoTab === 'payers' && payersActiveTab === 'secondary' && Object.keys(patientData).length > 0 ? '' : 'none' }}
              >
                {/* Add Secondary Insurance Button */}
                <Button
                  btnType='primary'
                  isDisabled={isNoSecondaryInsurance}
                  onClick={() => { setShowSecondaryInsuranceDrawer(true); }}
                  style={{
                    width: '375px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  className={`${isNoSecondaryInsurance ? 'disabled' : ''} ${css(styles.btnIcon)}`}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <svg
                      width="18"
                      height="20"
                      fill="inherit"
                      xmlns="http://www.w3.org/2000/svg"
                      className='mr-2'
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M.5 2.857 9.071 0l8.572 2.857v8.572A8.57 8.57 0 0 1 9.07 20 8.57 8.57 0 0 1 .5 11.429V2.857Zm8.574 15.536a6.965 6.965 0 0 0 6.964-6.964V4.063L9.074 1.607 2.109 4.063v7.366a6.965 6.965 0 0 0 6.965 6.964Zm1.101-5.318V14h-4v-.925H7.64V8.86c0-.167.005-.338.015-.515l-1.04.87a.457.457 0 0 1-.18.09.457.457 0 0 1-.3-.05.318.318 0 0 1-.09-.085l-.39-.535L7.86 6.76h1.015v6.315h1.3Zm2.957-8.095a2.222 2.222 0 0 0-.828-.148c-.307 0-.585.05-.836.148-.25.099-.465.239-.644.42a1.906 1.906 0 0 0-.412.664 2.526 2.526 0 0 0-.144.876c0 .325.048.619.144.88.099.261.236.484.412.668.179.184.393.325.644.424.25.099.53.148.836.148.304 0 .58-.05.828-.148.25-.099.464-.24.64-.424a1.87 1.87 0 0 0 .412-.668c.096-.261.144-.555.144-.88 0-.323-.048-.615-.144-.876a1.794 1.794 0 0 0-1.052-1.084Zm-.072 2.976c-.163.23-.415.344-.756.344-.347 0-.604-.115-.772-.344-.165-.232-.248-.568-.248-1.008 0-.44.083-.776.248-1.008.168-.235.425-.352.772-.352.341 0 .593.116.756.348.165.232.248.57.248 1.012 0 .443-.083.779-.248 1.008Z"
                        style={{ fill: 'inherit' }}
                      />
                    </svg>
                    <div>Add Secondary Insurnace</div>
                  </div>
                </Button>
                {/* Is No Secondary Insurance Checkbox */}
                <div className='mt-2'>
                  <Checkbox
                    text='Check if no Secondary Insurance'
                    isChecked={isNoSecondaryInsurance}
                    onChange={() => {
                      handleOnChangeIsNoSecondaryInsurance();
                    }}
                  />
                </div>
              </div>
            }

            {/* Tertiary Insurance */}
            {!isLoadingData && activeRcmTab === 'patient-case-info' && activePatientCaseInfoTab === 'payers' && payersActiveTab === 'tertiary' && Object.keys(patientData).length === 0 &&
              <div
                className='pl-4 mt-4'
                style={{ width: '375px' }}
              >
                Patient profile must be created to add payers. Go to &quot;Patient&quot; tab to create patient profile.
              </div>
            }
            {patientData && Object.keys(patientData).length > 0 && activeRcmTab === 'patient-case-info' && activePatientCaseInfoTab === 'payers' && payersActiveTab === 'tertiary' && patientData.tertiaryInsurance && Object.keys(patientData.tertiaryInsurance).length > 0
              ? <InsuranceInfo
                headingText='Tertiary Insurnace'
                patientData={patientData}
                insuranceType='tertiary'
                onDeletePayer={() => {
                  setShowTertiaryPayerDeleteWarning(true);
                }}
                onEditInsuranceInfo={() => {
                  setShowTertiaryInsuranceDrawer(true);
                }}
              />
              : <div
                className='pl-4 mt-4'
                style={{ display: !isLoadingData && activeRcmTab === 'patient-case-info' && activePatientCaseInfoTab === 'payers' && payersActiveTab === 'tertiary' && Object.keys(patientData).length > 0 ? '' : 'none' }}
              >
                <Button
                  btnType='primary'
                  isDisabled={isNoTertiaryInsurance}
                  onClick={() => { setShowTertiaryInsuranceDrawer(true); }}
                  style={{
                    width: '375px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  className={`${isNoTertiaryInsurance ? 'disabled' : ''} ${css(styles.btnIcon)}`}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <svg
                      width="18"
                      height="20"
                      fill="inherit"
                      xmlns="http://www.w3.org/2000/svg"
                      className='mr-2'
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M.5 2.857 9.071 0l8.572 2.857v8.572A8.57 8.57 0 0 1 9.07 20 8.57 8.57 0 0 1 .5 11.429V2.857Zm8.574 15.536a6.965 6.965 0 0 0 6.964-6.964V4.063L9.074 1.607 2.109 4.063v7.366a6.965 6.965 0 0 0 6.965 6.964Zm1.101-5.318V14h-4v-.925H7.64V8.86c0-.167.005-.338.015-.515l-1.04.87a.457.457 0 0 1-.18.09.457.457 0 0 1-.3-.05.318.318 0 0 1-.09-.085l-.39-.535L7.86 6.76h1.015v6.315h1.3Zm2.957-8.095a2.222 2.222 0 0 0-.828-.148c-.307 0-.585.05-.836.148-.25.099-.465.239-.644.42a1.906 1.906 0 0 0-.412.664 2.526 2.526 0 0 0-.144.876c0 .325.048.619.144.88.099.261.236.484.412.668.179.184.393.325.644.424.25.099.53.148.836.148.304 0 .58-.05.828-.148.25-.099.464-.24.64-.424a1.87 1.87 0 0 0 .412-.668c.096-.261.144-.555.144-.88 0-.323-.048-.615-.144-.876a1.794 1.794 0 0 0-1.052-1.084Zm-.072 2.976c-.163.23-.415.344-.756.344-.347 0-.604-.115-.772-.344-.165-.232-.248-.568-.248-1.008 0-.44.083-.776.248-1.008.168-.235.425-.352.772-.352.341 0 .593.116.756.348.165.232.248.57.248 1.012 0 .443-.083.779-.248 1.008Z"
                        style={{ fill: 'inherit' }}
                      />
                    </svg>
                    <div>Add Tertiary Insurnace</div>
                  </div>
                </Button>
                <div className='mt-2'>
                  <Checkbox
                    text='Check if no Tertiary Insurance'
                    isChecked={isNoTertiaryInsurance}
                    onChange={() => {
                      handleOnChangeIsNoTertiaryInsurance();
                    }}
                  />
                </div>
              </div>
            }
          </div>
        }

        {/* Case Tab */}
        {activeRcmTab === 'patient-case-info' && activePatientCaseInfoTab === 'case-info' &&
          <div style={{ width: '355px' }}>
            <CaseTab
              ref={caseTabRef}
              isDashboard
              onUpdatePatientCard={handleOnUpdatePatientCard}
              onClose={closeScheduleCaseDrawer}
              onSave={handleOnSaveCase}
              onDataChange={handleOnCaseDataChange}
              showFooter={false}
              caseData={caseData}
              // onUpdateCaseData={handleOnUpdateCaseData}
              onUpdateCaseData={handleOnSaveCase}
              onChangeFlag={handleOnChangeCaseFlag}
            />
          </div>
        }
        {/* Facility - RCM */}
        {activeRcmTab === 'facility-rcm' &&
          <div className='p-2'>
            Facility RCM Placeholder
          </div>
        }
        {/* Nursing - RCM */}
        {activeRcmTab === 'nursing-rcm' &&
          <div className='p-2'>
            Nursing RCM Placeholder
          </div>
        }
        {/* Surgeon - RCM */}
        {activeRcmTab === 'surgeon-rcm' &&
          <div className='p-2'>
            Surgeon Data RCM Placeholder
          </div>
        }

        {/* Anesthesia - RCM */}
        {activeRcmTab === 'anesthesia-rcm' && activeAnesthesiaRCMTab === 'cms1500' &&
          <div className='p-2'>
            Anesthesia RCM Placeholder
          </div>
        }

        {activeRcmTab === 'anesthesia-rcm' && activeAnesthesiaRCMTab === 'payments' &&
          <div className='p-2'>
            Anesthesia RCM Placeholder
          </div>
        }
        {activeRcmTab === 'anesthesia-rcm' && activeAnesthesiaRCMTab === 'superbill' &&
          <div
            className='p-2'
            style={{ height: '100%' }}
          >
            <Superbill
              caseData={caseData}
              patients={[patientData._id]}
            />
          </div>
        }
      </div>
    </div>
  );

  const callBackMouseMove = useCallback((e) => {
    // console.log(`[Debug] isResizing = ${isResizing}`);
    console.log('Resizing ... ');
    // setDocsWidth(docsWidth + e.clientX - startX);
    setDocsWidth(e.clientX);
    // if (isResizing) {
    //   setDocsWidth(docsWidth + e.clientX - startX);
    // }
  }, []);

  const callBackMouseUp = useCallback(() => {
    console.log('[Debug] MouseUp - Stop Resizing');
    document.removeEventListener('mousemove', callBackMouseMove);
  }, []);

  const callBackMouseDown = useCallback(() => {
    console.log('[Debug] Start Resizing');
    document.addEventListener('mousemove', callBackMouseMove);
  }, []);

  /* _____ MENU ITEMS ________________________________________________ */
  // #region * MENU ITEMS * -- Anesthesia Procedures
  const procedureSearchItems = [];
  // Append search text
  if (anesthesiaProcedureValue && anesthesiaProcedureValue.length > 0) {
    procedureSearchItems.push(
      <div
        key='88888999SurgProcedure'
        className={`fx fx__v-center ${css(styles.menuItem)}`}
        onClick={(e) => {
          e.stopPropagation();
          const tmpProc = {
            id: Math.random().toString(36).slice(2),
            text: anesthesiaProcedureValue,
            code: '',
            description: anesthesiaProcedureValue,
            npi: 0
          };
          handleOnCaseDataChange();
          setShowAnesthesiaProceduresSearchMenu(false);
          setAnesthesiaProcedureValue('');
          let updatedAnesProcs = clone(caseData.anesthesiaProcedures);
          updatedAnesProcs = updatedAnesProcs.concat(tmpProc);
          setCaseData(prvState => ({
            ...prvState,
            anesthesiaProcedures: updatedAnesProcs
          }));
        }}
      >
        <div className='ml-2'>
          <div style={{ height: '40px', display: 'flex', alignItems: 'center' }}>
            {anesthesiaProcedureValue}
          </div>
        </div>
      </div>
    );
  }
  if (anesthesiaProceduresSearchResults.length > 0) {
    // Append search results
    anesthesiaProceduresSearchResults.forEach((el) => {
      procedureSearchItems.push(
        <div
          key={el._id}
          className={`fx fx__v-center ${css(styles.menuItem)}`}
          onClick={(e) => {
            e.stopPropagation();
            handleOnCaseDataChange();
            setShowAnesthesiaProceduresSearchMenu(false);
            setAnesthesiaProcedureValue('');
            setCaseData((prvState) => {
              let updatedAnesProcs = clone(caseData.anesthesiaProcedures);
              if (updatedAnesProcs.findIndex(item => item.id === el._id) < 0) {
                updatedAnesProcs = updatedAnesProcs.concat({
                  id: el._id,
                  text: `${el.description}`,
                  code: el.code,
                  description: `${el.description}`,
                  npi: el.npi
                });
                return {
                  ...prvState,
                  anesthesiaProcedures: updatedAnesProcs
                };
              } else {
                return prvState;
              }
            });
          }}
        >
          <div className='ml-2'>
            <div style={{ height: '40px', display: 'flex', alignItems: 'center' }}>
              {getHighlightedText(el.description, anesthesiaProcedureValue)}
            </div>
          </div>
        </div>
      );
    });
  }
  // #endregion

  return (
    <div className={css(styles.dashboardContainer)}>
      {/* ------------------------ Main Header ------------------------ */}
      <div className={css(styles.header)}>
        {/* Left side */}
        <div className='fx fx__hv-center'>
          <div className={`pl-1 ${css(styles.headerText)}`}>
            {headerText}
          </div>
        </div>
        {/* Right side */}
        <div className='fx fx__hv-center'>
          {/* Unknown icons ??? */}
          <div
            className={css(styles.rightIconContainer)}
            style={{ display: 'none' }}
          >
            <svg
              width="24"
              height="24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                width="24"
                height="24"
                rx="2"
                fill="#00A0FF"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M16.2 7.2h3.2c.888 0 1.6.712 1.6 1.6v8.8c0 .888-.712 1.6-1.6 1.6H6.6c-.888 0-1.6-.712-1.6-1.6l.008-8.8c0-.888.704-1.6 1.592-1.6h3.2V5.6c0-.888.712-1.6 1.6-1.6h3.2c.888 0 1.6.712 1.6 1.6v1.6Zm-4.802 0h3.2V5.6h-3.2v1.6Z"
                fill="#fff"
              />
            </svg>
            <svg
              width="16"
              height="16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className='ml-1'
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.437 2a.562.562 0 0 0-.563.563v1.343A8.006 8.006 0 0 0 8 0a7.97 7.97 0 0 0-5.453 2.147A7.972 7.972 0 0 0 .021 7.395c-.023.31.21.58.519.603a.564.564 0 0 0 .603-.519c.13-1.724.9-3.326 2.169-4.509a6.854 6.854 0 0 1 4.687-1.845c1.36 0 2.674.395 3.8 1.145a6.87 6.87 0 0 1 2.325 2.605h-1.562a.562.562 0 1 0 0 1.125h2.875c.31 0 .562-.252.562-.563V2.563A.562.562 0 0 0 15.437 2Zm.02 6.002a.563.563 0 0 0-.602.518 6.853 6.853 0 0 1-2.17 4.51A6.847 6.847 0 0 1 8 14.875a6.833 6.833 0 0 1-3.8-1.145 6.87 6.87 0 0 1-2.325-2.605h1.563a.562.562 0 1 0 0-1.125H.562a.562.562 0 0 0-.562.563v2.875a.562.562 0 1 0 1.125 0v-1.344a8.017 8.017 0 0 0 2.452 2.573A7.966 7.966 0 0 0 8 16a7.97 7.97 0 0 0 5.453-2.147 7.963 7.963 0 0 0 2.524-5.248.562.562 0 0 0-.52-.603ZM7.103 6.556c0 .422.325.693 1.334.955 1.008.262 2.088.693 2.088 1.956 0 .91-.688 1.413-1.555 1.577V12H7.636v-.964c-.854-.183-1.583-.73-1.636-1.703h.978c.049.525.409.934 1.324.934.982 0 1.2-.49 1.2-.796 0-.413-.222-.804-1.333-1.071-1.24-.298-2.089-.809-2.089-1.836 0-.857.693-1.417 1.556-1.604V4h1.333v.973c.929.227 1.395.93 1.427 1.694h-.983c-.026-.556-.32-.934-1.11-.934-.752 0-1.2.338-1.2.823Z"
                fill="#96C8FF"
              />
            </svg>
          </div>
          {/* Case Master Flag */}
          <div className='ml-2'>
            {masterFlag === FLAG_VALUE.success && SUCCESS_ICON}
            {masterFlag === FLAG_VALUE.warning && BELL_ICON}
            {masterFlag === FLAG_VALUE.critical && CRITIAL_ICON}
          </div>
          {/* Header Popup Menu */}
          <div
            className={`ml-3 ${css(styles.ellipsisBtn)}`}
            ref={headerMenuRef}
          >
            <i
              className='fa fa-ellipsis-h'
              onClick={() => {
                setShowHeaderMenu(true);
              }}
            />
            {showHeaderMenu &&
              <PopupMenu
                top={32}
                left={0}
                zIndex='1550'
                position="absolute"
                visible={showHeaderMenu}
                parent={headerMenuRef}
                onOutsideClick={() => { setShowHeaderMenu(false); }}
              >
                <div
                  className={css(styles.searchPopupMenuContainer)}
                  style={{
                    width: '140px'
                  }}
                >
                  {/* Save Button */}
                  <div
                    className={`pl-2 fx fx__v-center ${css(styles.menuItem)}`}
                    style={{ height: '32px' }}
                    onClick={() => {
                      setShowHeaderMenu(false);
                      if (caseTabRef.current) {
                        caseTabRef.current.updateCaseData(true, false);
                      } else {
                        console.log('❌ caseTabRef is null!');
                      }
                    }}
                  >
                    Save
                  </div>
                  {/* Save and Close */}
                  <div
                    className={`pl-2 fx fx__v-center ${css(styles.menuItem)}`}
                    style={{ height: '32px' }}
                    onClick={() => {
                      setShowHeaderMenu(false);
                      if (caseTabRef.current) {
                        // caseTabRef.current.updateCaseData(true, false);
                        caseTabRef.current.updateCaseData();
                        console.log('✅ Case data updated successfully!');
                      } else {
                        console.log('❌ caseTabRef is null!');
                      }
                      // history.push('/schedule');
                    }}
                  >
                    Save &amp; Close
                  </div>
                </div>
              </PopupMenu>
            }
          </div>
          {/* Collapse icon */}
          <div
            className='ml-2' style={{ height: '24px' }}
            onClick={() => {
              if (shouldSaveCase) {
                setShowDiscardDialog1(true);
              } else {
                history.push('/admin/cases');
              }
            }}
          >
            <svg
              width="24"
              height="24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M21.6 5.053h-4.8V2.526C16.8 1.124 15.732 0 14.4 0H9.6C8.268 0 7.2 1.124 7.2 2.526v2.527H2.4C1.068 5.053.012 6.177.012 7.579L0 21.474C0 22.876 1.068 24 2.4 24h19.2c1.332 0 2.4-1.124 2.4-2.526V7.579c0-1.402-1.068-2.526-2.4-2.526ZM14.4 4.8H9.6V2.4h4.8v2.4Zm-.89 9.353h4.304a.59.59 0 1 0 0-1.179h-2.831l3.112-3.107a.588.588 0 0 0-.417-1.006.587.587 0 0 0-.417.173l-3.16 3.157V9.386a.59.59 0 0 0-1.18 0v4.18a.587.587 0 0 0 .588.587ZM6.992 16.62h2.832l-3.112 3.109a.59.59 0 0 0 .834.833l3.162-3.157v2.805a.59.59 0 0 0 1.18 0v-4.18a.59.59 0 0 0-.59-.589H6.995a.59.59 0 0 0-.002 1.18Z"
                fill="#96C8FF"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Discad Dialog 1 */}
      {showDisacardDialog1 &&
        <DialogBox
          showCloseBtn={false}
          initWidth={DIALOG_WIDTH}
          top={window.innerWidth <= 576 ? 48 : null}
          left={window.innerWidth <= 576 ? 8 : null}
          initHeight={265}
          isOpen={showDisacardDialog1}
          onClose={() => {
            setShowDiscardDialog1(false);
          }}
          maskBackground="#004080"
          maskOpacity={0.5}
          closeOnEscKey
          closeOnMaskClick={false}
          header={{
            overrideTheme: true,
            text: 'Warning! You Have Unsaved Changes!',
            backColor: '#082840',
            foreColor: 'yellow',
            closeColor: '#FF6060',
            fontStyle: 'bold',
            fontSize: '14px',
            icon: 'fa fa-exclamation-triangle'
          }}
        >
          <div className='p-2'>
            <div className='p-2 text-center'>
              What do you want to do?
            </div>
            {/* Save Button */}
            <Button
              className='mt-2'
              btnType='primary'
              style={{
                height: '47px',
                width: '334px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}
              onClick={() => {
                if (caseTabRef.current) {
                  caseTabRef.current.updateCaseData();
                }
              }}
            >
              Save
            </Button>
            {/* Do tnot save Button */}
            <Button
              className='mt-4'
              btnType='danger'
              style={{
                height: '32px',
                width: '334px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => {
                setShowDiscardDialog1(false);
                setShowDiscardDialog2(true);
              }}
            >
              Do Not Save
            </Button>
            {/* Cancel Button */}
            <Button
              className='mt-4'
              btnType='primary'
              isOutlineBtn
              style={{
                border: `1px solid ${gray[400]}`,
                color: gray[300],
                height: '32px',
                width: '334px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => setShowDiscardDialog1(false)}
            >
              Cancel
            </Button>
          </div>
        </DialogBox>
      }

      {/* Discad Dialog 2 */}
      {showDisacardDialog2 &&
        <DialogBox
          showCloseBtn={false}
          initWidth={DIALOG_WIDTH}
          top={window.innerWidth <= 576 ? 48 : null}
          left={window.innerWidth <= 576 ? 8 : null}
          initHeight={265}
          isOpen={showDisacardDialog2}
          onClose={() => {
            setShowDiscardDialog2(false);
          }}
          maskBackground="#004080"
          maskOpacity={0.5}
          closeOnEscKey
          closeOnMaskClick={false}
          header={{
            overrideTheme: true,
            text: 'Really?',
            backColor: '#082840',
            foreColor: 'yellow',
            closeColor: '#FF6060',
            fontStyle: 'bold',
            fontSize: '14px',
            icon: 'fa fa-exclamation-triangle'
          }}
        >
          <div className='p-2'>
            <div className='p-2 text-center'>
              Are you 100% sure?
            </div>
            {/* Save Button */}
            <Button
              className='mt-2'
              btnType='primary'
              style={{
                height: '47px',
                width: '334px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}
              onClick={() => {
                if (caseTabRef.current) {
                  caseTabRef.current.updateCaseData();
                }
              }}
            >
              Save
            </Button>
            {/* Do not save Button */}
            <Button
              className='mt-4'
              btnType='danger'
              style={{
                height: '32px',
                width: '334px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => {
                setShowDiscardDialog2(false);
                history.push('/admin/cases');
              }}
            >
              Do Not Save... I am 100% sure
            </Button>
            {/* Cancel Button */}
            <Button
              className='mt-4'
              btnType='primary'
              isOutlineBtn
              style={{
                border: `1px solid ${gray[400]}`,
                color: gray[300],
                height: '32px',
                width: '334px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => setShowDiscardDialog2(false)}
            >
              Cancel
            </Button>
          </div>
        </DialogBox>
      }
      {/* ------------------------ Main Dashboard Container ------------------------ */}
      <div className={css(styles.sectionsContainer)}>
        {/* ------------------------ Case Dcuments Section ------------------------ */}
        <div
          className='noselect'
          style={{
            minWidth: !isMobileLayout ? `${docsWidth}px` : '100%',
            maxWidth: !isMobileLayout ? `${docsWidth}px` : '100%',
            flexGrow: '1',
            position: 'relative'
          }}
        >
          {/* ------ Patient Vitals & Card Section ------ */}
          <div
            className={css(styles.patientCardSection)}
          // style={{ maxWidth: '1024px' }}
          >
            {/* Patient Vitals */}
            {docsWidth >= 700 &&
              <div
                style={{
                  flexGrow: '1',
                  // background: 'yellow',
                  background: blue[900],
                  height: '120px',
                  padding: '8px'
                }}
              >
                Patient Vitals
              </div>
            }
            {/* Patient Card */}
            <PatientCard
              onClickCase={() => { console.log('Not implemented yet!'); }}
              flagType={masterFlag}
              patientData={{
                dos: patientDos,
                patientType: patientAdmType,
                facilityName: patientFacility,
                surgeons: patientSurgeons,
                anesthesia: patientAnesthesia,
                admDx: patientPreOpDx,
                surgicalProcedures: patientSurgicalProcedures,
                firstName: patientData && patientData.firstName ? patientData.firstName : '',
                middleName: patientData && patientData.middleName ? patientData.middleName : '',
                lastName: patientData && patientData.lastName ? patientData.lastName : '',
                gender: patientData && patientData.gender ? patientData.gender : '',
                dob: patientData && patientData.dob ? patientData.dob : ''
              }}
            />
          </div>
          {/* Tab Section */}
          <div
            className={css(styles.tabSection)}
            style={{
              height: pathname.endsWith('faceSheet') || pathname.endsWith('nursing') || pathname.endsWith('discharge') || pathname.endsWith('surgeon')
                ? '40px'
                : '68px'
            }}
          >
            {/* Tab section - TOP */}
            <div className={css(styles.tabSectionTop)}>
              <TabsContainer
                dropDownId="d2"
                showPlusIcon={false}
                isDrawer={false}
                shiftMethod='minWidth'
                minWidth={610}
                containerStyles={{ width: '100%' }}
              >
                {/* Face Sheet */}
                <TabItem
                  key='FaceSheet_Tab'
                  tabKey="1"
                  parentKey="1212"
                  text="Face Sheet"
                  isActive={pathname.endsWith('faceSheet') || pathname.endsWith('faceSheet/')}
                  showTabIcon
                  showSvgIcon
                  svgIcon={(() => {
                    if (Object.keys(caseData).length > 0) {
                      if (caseData.isNoneFaceSheet) {
                        return IS_NONE_ICON;
                      }
                      if (caseData.faceSheetDocs.length > 0 || caseData.isNoneFaceSheet) {
                        return SUCCESS_ICON;
                      }
                      return CRITIAL_ICON;
                    }
                    return CRITIAL_ICON;
                  })()}
                  style={{
                    width: '108px',
                    minWidth: '108px',
                    maxWidth: '108px',
                    // background: pathname.endsWith('faceSheet') || pathname.endsWith('faceSheet/') ? '#184878' : '#083050',
                    background: activeDrawerTab === 'faceSheet' ? '#184878' : '#083050',
                    height: '32px',
                    borderBottom: 'none',
                    borderLeft: '1px solid black',
                    borderRight: '1px solid black',
                    marginRight: '0'
                  }}
                  textStyles={tabsTextStyles}
                  onActivate={() => {
                    setActiveDrawerTab('faceSheet');
                    const updatedCaseData = clone(caseData);
                    updatedCaseData.activeDrawerTab = 'faceSheet';
                    setCaseData(updatedCaseData);
                    history.push(`${url}/manage/faceSheet`);
                    // setActiveDrawerTab(() => {
                    //   const updatedCaseData = clone(caseData);
                    //   updatedCaseData.activeDrawerTab = 'faceSheet';
                    //   setCaseData(updatedCaseData);
                    //   history.push(`${url}/manage/faceSheet`);
                    // });
                  }}
                >
                  FaceSheet
                </TabItem>
                {/* Records */}
                <TabItem
                  key='Records_Tab'
                  tabKey="2"
                  parentKey="1313"
                  text="Records"
                  isActive={pathname.endsWith('records') || pathname.endsWith('records/')}
                  showTabIcon
                  showSvgIcon
                  svgIcon={(() => {
                    if (Object.keys(caseData).length > 0) {
                      if (caseData.isNoneLabDocs && caseData.isNoneEkgDocs && caseData.isNoneImagingDocs && caseData.isNoneConsults) {
                        return IS_NONE_ICON;
                      }

                      const labsFlag = caseData.labsDocs.length > 0 || caseData.isNoneLabDocs;
                      const ekgFlag = caseData.ekgDocs.length > 0 || caseData.isNoneEkgDocs;
                      const imagingFlag = caseData.imagingDocs.length > 0 || caseData.isNoneImagingDocs;
                      const consultsFlag = caseData.consultsDocs.length > 0 || caseData.isNoneConsults;

                      if (labsFlag && ekgFlag && imagingFlag && consultsFlag) {
                        return SUCCESS_ICON;
                      }
                      return CRITIAL_ICON;
                    } else {
                      return CRITIAL_ICON;
                    }
                  })()}
                  style={{
                    width: '108px',
                    minWidth: '108px',
                    maxWidth: '108px',
                    // background: pathname.endsWith('records') || pathname.endsWith('records/') ? '#184878' : '#083050',
                    background: activeDrawerTab === 'records' ? '#184878' : '#083050',
                    height: '32px',
                    borderBottom: 'none',
                    borderLeft: '1px solid black',
                    borderRight: '1px solid black',
                    marginRight: '0'
                  }}
                  textStyles={tabsTextStyles}
                  onActivate={() => {
                    setActiveDrawerTab('records');
                    history.push(`${url}/manage/records`);
                  }}
                >
                  Records
                </TabItem>
                {/* Consents */}
                <TabItem
                  key='Consents_main_Tab'
                  tabKey="1"
                  parentKey="1214"
                  text="Consents"
                  isActive={pathname.endsWith('consents') || pathname.endsWith('consents/')}
                  showTabIcon
                  showSvgIcon
                  svgIcon={(() => {
                    if (Object.keys(caseData).length > 0) {
                      if (caseData.isNoneConsentSurgFormDocs && caseData.isNoneConsentFormDocs) {
                        return IS_NONE_ICON;
                      }

                      const surgFlag = caseData.consentSurgFormDocs.length > 0 || caseData.isNoneConsentSurgFormDocs;
                      const anesFlag = caseData.consentFormDocs.length > 0 || caseData.isNoneConsentFormDocs;

                      if (surgFlag && anesFlag) {
                        return SUCCESS_ICON;
                      }
                      return CRITIAL_ICON;
                    } else {
                      return CRITIAL_ICON;
                    }
                  })()}
                  style={{
                    width: '108px',
                    minWidth: '108px',
                    maxWidth: '108px',
                    // background: pathname.endsWith('consents') || pathname.endsWith('consents/') ? '#184878' : '#083050',
                    background: activeDrawerTab === 'consents' ? '#184878' : '#083050',
                    height: '32px',
                    borderBottom: 'none',
                    borderLeft: '1px solid black',
                    borderRight: '1px solid black',
                    marginRight: '0'
                  }}
                  textStyles={tabsTextStyles}
                  onActivate={() => {
                    setActiveDrawerTab('consents');
                    history.push(`${url}/manage/consents`);
                  }}
                >
                  Consents
                </TabItem>
                {/* Nursing */}
                <TabItem
                  key='Nursing_Tab'
                  tabKey="3"
                  parentKey="1414"
                  text="Nursing"
                  isActive={pathname.endsWith('nursing') || pathname.endsWith('nursing/')}
                  showTabIcon
                  showSvgIcon
                  svgIcon={(() => {
                    if (Object.keys(caseData).length > 0) {
                      if (caseData.isNoneNursingDocs) {
                        return IS_NONE_ICON;
                      }
                      if (caseData.nursingDocs.length > 0 || caseData.isNoneNursingDocs) {
                        return SUCCESS_ICON;
                      }
                      return CRITIAL_ICON;
                    } else {
                      return CRITIAL_ICON;
                    }
                  })()}
                  style={{
                    width: '108px',
                    minWidth: '108px',
                    maxWidth: '108px',
                    // background: pathname.endsWith('nursing') || pathname.endsWith('nursing/') ? '#184878' : '#083050',
                    background: activeDrawerTab === 'nursing' ? '#184878' : '#083050',
                    height: '32px',
                    borderBottom: 'none',
                    borderLeft: '1px solid black',
                    borderRight: '1px solid black',
                    marginRight: '0'
                  }}
                  textStyles={tabsTextStyles}
                  onActivate={() => {
                    setActiveDrawerTab('nursing');
                    history.push(`${url}/manage/nursing`);
                  }}
                >
                  Nursing
                </TabItem>
                {/* Surgeon */}
                <TabItem
                  key='Surgeon_Tab'
                  tabKey="2222222222"
                  parentKey="2113333"
                  text="Surgeon"
                  isActive={pathname.endsWith('surgeon') || pathname.endsWith('surgeon/')}
                  showTabIcon
                  showSvgIcon
                  svgIcon={(() => {
                    if (Object.keys(caseData).length > 0) {
                      if (caseData.isNoneSurgeonDocs) {
                        return IS_NONE_ICON;
                      }
                      if (caseData.surgeonDocs.length > 0 || caseData.isNoneSurgeonDocs) {
                        return SUCCESS_ICON;
                      }
                      return CRITIAL_ICON;
                    } else {
                      return CRITIAL_ICON;
                    }
                  })()}
                  style={{
                    width: '108px',
                    minWidth: '108px',
                    maxWidth: '108px',
                    // background: pathname.endsWith('surgeon') || pathname.endsWith('surgeon/') ? '#184878' : '#083050',
                    background: activeDrawerTab === 'surgeon' ? '#184878' : '#083050',
                    height: '32px',
                    borderBottom: 'none',
                    borderLeft: '1px solid black',
                    borderRight: '1px solid black',
                    marginRight: '0'
                  }}
                  textStyles={tabsTextStyles}
                  onActivate={() => {
                    setActiveDrawerTab('surgeon');
                    history.push(`${url}/manage/surgeon`);
                  }}
                >
                  Surgeon
                </TabItem>
                {/* Anesthesia */}
                <TabItem
                  key='Anesthesia_main_Tab'
                  tabKey="1"
                  parentKey="1217"
                  text="Anesthesia"
                  isActive={pathname.endsWith('anesthesia') || pathname.endsWith('anesthesia/')}
                  showTabIcon
                  showSvgIcon
                  svgIcon={(() => {
                    if (Object.keys(caseData).length > 0) {
                      // PreOp Tab Flag
                      let preOpFlagValue = FLAG_VALUE.critical;
                      if (caseData.isNonePreOpFormDocs) {
                        preOpFlagValue = FLAG_VALUE.none;
                      } else {
                        if (caseData.preOpFormDocs.length > 0) {
                          preOpFlagValue = FLAG_VALUE.success;
                        } else {
                          preOpFlagValue = FLAG_VALUE.critical;
                        }
                      }

                      // IntraOp Tab Flag
                      let intraOpFlagValue = FLAG_VALUE.critical;
                      let anesthesiaDataFlagValue = FLAG_VALUE.critical;
                      if (caseData.asaScore.length > 0 && caseData.anesthesiaTypes.length > 0 && caseData.anesthesiaModifiers.length > 0) {
                        anesthesiaDataFlagValue = FLAG_VALUE.success;
                      }
                      if (anesthesiaDataFlagValue === FLAG_VALUE.critical) {
                        intraOpFlagValue = anesthesiaDataFlagValue;
                      } else {
                        if (caseData.isNoneIntraOpFormDocs) {
                          intraOpFlagValue = FLAG_VALUE.none;
                        } else {
                          if (caseData.intraOpFormDocs.length > 0) {
                            intraOpFlagValue = FLAG_VALUE.success;
                          } else {
                            intraOpFlagValue = FLAG_VALUE.critical;
                          }
                        }
                      }

                      // PostOp Tab Flag
                      let postOpFlagValue = FLAG_VALUE.critical;
                      if (caseData.isNonePostOpFormDocs) {
                        postOpFlagValue = FLAG_VALUE.none;
                      } else {
                        if (caseData.postOpFormDocs.length > 0) {
                          postOpFlagValue = FLAG_VALUE.success;
                        } else {
                          postOpFlagValue = FLAG_VALUE.critical;
                        }
                      }

                      // Procedures Tab Flag
                      // anesthesiaProcedures
                      let proceduresFlagValue = FLAG_VALUE.critical;
                      if (caseData.isNoneAnesthesiaProcedure) {
                        proceduresFlagValue = FLAG_VALUE.none;
                      } else {
                        if (caseData.anesthesiaProcedures && Array.isArray(caseData.anesthesiaProcedures)) {
                          if (caseData.anesthesiaProcedures.length === 0) {
                            proceduresFlagValue = FLAG_VALUE.critical;
                          } else if (caseData.anesthesiaProcedures.length > 0 && (caseData.anesthesiaProcedures.filter(el => el.npi === 0)).length > 0) {
                            proceduresFlagValue = FLAG_VALUE.warning;
                          } else if (caseData.anesthesiaProcedures.length > 0 && (caseData.anesthesiaProcedures.filter(el => el.npi === 0)).length === 0) {
                            proceduresFlagValue = FLAG_VALUE.success;
                          } else {
                            proceduresFlagValue = FLAG_VALUE.critical;
                          }
                        }
                      }

                      const anesthesiaTabFlag = findFlag(preOpFlagValue, intraOpFlagValue, postOpFlagValue, proceduresFlagValue);
                      switch (anesthesiaTabFlag) {
                        case FLAG_VALUE.critical:
                          return CRITIAL_ICON;
                        case FLAG_VALUE.warning:
                          return BELL_ICON;
                        case FLAG_VALUE.success:
                          return SUCCESS_ICON;
                        case FLAG_VALUE.none:
                          return IS_NONE_ICON;
                        default:
                          return CRITIAL_ICON;
                      }
                    } else {
                      return CRITIAL_ICON;
                    }
                  })()}
                  style={{
                    width: '108px',
                    minWidth: '108px',
                    maxWidth: '108px',
                    // background: pathname.endsWith('anesthesia') || pathname.endsWith('anesthesia/') ? '#184878' : '#083050',
                    background: activeDrawerTab === 'anesthesia' ? '#184878' : '#083050',
                    height: '32px',
                    borderBottom: 'none',
                    borderLeft: '1px solid black',
                    borderRight: '1px solid black',
                    marginRight: '0'
                  }}
                  textStyles={tabsTextStyles}
                  onActivate={() => {
                    setActiveDrawerTab('anesthesia');
                    history.push(`${url}/manage/anesthesia`);
                  }}
                >
                  Anesthesia
                </TabItem>
                {/* Orders */}
                <TabItem
                  key='Orders_Tab'
                  tabKey="12"
                  parentKey="2112"
                  text="Orders"
                  isActive={pathname.endsWith('orders') || pathname.endsWith('orders/')}
                  showTabIcon
                  showSvgIcon
                  svgIcon={(() => {
                    if (Object.keys(caseData).length > 0) {
                      if (caseData.isNoneOrdersPreOpDocs && caseData.isNoneOrdersIntraOpDocs && caseData.isNoneOrdersPostOpDocs) {
                        return IS_NONE_ICON;
                      }
                      const preOPFlag = caseData.ordersPreOpDocs.length > 0 || caseData.isNoneOrdersPreOpDocs;
                      const intraOpFlag = caseData.ordersIntraOpDocs.length > 0 || caseData.isNoneOrdersIntraOpDocs;
                      const postOpFlag = caseData.ordresPostOpDocs.length > 0 || caseData.isNoneOrdersPostOpDocs;

                      if (preOPFlag && intraOpFlag && postOpFlag) {
                        return SUCCESS_ICON;
                      }
                      return CRITIAL_ICON;
                    } else {
                      return CRITIAL_ICON;
                    }
                  })()}
                  style={{
                    width: '108px',
                    minWidth: '108px',
                    maxWidth: '108px',
                    // background: pathname.endsWith('orders') || pathname.endsWith('orders/') ? '#184878' : '#083050',
                    background: activeDrawerTab === 'orders' ? '#184878' : '#083050',
                    height: '32px',
                    borderBottom: 'none',
                    borderLeft: '1px solid black',
                    borderRight: '1px solid black',
                    marginRight: '0'
                  }}
                  textStyles={tabsTextStyles}
                  onActivate={() => {
                    setActiveDrawerTab('orders');
                    history.push(`${url}/manage/orders`);
                  }}
                >
                  Orders
                </TabItem>
                {/* Discharge */}
                <TabItem
                  key='Discharge_Tab'
                  tabKey="13"
                  parentKey="2113"
                  text="Orders"
                  isActive={pathname.endsWith('discharge') || pathname.endsWith('discharge/')}
                  showTabIcon
                  showSvgIcon
                  svgIcon={(() => {
                    if (Object.keys(caseData).length > 0) {
                      if (caseData.isNoneDischargeDocs) {
                        return IS_NONE_ICON;
                      }
                      if (caseData.dischargeDocs.length > 0 || caseData.isNoneDischargeDocs) {
                        return SUCCESS_ICON;
                      }
                      return CRITIAL_ICON;
                    } else {
                      return CRITIAL_ICON;
                    }
                  })()}
                  style={{
                    width: '108px',
                    minWidth: '108px',
                    maxWidth: '108px',
                    // background: pathname.endsWith('discharge') || pathname.endsWith('discharge/') ? '#184878' : '#083050',
                    background: activeDrawerTab === 'discharge' ? '#184878' : '#083050',
                    height: '32px',
                    borderBottom: 'none',
                    borderLeft: '1px solid black',
                    borderRight: '1px solid black',
                    marginRight: '0'
                  }}
                  textStyles={tabsTextStyles}
                  onActivate={() => {
                    setActiveDrawerTab('discharge');
                    history.push(`${url}/manage/discharge`);
                  }}
                >
                  Discharge
                </TabItem>
              </TabsContainer>
            </div>
            {/* Tab section - BOTTOM */}
            <div
              ref={mainTabsContainer}
              style={{
                paddingLeft: '4px',
                borderBottom: '1px solid #000',
                display: pathname.endsWith('faceSheet') || pathname.endsWith('nursing') || pathname.endsWith('discharge') || pathname.endsWith('surgeon')
                  ? 'none'
                  : ''
              }}
            >
              {tabs}
            </div>
          </div>
          {/* Active Component */}
          {isLoadingData
            ? <Loader iconType="cog" iconSize="5x" text="Loading Case Data" />
            : <div
              className={css(styles.activeComponentContainer)}
              style={{ display: 'flex', width: `${docsWidth}px`, maxWidth: `${docsWidth}px` }}
            >
              {activeDrawerTab === 'faceSheet' &&
                <AnesthesiaForms
                  formType='Face Sheet'
                  isLoadingData={isLoadingData}
                  caseData={caseData || {}}
                  subTab='faceSheet'
                  onChangeIsNone={handleOnChangeIsNoneFaceSheet}
                  onCaseDocUploaded={handleOnFaceSheetDocsUploaded}
                  onDeletePage={handleOnDeleteFaceSheetPage}
                  onDeleteDocument={handleOnDeleteFaceSheetDocument}
                />
              }

              {activeDrawerTab === 'records' && recordsActiveSubTab === 'labs' &&
                <AnesthesiaForms
                  formType='Labs'
                  isLoadingData={isLoadingData}
                  caseData={caseData || {}}
                  subTab='labs'
                  onChangeIsNone={handleOnChangeIsNoneLabsDocs}
                  onCaseDocUploaded={handleOnLabsDocsUploaded}
                  onDeletePage={handleOnDeleteLabsDocsPage}
                  onDeleteDocument={handleOnDeleteLabsDocsDocument}
                />
              }
              {activeDrawerTab === 'records' && recordsActiveSubTab === 'ekg' &&
                <AnesthesiaForms
                  formType='EKG'
                  isLoadingData={isLoadingData}
                  caseData={caseData || {}}
                  subTab='ekg'
                  onChangeIsNone={handleOnChangeIsNoneEkgDocs}
                  onCaseDocUploaded={handleOnEkgDocsUploaded}
                  onDeletePage={handleOnDeleteEkgDocsPage}
                  onDeleteDocument={handleOnDeleteEkgDocsDocument}
                />
              }
              {activeDrawerTab === 'records' && recordsActiveSubTab === 'imaging' &&
                <AnesthesiaForms
                  formType='Imaging'
                  isLoadingData={isLoadingData}
                  caseData={caseData || {}}
                  subTab='imaging'
                  onChangeIsNone={handleOnChangeIsNoneImagingDocs}
                  onCaseDocUploaded={handleOnImagingDocsUploaded}
                  onDeletePage={handleOnDeleteImagingDocsPage}
                  onDeleteDocument={handleOnDeleteImagingDocsDocument}
                />

              }
              {activeDrawerTab === 'records' && recordsActiveSubTab === 'consults' &&
                <AnesthesiaForms
                  formType='Consults'
                  isLoadingData={isLoadingData}
                  caseData={caseData || {}}
                  subTab='consults'
                  onChangeIsNone={handleOnChangeIsNoneConsultsDocs}
                  onCaseDocUploaded={handleOnConsultsDocsUploaded}
                  onDeletePage={handleOnDeleteConsultsDocsPage}
                  onDeleteDocument={handleOnDeleteConsultsDocsDocument}
                />
              }

              {activeDrawerTab === 'consents' && consentActiveTab === 'anesthesia' &&
                <AnesthesiaForms
                  isLoadingData={isLoadingData}
                  caseData={caseData || {}}
                  subTab='consent_anethesia'
                  formType='Anesthsia Consent'
                  onChangeIsNone={handleOnChangeIsNoneConsentForm}
                  onCaseDocUploaded={handleOnConsentFormDocsUploaded}
                  onDeletePage={handleOnDeleteConsentFormDocPage}
                  onDeleteDocument={handleOnDeleteConsentFormDocDocument}
                />
              }

              {activeDrawerTab === 'consents' && consentActiveTab === 'surgery' &&
                <AnesthesiaForms
                  isLoadingData={isLoadingData}
                  caseData={caseData || {}}
                  subTab='consent_surgery'
                  formType='Surgery Consent'
                  onChangeIsNone={handleOnChangeIsNoneConsentSurgForm}
                  onCaseDocUploaded={handleOnConsentSurgFormDocsUploaded}
                  onDeletePage={handleOnDeleteConsentSurgFormDocPage}
                  onDeleteDocument={handleOnDeleteConsentSurgFormDocDocument}
                />
              }

              {activeDrawerTab === 'nursing' &&
                <AnesthesiaForms
                  formType='Nursing'
                  isLoadingData={isLoadingData}
                  caseData={caseData || {}}
                  subTab='nursing'
                  onChangeIsNone={handleOnChangeIsNoneNursingDocs}
                  onCaseDocUploaded={handleOnNursingDocsUploaded}
                  onDeletePage={handleOnDeleteNursingDocsPage}
                  onDeleteDocument={handleOnDeleteNursingDocsDocument}
                />
              }

              {activeDrawerTab === 'surgeon' &&
                <AnesthesiaForms
                  formType='Surgeon'
                  isLoadingData={isLoadingData}
                  caseData={caseData || {}}
                  subTab='surgeon'
                  onChangeIsNone={handleOnChangeIsNoneSurgeonDocs}
                  onCaseDocUploaded={handleOnSurgeonDocsUploaded}
                  onDeletePage={handleOnDeleteSurgeonDocsPage}
                  onDeleteDocument={handleOnDeleteSurgeonDocsDocument}
                />
              }

              {activeDrawerTab === 'anesthesia' && anesthesiaActiveTab === 'preOp' &&
                <AnesthesiaForms
                  formType='PreOp Form'
                  isLoadingData={isLoadingData}
                  caseData={caseData || {}}
                  subTab='anesthesia_preOp'
                  onChangeIsNone={handleOnChangeIsNonePreOpForm}
                  onCaseDocUploaded={handleOnPreOpFormDocsUploaded}
                  onDeletePage={handleOnDeletePreOpFormmDocPage}
                  onDeleteDocument={handleOnDeletePreOpFormmDocDocument}
                />
              }
              {activeDrawerTab === 'anesthesia' && anesthesiaActiveTab === 'intraOp' &&
                <IntraOp
                  showFlag
                  asaScore={caseData.asaScore}
                  subTab='anesthesia_intraOp'
                  onChangeAsaScore={(asa) => {
                    setAsaScore(asa);
                  }}
                  anesthesiaTypes={caseData.anesthesiaTypes}
                  onChangeAnesthesiaTypes={(data) => {
                    setAnesthesiaTypes(data);
                  }}
                  anesthesiaModifiers={caseData.anesthesiaModifiers}
                  onChangeAnesthesiaModifiers={(data) => {
                    setAnesthesiaModifiers(data);
                  }}
                  formType='Intra Op Form'
                  flagType={FLAG_VALUE.critical}
                  caseDocs={caseData.intraOpFormDocs}
                  isNone={caseData.isNoneIntraOpFormDocs}
                  onChangeIsNone={handleOnChangeIsNoneIntraOpForm}
                  onCaseDocUploaded={handleOnIntraOpFormDocsUploaded}
                  onDeletePage={handleOnDeleteIntraOpFormmDocPage}
                  onDeleteDocument={handleOnDeleteIntraOpFormmDocDocument}
                />
              }

              {activeDrawerTab === 'anesthesia' && anesthesiaActiveTab === 'postOp' &&
                <AnesthesiaForms
                  formType='Post Op Form'
                  isLoadingData={isLoadingData}
                  caseData={caseData || {}}
                  subTab='anesthesia_postOp'
                  onChangeIsNone={handleOnChangeIsNonePostOpForm}
                  onCaseDocUploaded={handleOnPostOpFormDocsUploaded}
                  onDeletePage={handleOnDeletePostOpFormmDocPage}
                  onDeleteDocument={handleOnDeletePostOpFormmDocDocument}
                />
              }

              {activeDrawerTab === 'anesthesia' && anesthesiaActiveTab === 'procedures' &&
                <div
                  className='m-2'
                  style={{ width: `${DRAWER_WIDTH}px` }}
                >
                  <div
                    className='mt-4 mb-2 fx fx__v-center'
                    style={{
                      width: '355px',
                      justifyContent: 'space-between'
                    }}
                  >
                    <CaseField
                      heading='Anesthesia Procedures'
                      showNoneFlag={caseData.isNoneAnesthesiaProcedure}
                      showAlert={(() => {
                        if (!caseData.isNoneAnesthesiaProcedure) {
                          if (caseData.anesthesiaProcedures.length === 0) {
                            return true;
                          }
                          return false;
                        }
                      })()}
                      showIncomplete={(() => {
                        if (caseData.anesthesiaProcedures.length > 0 && (caseData.anesthesiaProcedures.filter(el => el.npi === 0)).length > 0) {
                          return true;
                        } else {
                          return false;
                        }
                      })()}
                      showPlusIcon={false}
                      className='ml-1'
                    />
                    <Checkbox
                      text='None'
                      isChecked={caseData.isNoneAnesthesiaProcedure}
                      onChange={() => {
                        handleOnCaseDataChange();
                        setCaseData((prvState) => ({
                          ...prvState,
                          isNoneAnesthesiaProcedure: !prvState.isNoneAnesthesiaProcedure,
                          anesthesiaProcedures: []
                        }));
                      }}
                    />
                  </div>
                  {/* Anesthesia procedure value textbox & search menu */}
                  <div
                    ref={procedureSearchMenuRef}
                    className='mt-0'
                    style={{
                      borderRadius: '4px',
                      position: 'relative'
                    }}
                  >
                    <TextInput
                      value={anesthesiaProcedureValue}
                      isDisabled={caseData.isNoneAnesthesiaProcedure}
                      placeholder="Search Anesthesia Procedure(s)"
                      disabled={caseData.isNoneAnesthesiaProcedure}
                      style={{
                        width: `${DRAWER_WIDTH}px`,
                        fontSize: '14px',
                        padding: '0 4px',
                        color: blue[200],
                        borderRadius: '4px'
                      }}
                      onChange={(e) => {
                        const { value } = e.target;
                        setAnesthesiaProcedureValue(value);
                      }}
                    />
                    {showAnesthesiaProceduresSearchMenu &&
                      <PopupMenu
                        top={35}
                        left={0}
                        zIndex='1550'
                        position="absolute"
                        visible={showAnesthesiaProceduresSearchMenu}
                        parent={procedureSearchMenuRef}
                        onOutsideClick={() => { setShowAnesthesiaProceduresSearchMenu(false); }}
                      >
                        <div className={css(styles.searchPopupMenuContainer)}>
                          {procedureSearchItems.length > 0 &&
                            <div className={`pl-2 fx fx__v-center ${css(styles.suggResults)}`}>
                              Suggested Results
                            </div>
                          }
                          {procedureSearchItems}
                        </div>
                      </PopupMenu>
                    }
                  </div>
                  {/* Surgical Procedures Cards */}
                  {caseData.anesthesiaProcedures.map(dx => {
                    return (
                      <CaseUserCard
                        className='mt-1'
                        avatarImageUrl={null}
                        showCloseIcon
                        noAvatar
                        key={dx.id || dx._id}
                        dataId={dx.id || dx._id}
                        onClose={(id) => {
                          handleOnCaseDataChange();
                          let updatedAnesProcs = clone(caseData.anesthesiaProcedures);
                          updatedAnesProcs = updatedAnesProcs.filter((el) => {
                            if (el.id) {
                              return el.id !== id;
                            }
                            return el._id !== id;
                          });
                          setCaseData(prvState => {
                            return {
                              ...prvState,
                              anesthesiaProcedures: updatedAnesProcs
                            };
                          });
                        }}
                      >
                        <div className='ml-2'>
                          {dx.npi === 0 &&
                            <i
                              className='font-16 mr-2 fa fa-bell'
                              style={{ color: salmon[500] }}
                            />
                          }
                          <span>
                            {dx.description}
                          </span>
                        </div>
                      </CaseUserCard>
                    );
                  })}
                </div>
              }

              {activeDrawerTab === 'orders' && ordersActiveSubTab === 'preOp' &&
                <AnesthesiaForms
                  formType='Orders Pre Op'
                  isLoadingData={isLoadingData}
                  caseData={caseData || {}}
                  subTab='orders_preOp'
                  onChangeIsNone={handleOnChangeIsNoneOrdersPreOpForm}
                  onCaseDocUploaded={handleOnOrdersPreOpFormDocsUploaded}
                  onDeletePage={handleOnDeleteOrdersPreOpFormmDocPage}
                  onDeleteDocument={handleOnDeleteOrdersPreOpFormmDocDocument}
                />
              }

              {activeDrawerTab === 'orders' && ordersActiveSubTab === 'intraOp' &&
                <AnesthesiaForms
                  formType='Orders Intra Op'
                  isLoadingData={isLoadingData}
                  caseData={caseData || {}}
                  subTab='orders_intraOp'
                  onChangeIsNone={handleOnChangeIsNoneOrdersIntraOpForm}
                  onCaseDocUploaded={handleOnOrdersIntraOpFormDocsUploaded}
                  onDeletePage={handleOnDeleteOrdersIntraOpFormmDocPage}
                  onDeleteDocument={handleOnDeleteOrdersIntraOpFormmDocDocument}
                />
              }

              {activeDrawerTab === 'orders' && ordersActiveSubTab === 'postOp' &&
                <AnesthesiaForms
                  formType='Orders Post Op'
                  isLoadingData={isLoadingData}
                  caseData={caseData || {}}
                  subTab='orders_postOp'
                  onChangeIsNone={handleOnChangeIsNoneOrdersPostOpForm}
                  onCaseDocUploaded={handleOnOrdersPostOpFormDocsUploaded}
                  onDeletePage={handleOnDeleteOrdersPostOpFormDocPage}
                  onDeleteDocument={handleOnDeleteOrdersPostOpFormDocDocument}
                />
              }

              {activeDrawerTab === 'discharge' &&
                <AnesthesiaForms
                  isLoadingData={isLoadingData}
                  caseData={caseData || {}}
                  subTab='discharge'
                  onChangeIsNone={handleOnChangeIsNoneDischarge}
                  onCaseDocUploaded={handleOnDischargeDocsUploaded}
                  onDeletePage={handleOnDeleteDischargeDocPage}
                  onDeleteDocument={handleOnDeleteDischargeDocDocument}
                />
              }
            </div>
          }
          {/* ------------------------ Separator ------------------------ */}
          {!isMobileLayout &&
            <div
              className={css(styles.separator2)}
              onMouseDown={callBackMouseDown}
              onMouseUp={callBackMouseUp}
            />
          }
          {/* Footer */}
          <div className={css(styles.docFooter)}>
            {/* AnesStart */}
            <div className='ml-2 fx fx__v-center'>
              <div className='fx fx__v-center' style={{ fontSize: '12px', fontWeight: '700', height: '20px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" className='mr-1'>
                  <path fill="#96C8FF" fillRule="evenodd" d="M11.25.804 3 6l8.25 5.196V.804ZM0 12h2V0H0v12Z" clipRule="evenodd" />
                </svg>
                Anes
              </div>
              <TimePicker
                className='pl-1 pr-2'
                hour={anesStartHour}
                minute={anesStartMinute}
                color={isAnesStartTimePristine ? yellow[500] : blue[500]}
                noUnderline
                timeFormat='24H'
                timePeriod='AM'
                isPristine={isAnesStartTimePristine}
                onChangeTime={(hour, min) => {
                  if (!shouldSaveCase) {
                    setShouldSaveCase(true);
                  }
                  setIsAnesStartTimePristine(false);
                  setAnesStartHour(hour);
                  setAnesStartMinute(min);
                }}
                disableTimeFormat
              />
            </div>
            {/* Anesthesia Stop */}
            <div className='ml-2 fx fx__v-center'>
              <div className='fx fx__v-center' style={{ fontSize: '12px', fontWeight: '700', height: '20px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" className='mr-1'>
                  <path fill="#96C8FF" fillRule="evenodd" d="M.75 11.196 9 6 .75.804v10.392ZM12 0h-2v12h2V0Z" clipRule="evenodd" />
                </svg>
                Anes
              </div>
              <TimePicker
                className='pl-1 pr-2'
                hour={anesStopHour}
                minute={anesStopMinute}
                // color={isAnesStopTimePristine ? yellow[500] : blue[500]}
                color={(isAnesStopTimePristine || showAnesStopFlag) ? yellow[500] : blue[500]}
                noUnderline
                timeFormat='24H'
                timePeriod='AM'
                isPristine={isAnesStopTimePristine}
                onChangeTime={(hour, min) => {
                  if (!shouldSaveCase) {
                    setShouldSaveCase(true);
                  }
                  setIsAnesStopTimePristine(false);
                  setAnesStopHour(hour);
                  setAnesStopMinute(min);
                }}
                disableTimeFormat
              />
            </div>
          </div>
        </div>

        {/* ------------------------ RCM Section ------------------------ */}
        {!isMobileLayout && rcmSection}

        {/* RCM Drawer */}
        {showCasePostingDrawer && isMobileLayout &&
          <>
            <div
              onClick={() => setShowPatientProfileDrawer(false)}
              style={{
                position: 'fixed',
                background: 'rgb(0, 24, 36)',
                top: '0px',
                height: '100vh',
                width: '100%',
                opacity: '0.8',
                zIndex: '1950'
              }}
            />
            <Drawer
              placement='right'
              visible={showCasePostingDrawer}
              toggleDrawer={() => setShowCasePostingDrawer(false)}
              zIndex={2200}
              showMask={false}
              style={{
                width: isMobileLayout ? 'calc(100% - 24px)' : null
              }}
              width={window.innerWidth - 24}
            >
              <div className={css(styles.drawerContainer)}>
                {/* Header */}
                <div className={css(styles.drawerHeader)}>
                  <div className='fx fx__hv-center'>
                    <i className='mr-2 fa fa-lg fa-building-o' />
                    {/* {showPatientFlag || showSurgeonFlag || showFacilityFlag || showDxFlag || showProcedureFlag
                      ? <svg
                        width="32"
                        height="32"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          width="32"
                          height="32"
                          rx="4"
                          fill="#0080FF"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12.5 4A1.5 1.5 0 0 0 11 5.5V9H6a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h12v-8a2 2 0 0 1 2-2h8v-7a2 2 0 0 0-2-2h-5V5.5A1.5 1.5 0 0 0 19.5 4h-7Zm.5 2h6v3h-6V6Z"
                          fill="#fff"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M28.998 19H20a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8.998c.001 0 .002 0 .002-.002v-9.996c0-.001 0-.002-.002-.002Zm0 0v10l-3.11-4.425a1 1 0 0 1 0-1.15L28.998 19Z"
                          fill="#FF6060"
                        />
                      </svg>
                      : <svg
                        width="32"
                        height="32"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          width="32"
                          height="32"
                          rx="4"
                          fill="#0080FF"
                        />
                        <path
                          fill="#fff"
                          d="M22 22h8v6h-8z"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12.5 4A1.5 1.5 0 0 0 11 5.5V9H6a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h14.803A6 6 0 0 1 28 19.341V11a2 2 0 0 0-2-2h-5V5.5A1.5 1.5 0 0 0 19.5 4h-7Zm.5 2h6v3h-6V6Z"
                          fill="#fff"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M21 25a5 5 0 1 1 10 0 5 5 0 0 1-10 0Zm2.205-.198 1.58 1.497L28.8 22.5l.534.506-4.548 4.309-2.118-2.007.538-.506Z"
                          fill="#00B080"
                        />
                      </svg>
                    } */}
                    <div className='ml-2'>Case Posting</div>
                  </div>
                  <i
                    className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                    onClick={() => setShowCasePostingDrawer(false)}
                  />
                </div>
                {/* Content */}
                <div className={css(styles.drawerContentArea)}>
                  {rcmSection}
                </div>
                {/* Footer */}
                <div
                  className={`fx pr-2 ${css(styles.drawerFooter)}`}
                  style={{ position: 'absolute', bottom: '0', height: '40px', background: 'yellow' }}
                >
                  Footer
                </div>
              </div>
            </Drawer>
          </>
        }
      </div>
    </div>
  );
};

export default CaseDashboard;
